/**
 * state.js
 * Central state management for ZAGX
 *
 * Exports:
 * - S (mutable let): the global state object
 * - Constants: CURRENT_SCHEMA, SEED, SEED_R, MERGE_ARRAYS
 * - Functions: stampItem, mergeArrays, mergeStates, showConflictModal, resolveConflict,
 *              save, runMigrations, _sanitizeImportArray, updateStorageBadge
 * - Globals: sortC, sortD, editHId, editTId, cTxTp
 * - Registration: _setPersistFn, _setRenderCallbacks for dependency injection
 */

import { preserveSecrets } from './secrets.js';
import { esc, f, uid, now, demojibake, realizedOrigin, tradeForRealized, cashSumByCurrency } from './utils.js';
// v56 mirrors two lot-arithmetic definitions rather than restating them (rule #24). Neither
// math.js nor its own imports reach back into this file -- it takes a state ref from main.js --
// so there is no cycle here.
import { blendedAvgCost, holdingAfterExit } from './math.js';
import { pricePurchase } from './fees.js';
import { toast, clMo } from '../ui/layout.js';

// ============ STATE & SCHEMA ============

// CURRENT_SCHEMA is DERIVED from MIGRATIONS — see the block immediately after that array. It
// cannot be declared here: MIGRATIONS is a const further down this file, so reading it at this
// point is a temporal-dead-zone error.
//
// A hand-typed number lived here and fell nineteen versions behind the migration list.
// `importData` refuses any file whose version is HIGHER, so every backup the app wrote was
// refused by the app's own Import button and the only file restore path was dead for about two
// months with no visible symptom. Two artifacts had to agree and nothing made them.
// (DATA-001, audit 2026-08-13)

export let S = {
  // Stamped from CURRENT_SCHEMA immediately after MIGRATIONS is defined. Nothing executes
  // between the two points, so no reader can observe the 0.
  _schemaVersion: 0,
  base: 2400,
  holdings: [],
  monthlyRates: {},
  trades: [],
  cashTxs: [],
  realized: [],
  history: [],
  prices: {},
  fxRate: null,
  fxRateEUR: null,
  cash: { USD: 2400, TRY: 0, EUR: 0 },
  // trInflation lives HERE and only here. It used to exist twice — `S.trInflation` (read by
  // the time-deposit real-return preview) and `S.settings.trInflation` (written by the
  // Settings input, read by everything else). On 2026-08-08 they held 0.42 and 0.321: the
  // same screen quoted Turkish inflation as 42% and 32.1%, and the 42% copy reached the AI
  // insight prompt as fact. One field, one writer. (audit 2026-08-08)
  settings: { eqInt: 120, crInt: 60, notif: true, backupDays: 7, reserveTargetUSD: 12000, riskBudgetUSD: 0, trInflation: 0.335, cashBanksTRY: ['Garanti', 'Akbank'] }, // riskBudgetUSD 0 = auto (5% of net worth)
  /**
   * What the banks actually said, and when they said it.
   *
   * `S.cash.TRY` is a running figure the app maintains; it is not, and has never been, a
   * balance anybody verified. On 2026-08-08 it read ₺234,384.75 while Garanti and Akbank
   * together held ₺336,800 — the gap was five months of salary the ledger recorded as
   * `_audit` mirror rows that never touch the balance. Nothing in the app could have shown
   * that, because nothing held the other number.
   *
   * `banks` is a per-account breakdown that MUST sum to `S.cash.TRY`; `at` is when the owner
   * last read them off. Both are checked (cash.bankSplit, cash.reconcileAge).
   */
  cashRecon: { TRY: { banks: {}, at: null } },
  snapshots: [],
  lastBackup: null,
  expenseDefs: [],
  incomeDefs: [],
  cashflow: {},
  reserveFund: { TRY: 0, USD: 0, EUR: 0, XAU: 0, XAG: 0, GBP: 0 },
  rfTxs: [],
  alertedKeys: {},
  budgets: { monthlyLimit: 0, categories: {} },
  dividends: [],
  liabilities: [],
  goals: [],
  goalSettings: { freedomNumber: 0, investingSince: null },
  achievements: [],
  portfolios: [],
  activePortfolioId: null,
  signals: [],
  signalSources: [],
  signalAlerts: [],
  tickerMap: {},
  oneTimeIncome: [],
  bonusPlans: [],
  bonusQuarters: [],
  aiRules: [],
  aiInsights: { items: [], generatedAt: null, model: '', portfolioHash: '' },
  notifications: [],
  aiCopilot: { recommendations: [], scenarios: [], alerts: [], lastScan: null, scanHistory: [] }
};

// Mutable globals for UI state
export let sortC = null, sortD = 1, editHId = null, editTId = null, cTxTp = 'deposit';

// ============ SEED DATA ============

export const SEED = [
  { tk: 'ENJSA', nm: 'ENJSA', tp: 'Stock', st: 'Stock', pn: '', bk: 'Garanti', bw: '28-31', al: 3, cr: 'TRY' },
  { tk: 'ISGYO', nm: 'ISGYO', tp: 'Stock', st: 'Stock', pn: '', bk: 'Garanti', bw: '28-31', al: 4, cr: 'TRY' },
  { tk: 'ASELS', nm: 'ASELS', tp: 'Stock', st: 'Stock', pn: '', bk: 'Garanti', bw: '28-31', al: 5, cr: 'TRY' },
  { tk: 'THYAO', nm: 'THYAO', tp: 'Stock', st: 'Stock', pn: '', bk: 'Garanti', bw: '28-31', al: 2, cr: 'TRY' },
  { tk: 'TUPRS', nm: 'TUPRS', tp: 'Stock', st: 'Stock', pn: '', bk: 'Garanti', bw: '28-31', al: 4, cr: 'TRY' },
  { tk: 'EUR', nm: 'EUR', tp: 'Cash', st: 'Cash', pn: '', bk: 'Garanti', bw: '28-31', al: 5, cr: 'EUR' },
  { tk: 'USD', nm: 'USD', tp: 'Cash', st: 'Cash', pn: '', bk: 'Garanti', bw: '28-31', al: 5, cr: 'USD' },
  { tk: 'XAU', nm: 'XAU', tp: 'Commodity', st: 'Commodity', pn: '', bk: 'Garanti', bw: '10-12', al: 7, cr: 'TRY' },
  { tk: 'XAG', nm: 'XAG', tp: 'Commodity', st: 'Commodity', pn: '', bk: 'Garanti', bw: '10-12', al: 4, cr: 'TRY' },
  { tk: 'HVZ', nm: 'HVZ', tp: 'Fund', st: 'Equity Fund', pn: 'Allbatross Portföy', bk: 'Garanti', bw: '28-31', al: 2, cr: 'TRY' },
  { tk: 'IDH', nm: 'IDH', tp: 'Fund', st: 'Equity Fund', pn: 'İşPortföy', bk: 'Garanti', bw: '28-31', al: 3, cr: 'TRY' },
  { tk: 'OTJ', nm: 'OTJ', tp: 'Fund', st: 'Fund of Funds', pn: 'OYAK Portföy', bk: 'Garanti', bw: '28-31', al: 4, cr: 'TRY' },
  { tk: 'TEJ', nm: 'TEJ', tp: 'Fund', st: 'Fund of Funds', pn: 'Azimut Portföy', bk: 'Garanti', bw: '28-31', al: 4, cr: 'TRY' },
  { tk: 'AFA', nm: 'AFA', tp: 'Fund', st: 'Equity Fund', pn: 'Ak Portföy', bk: 'Garanti', bw: '28-31', al: 4, cr: 'TRY' },
  { tk: 'YKT', nm: 'YKT', tp: 'Fund', st: 'Gold Fund', pn: 'Yapı Kredi Portföy', bk: 'Garanti', bw: '28-31', al: 3, cr: 'TRY' },
  { tk: 'NVB', nm: 'NVB', tp: 'Fund', st: 'Equity Fund', pn: 'NEO Portföy', bk: 'Garanti', bw: '28-31', al: 2, cr: 'TRY' },
  { tk: 'NVDA', nm: 'NVDA', tp: 'Stock', st: 'Stock', pn: 'IBKR', bk: 'IBKR', bw: '20-25', al: 2, cr: 'USD' },
  { tk: 'MSFT', nm: 'MSFT', tp: 'Stock', st: 'Stock', pn: 'IBKR', bk: 'IBKR', bw: '20-25', al: 1.5, cr: 'USD' },
  { tk: 'CVX', nm: 'CVX', tp: 'Stock', st: 'Stock', pn: 'IBKR', bk: 'IBKR', bw: '20-25', al: 2, cr: 'USD' },
  { tk: 'ENB', nm: 'ENB', tp: 'Stock', st: 'Stock', pn: 'IBKR', bk: 'IBKR', bw: '20-25', al: 1.5, cr: 'USD' },
  { tk: 'AVGO', nm: 'AVGO', tp: 'Stock', st: 'Stock', pn: 'IBKR', bk: 'IBKR', bw: '20-25', al: 2, cr: 'USD' },
  { tk: 'VOO', nm: 'VOO', tp: 'ETF', st: 'ETF', pn: 'IBKR', bk: 'IBKR', bw: '20-25', al: 7, cr: 'USD' },
  { tk: 'QQQ', nm: 'QQQ', tp: 'ETF', st: 'ETF', pn: 'IBKR', bk: 'IBKR', bw: '20-25', al: 6, cr: 'USD' },
  { tk: 'SMH', nm: 'SMH', tp: 'ETF', st: 'ETF', pn: 'IBKR', bk: 'IBKR', bw: '20-25', al: 4, cr: 'USD' },
  { tk: 'XLE', nm: 'XLE', tp: 'ETF', st: 'ETF', pn: 'IBKR', bk: 'IBKR', bw: '20-25', al: 3, cr: 'USD' },
  { tk: 'XLV', nm: 'XLV', tp: 'ETF', st: 'ETF', pn: 'IBKR', bk: 'IBKR', bw: '20-27', al: 3, cr: 'USD' },
  { tk: 'DBA', nm: 'DBA', tp: 'ETF', st: 'ETF', pn: 'IBKR', bk: 'IBKR', bw: '20-25', al: 2.5, cr: 'USD' },
  { tk: 'COPX', nm: 'COPX', tp: 'ETF', st: 'ETF', pn: 'IBKR', bk: 'IBKR', bw: '20-25', al: 3, cr: 'USD' },
  { tk: 'CLOU', nm: 'CLOU', tp: 'ETF', st: 'ETF', pn: 'IBKR', bk: 'IBKR', bw: '20-25', al: 1.5, cr: 'USD' }
];

export const SEED_R = {
  '2026-01': { usdtry: 42.94, eurtry: 50.54, mul: 1 },
  '2026-02': { usdtry: 43.42, eurtry: 52.12, mul: 1 },
  '2026-03': { usdtry: 43.96, eurtry: 51.91, mul: 1 }
};

// ============ MERGE CONFIGURATION ============

export const MERGE_ARRAYS = ['holdings', 'trades', 'cashTxs', 'realized', 'rfTxs', 'dividends', 'expenseDefs', 'incomeDefs', 'liabilities', 'goals', 'achievements', 'portfolios', 'signals', 'signalSources', 'signalAlerts', 'oneTimeIncome', 'bonusPlans', 'bonusQuarters', 'aiRules', 'notifications'];
// benchmarkPrices: { VOO: { 'YYYY-MM': close } }. One float per month, ~600 bytes for a
// decade — historical closes never change, so it is written once per month and then read.
// `aiInsights` was in NEITHER list, which put it outside the sync contract entirely: it got no
// per-field timestamp on save, so a locally regenerated deck could not win a merge and the copy
// on screen reverted on the next reload. Measured 2026-08-08 — a deck generated at 15:58 was
// back to its 2026-06-05 predecessor twelve seconds after a reload. That is the real reason the
// cards were 64 days old, and why the `ai.insights` age check kept firing: it was reporting a
// sync defect, not a habit. (audit 2026-08-08)
// ⚠ `fxEURUSD` and `fxGBPUSD` JOINED THIS LIST ON 2026-08-16, AND THAT IS THREE FIXES IN ONE.
// They are written by `prices.js` (`_S.fxEURUSD = eurRate`, `_S.fxGBPUSD = 1 / gbpRate`) and read
// by `math.js`, `utils.js`, `dividends.js` and `prices.js` itself to convert EUR and GBP money —
// and they appeared in NO list: not the `S` literal, not `SCALAR_FIELDS`, not `_IMPORT_KEYS`.
// Consequences, all measured against `zagx-2026-08-14.json`, which carries both:
//   · SYNC-004 — a second device's newer rate could never arrive, because the merge only walks
//     the arrays and this list.
//   · An IMPORT dropped them: `RESTORE_KEYS` is derived from these three lists, so a restored
//     book fell back to `fxRateEUR / fxRate` for every EUR conversion while `dividends.js:108`
//     already documents that the two disagree.
// They are the same KIND of value as `fxRate` and `fxRateEUR`, two entries to their left, and
// they now travel the same way. ⚠ Cross-version caveat, the same one `aiInsights` carried: a tab
// still running the previous build rebuilds `_scalarTimestamps` from ITS list and drops the two
// keys on every save until it reloads.
export const SCALAR_FIELDS = ['base', 'fxRate', 'fxRateEUR', 'fxEURUSD', 'fxGBPUSD', 'cash', 'cashRecon', 'settings', 'aiInsights', 'cashflow', 'reserveFund', 'budgets', 'monthlyRates', 'alertedKeys', 'goalSettings', 'activePortfolioId', 'tickerMap', 'aiCopilot', 'rebalancing', 'benchmarkPrices'];

// ============ STAMPING & MERGING ============

/**
 * Stamp an item with current timestamp
 */
export function stampItem(item) {
  if (item && typeof item === 'object') item._updatedAt = Date.now();
  return item;
}

/**
 * ⚠ A MIGRATION THAT EDITS A ROW MUST OUTRANK THE ROW IT EDITED — AND NOTHING ELSE (plan #5).
 *
 * `mergeArrays` decides row by row on `_updatedAt`, and its tie-break is **`localTs === cloudTs`
 * → the CLOUD copy wins.** A migration that edits a row in place without touching the stamp
 * therefore produces exactly that tie against the cloud's pre-migration twin, and loses it. The
 * edit is reverted in memory, `schemaFloor` has already pinned `_schemaVersion` at the new
 * number, and **`runMigrations` is never called again** — the three post-boot merge sites (poll,
 * realtime, tab-sync) each `Object.assign(S, merged)` and not one of them re-migrates. The row
 * stays un-migrated for good, silently.
 *
 * Measured 2026-08-17 across all 54. v45–v54 stamp nothing, and of those exactly FOUR edit a row
 * belonging to a `MERGE_ARRAYS` array — **v48 (cashTxs), v50 (dividends), v52 (cashTxs), v53
 * (realized)**. The rest are safe for reasons rather than by luck: v45/46/47/49 write SCALARS,
 * which `save()` stamps by changed-JSON; v51 and v54 only DELETE, and the tombstone union
 * outranks every clock; v44 already stamps.
 *
 * ⚠ **`+1`, NOT `Date.now()`, AND THE DIFFERENCE IS THE WHOLE POINT.** `restampRestoredRows` uses
 * `now` and says out loud that it therefore outranks a genuinely newer edit on a second device —
 * acceptable there, because a restore is explicit, confirmed and destructive by construction. A
 * migration is none of the three. It runs unattended on whichever device boots first, so a stale
 * one coming online would stamp `now` and beat a real edit another device made yesterday.
 * Bumping by a single millisecond breaks the tie against the row's OWN pre-migration twin — the
 * one comparison that is actually wrong — and still loses to every genuine edit, which is right.
 */
export function bumpMigrated(item) {
  if (item && typeof item === 'object') item._updatedAt = (Number(item._updatedAt) || 0) + 1;
  return item;
}

/**
 * Tombstone an id so a multi-device merge (mergeArrays union) can never resurrect it.
 * Use for cashTx ids removed by a predicate (OTI/trade/income reversals).
 */
export function tombstoneId(id) {
  if (!id) return;
  if (!S._deletedIds) S._deletedIds = [];
  if (!S._deletionTimestamps) S._deletionTimestamps = {};
  if (!S._deletedIds.includes(id)) S._deletedIds.push(id);
  S._deletionTimestamps[id] = Date.now();
}

/**
 * The one true delete primitive for any MERGE_ARRAYS array: splice by id + tombstone.
 * Returns the removed item (for balance reversal). Never bypass this with a bare filter —
 * an untombstoned delete resurrects on the next merge and double-counts money.
 */
export function softDelete(arrayKey, id) {
  let removed = null;
  if (Array.isArray(S[arrayKey])) {
    const idx = S[arrayKey].findIndex(x => x && x.id === id);
    if (idx >= 0) removed = S[arrayKey].splice(idx, 1)[0];
  }
  tombstoneId(id);
  return removed;
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * ONE REPORT LINE, ONE SIGNAL ROW.
 *
 * Measured 2026-08-16 on the live book: **16 duplicate groups, 16 extra rows out of 168** — 9.5%
 * of the signal book, in three incidents (08-10, 08-11, 08-13). Every pair is byte-identical apart
 * from `id` / `createdAt`, their `createdAt` values are ~800 ms apart, and — the load-bearing part —
 * **their `_extId` is identical**:
 *
 *     ASELS dup A  _extId = "OeA0CRpZHmU::Aselsan"
 *     ASELS dup B  _extId = "OeA0CRpZHmU::Aselsan"
 *
 * ⚠ THE IMPORT DEDUP IS NOT BROKEN, AND A RE-ENTRANCY GUARD WOULD NOT HAVE HELPED. That was the
 * first theory and the measurement killed it. `fetchSignalsFromMongo` builds `byExtId` *after* its
 * `await res.json()`, so a second call in the same context would see the first call's rows. The two
 * `createdAt` clusters are internally tight (~2 ms) and 795 ms apart: **two separate JS contexts** —
 * two tabs, or laptop and phone — each pulling ~60 s after its own load, neither able to see the
 * other's push. `mergeArrays` then unions by `id`, and two different ids with one `_extId` both
 * survive. `main.js` says *"Dedupe makes repeats no-ops"*; across two contexts it is not a no-op.
 *
 * So the collapse cannot live on the import path. It lives at **save**, the one funnel both the
 * import path and the merge path pass through, and it repairs the existing 16 with no migration.
 *
 * ⚠ AND IT MUST TOMBSTONE. A bare filter here would be undone by the very next merge — the other
 * context still holds the row, so it comes straight back. The untombstoned delete is the mechanism
 * that created the duplicates; repeating it would make the fix a no-op that looked like a fix.
 * `duplicateSignalGroups` therefore only *decides*; `save()` applies it through `softDelete`.
 *
 * `_extId` ⟺ mongo-sourced, exactly: measured 150/150 in both directions, 0 rows with an `_extId`
 * and a non-mongo `sourceId`, 0 mongo rows without one. So this predicate is `_extId` alone rather
 * than a second spelling of `sourceId.startsWith('mongo')` — one concept, one test.
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

/** Resolution rank for picking a survivor. Lower wins. Unknown statuses sort last, not first. */
export const SIG_STATUS_RANK = { ACTED: 0, TRIGGERED: 1, APPROACHING: 2, NEW: 3, EXPIRED: 4 };

/**
 * Which of a duplicate pair is the real row.
 *
 * ⚠ THIS PICKS, IT DOES NOT FIELD-MERGE. A merge that carries every field from both is the mirror
 * of a replace that carries none (rule #47) and would let a stale twin's `NEW` overwrite a live
 * `ACTED`. Ranked, most-resolved first, and every tier is a real tiebreak rather than decoration:
 *
 *   1. an `actedAt` stamp   — he did something about this signal; that row is the one with history
 *   2. an `expiredAt` stamp — a decided outcome beats an undecided one
 *   3. status rank          — ACTED > TRIGGERED > APPROACHING > NEW > EXPIRED
 *   4. oldest `createdAt`   — the first write is the one older references point at
 *   5. lowest `id`          — so the answer is deterministic even for two identical rows
 *
 * Today all 16 pairs agree on tiers 1–3 (measured: **0 of 16 diverge** on status / actedAt /
 * expiredAt / _outcome / levels), so today only tiers 4–5 decide. That is a fact about this
 * Sunday, not a property of the data — rule #45 — which is why the earlier tiers are written and
 * tested now rather than added the first time a pair does diverge.
 */
function _sigSurvivorOrder(a, b) {
  const stamped = x => (x.actedAt ? 0 : x.expiredAt ? 1 : 2);
  const rank = x => { const r = SIG_STATUS_RANK[x.status]; return typeof r === 'number' ? r : 99; };
  return stamped(a) - stamped(b)
    || rank(a) - rank(b)
    || String(a.createdAt || '').localeCompare(String(b.createdAt || ''))
    || String(a.id).localeCompare(String(b.id));
}

/**
 * Duplicate signal rows, grouped by `_extId`. Pure — decides, never mutates.
 *
 * @returns {Array<{extId:string, keep:string, drop:string[]}>} one entry per group of 2+
 */
export function duplicateSignalGroups(signals) {
  const by = new Map();
  (signals || []).forEach(s => {
    if (!s || !s.id || !s._extId) return;
    const g = by.get(s._extId);
    if (g) g.push(s); else by.set(s._extId, [s]);
  });
  const out = [];
  by.forEach((group, extId) => {
    if (group.length < 2) return;
    // Sorted in place on purpose: `group` is built right here and is never the caller's array. A
    // defensive `.slice()` was written first, and the mutation run proved it unobservable — no
    // mutation of it could be made to fail a test, so it was not a guard, it was a habit.
    const sorted = group.sort(_sigSurvivorOrder);
    out.push({ extId, keep: sorted[0].id, drop: sorted.slice(1).map(s => s.id) });
  });
  return out;
}

/**
 * Alerts that became duplicates once their signals were collapsed.
 *
 * The duplication propagated: **6 of his 27 `signalAlerts` point at a duplicated signal**, in three
 * pairs (EREGL, TUPRS, XU100), all `TRIGGERED` and all still live — he was notified twice about one
 * call. `save()` re-points a dropped row's alerts onto the survivor first, which turns those pairs
 * into two alerts on one `(signalId, type)`; this then keeps one.
 *
 * ⚠ A DISMISSED ALERT OUTRANKS A LIVE ONE, and that direction is deliberate. He dismissed *the
 * alert for this signal*; the twin only exists because of the bug. Keeping the live one would
 * resurrect a notification he has already answered, which is the one failure here that costs him
 * something. Today none of the six is dismissed, so this tier is untested by his data and proved by
 * tests only.
 */
export function duplicateAlertIds(alerts) {
  const by = new Map();
  (alerts || []).forEach(a => {
    if (!a || !a.id || !a.signalId) return;
    // NUL as the separator, not a space: an id or type that carried the separator would let two
    // different pairs build one key and collapse two unrelated alerts into one.
    const k = a.signalId + '\u0000' + (a.type || '');
    const g = by.get(k);
    if (g) g.push(a); else by.set(k, [a]);
  });
  const drop = [];
  by.forEach(group => {
    // An early exit, NOT a guard — the mutation run confirmed nothing observable changes if it
    // goes, because `sorted.slice(1)` is empty for a singleton anyway. Kept because it skips a
    // sort per unique alert; recorded as an optimisation so nobody later mistakes it for a rule.
    if (group.length < 2) return;
    // dismissed first · then the EARLIEST trigger, because the first time a level was crossed is
    // the true fact and the later copy is the echo · then id, so the answer is deterministic.
    // ⚠ A MISSING `triggeredAt` SORTS LAST, NOT FIRST. `'' < '2026-…'` under localeCompare, so the
    // obvious `a.triggeredAt || ''` would make the row that never triggered outrank the row that
    // did. Same shape as `Number('') === 0` in thesis.js: absent is not "earliest", it is unknown.
    const firstTrig = x => x.triggeredAt || '\uffff';
    const sorted = group.sort((a, b) =>
      ((a.dismissedAt ? 0 : 1) - (b.dismissedAt ? 0 : 1))
      || firstTrig(a).localeCompare(firstTrig(b))
      || String(a.id).localeCompare(String(b.id)));
    sorted.slice(1).forEach(a => drop.push(a.id));
  });
  return drop;
}

/**
 * Is the difference between these two versions of a row ONLY the timestamp?
 *
 * ⚠ UI-003. A CONFLICT IS A DISAGREEMENT ABOUT CONTENT, NOT ABOUT WHEN A ROW WAS TOUCHED.
 * `restampRestoredRows` (the DATA-011 fix) stamps EVERY restored row with one fresh `_updatedAt`,
 * so the merge that follows a restore sees 638 rows whose timestamps differ from the cloud's and
 * calls all 638 conflicts. Measured on `zagx-2026-08-14.json` with seven genuinely edited rows:
 *
 *     conflicts reported     638
 *     identical but for _updatedAt   631
 *     actually different       7
 *
 * The modal then asks him to adjudicate 638 rows, of which 631 have nothing to adjudicate. That is
 * the shape rule #28 warns about from the other side: a measurement rendered where there is none.
 *
 * ⚠ THIS CHANGES WHAT IS REPORTED, NEVER WHAT IS KEPT. The newest row still wins, byte for byte —
 * the merge result is untouched. Only the conflict LIST shrinks, and with it the modal.
 *
 * ⚠ KEY ORDER IS NOT CONTENT. `JSON.stringify` is insertion-ordered, and two copies of one row can
 * carry their keys in different orders after a round trip through a backup and the cloud. Sorted
 * keys, recursively, or this helper invents differences of its own.
 */
function _stableStringify(v) {
  if (v === null || typeof v !== 'object') return JSON.stringify(v);
  if (Array.isArray(v)) return '[' + v.map(_stableStringify).join(',') + ']';
  return '{' + Object.keys(v).sort()
    .map(k => JSON.stringify(k) + ':' + _stableStringify(v[k])).join(',') + '}';
}
export function sameExceptStamp(a, b) {
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
  const strip = o => { const c = {}; Object.keys(o).forEach(k => { if (k !== '_updatedAt') c[k] = o[k]; }); return c; };
  return _stableStringify(strip(a)) === _stableStringify(strip(b));
}

/**
 * Merge two arrays by ID: union of items, newest _updatedAt wins per ID
 */
export function mergeArrays(localArr, cloudArr, deletedIds) {
  const map = new Map();
  const conflicts = [];

  // Index cloud items
  (cloudArr || []).forEach(item => {
    if (item && item.id) map.set(item.id, { src: 'cloud', item });
  });

  // Merge local items
  (localArr || []).forEach(item => {
    if (!item || !item.id) return;
    const existing = map.get(item.id);
    if (!existing) {
      map.set(item.id, { src: 'local', item });
    } else {
      const localTs = item._updatedAt || 0;
      const cloudTs = existing.item._updatedAt || 0;
      if (localTs === cloudTs) {
        // Identical timestamp — keep either (no conflict)
        map.set(item.id, { src: 'cloud', item: existing.item });
      } else if (localTs > cloudTs) {
        // Local is newer — flag conflict if cloud was also modified (timestamp > 0) AND the two
        // versions actually say something different. ⚠ See `sameExceptStamp`: a restore restamps
        // every row, and without this the modal lists the whole book. (UI-003)
        if (cloudTs > 0 && !sameExceptStamp(item, existing.item)) {
          conflicts.push({ id: item.id, local: item, cloud: existing.item });
        }
        map.set(item.id, { src: 'local', item });
      } else {
        // Cloud is newer — same rule, mirrored.
        if (localTs > 0 && !sameExceptStamp(item, existing.item)) {
          conflicts.push({ id: item.id, local: item, cloud: existing.item });
        }
        // Cloud already in map, keep it
      }
    }
  });

  // Remove soft-deleted items
  const delSet = new Set(deletedIds || []);
  const merged = [];
  map.forEach(({ item }) => {
    if (!delSet.has(item.id)) merged.push(item);
  });

  return { merged, conflicts };
}

/**
 * THE SCHEMA FLOOR. The highest version any store has seen, and the only way this number moves.
 *
 * A SCHEMA VERSION IS MONOTONIC AND NEVER MERGES DOWN. A copy that had not yet received the
 * migrated version makes a merged state look unmigrated, and the whole chain re-runs on the next
 * load. The lower of two values is always the staler copy, never a rollback anyone asked for.
 * (audit 2026-08-03: a merged state carrying v34 re-ran v35..v40 and, because two of those were
 * written as deltas rather than targets, five positions took TRY 63,485 of assets that were
 * never bought.)
 *
 * ⚠ THIS RULE HAD TWO HAND-WRITTEN COPIES: here and the schema-floor block inside
 * `persistence.loadState`. Both now call this. It is deliberately NOT the rule the two IMPORT
 * doors use — `importData` REJECTS a file with no numeric version rather than flooring it, and
 * `restoreBackup` lets the number go DOWN on purpose, because a backup's low version is TRUE of
 * the data inside it. Three different correct answers; only two of them are this one.
 *
 * ⚠ AND THE FLOOR IS LOAD-BEARING, NOT HYGIENE. Measured 2026-08-16 against the live book: with
 * the floor removed, replaying v40 on today's data writes back AFA (29,728.4485 units,
 * TRY 31,403.00) and YKT (30,803.6667 / TRY 27,197.30) — two positions he has since CLOSED — and
 * rolls the USD cash holding from 927.4652 down to 876.8052. See
 * `tests/core/a-migration-must-not-run-twice.test.js`.
 *
 * @param {...*} versions any number of candidate versions; non-numeric ones count as 0
 * @returns {number} the highest
 */
export function schemaFloor(...versions) {
  return versions.reduce((max, v) => {
    const n = Number(v);
    return isFinite(n) && n > max ? n : max;
  }, 0);
}

/**
 * Full state merge: field-level for arrays, last-write-wins for scalars
 */
export function mergeStates(local, cloud) {
  if (!cloud || !local) return { state: local || cloud, conflicts: [], hasConflicts: false };

  const result = { ...local };
  // `result` starts as a copy of local, so without this the local number wins by accident.
  result._schemaVersion = schemaFloor(local._schemaVersion, cloud._schemaVersion);
  const allConflicts = [];
  const localDel = new Set(local._deletedIds || []);
  const cloudDel = new Set(cloud._deletedIds || []);
  const mergedDel = [...new Set([...localDel, ...cloudDel])];

  // Merge each array field
  MERGE_ARRAYS.forEach(key => {
    const { merged, conflicts } = mergeArrays(local[key] || [], cloud[key] || [], mergedDel);
    result[key] = merged;
    if (conflicts.length) {
      conflicts.forEach(c => { c.field = key; allConflicts.push(c); });
    }
  });

  // SYNC-04 Fix: Per-field scalar merge using _scalarTimestamps
  // Each device stamps the fields it actually changed. On merge, pick the newer per field.
  const localMod = local._lastModified || 0;
  const cloudMod = cloud._lastModified || 0;
  const localScalarTs = local._scalarTimestamps || {};
  const cloudScalarTs = cloud._scalarTimestamps || {};
  const mergedScalarTs = {};

  SCALAR_FIELDS.forEach(key => {
    // TRIED AND REVERTED, 2026-08-08: "an explicit per-field stamp outranks a global fallback".
    // The intent was right — `_lastModified` advances on every save, so a side that never wrote
    // this field carries a clock that beats one that did. The rule was not.
    //
    //   1. It is symmetric in TIME. It fires just as hard when the explicit side is the OLDER
    //      one, and then it destroys a genuinely newer value the old code would have kept. All
    //      it needs is one write that reaches the cloud unstamped — which a tab still running a
    //      build without the field in SCALAR_FIELDS produces on every save, because line ~306
    //      rebuilds `mergedScalarTs` from THAT build's list and drops the key.
    //   2. It disarms itself. The loop below writes `lt`/`ct` — which are `|| _lastModified`
    //      fallbacks — into `mergedScalarTs` for all 18 keys, so after ONE merge both sides are
    //      explicit for everything and the rule can never fire again. The only place it kept
    //      firing was the cross-version case in (1), i.e. the one where it did harm.
    //
    // Adding `aiInsights` to SCALAR_FIELDS was the whole fix; this was not needed for it.
    const lt = localScalarTs[key] || local._lastModified || 0;
    const ct = cloudScalarTs[key] || cloud._lastModified || 0;
    const lHas = local[key] !== undefined;
    const cHas = cloud[key] !== undefined;
    // ABSENT NEVER BEATS PRESENT (audit 2026-08-01). Only one side holding the field is
    // not a conflict to resolve by timestamp — it is one copy that has the data and one
    // that has not received it yet. Deciding by timestamp there let a newer-but-empty
    // side erase a populated one, which is how five backfilled benchmark closes were
    // destroyed across every store while the sync badge read "synced".
    if (lHas !== cHas) {
      result[key] = lHas ? local[key] : cloud[key];
      mergedScalarTs[key] = lHas ? lt : ct;
      return;
    }
    if (ct > lt) {
      if (cHas) result[key] = cloud[key];
      mergedScalarTs[key] = ct;
    } else {
      // local wins (or tie → keep local)
      if (lHas) result[key] = local[key];
      mergedScalarTs[key] = lt;
    }
  });
  result._scalarTimestamps = mergedScalarTs;

  // D2 fix: cashflow is keyed by month — merge per-month instead of wholesale, so a
  // stale/empty copy on one device can never wipe months that exist only on the other
  // (the silent income-loss path behind the empty tax summary). Timestamp decides a
  // genuine per-month conflict; presence keeps everything else.
  {
    const lcf = local.cashflow || {}, ccf = cloud.cashflow || {};
    const cfTsL = localScalarTs.cashflow || localMod;
    const cfTsC = cloudScalarTs.cashflow || cloudMod;
    const mergedCf = {};
    new Set([...Object.keys(lcf), ...Object.keys(ccf)]).forEach(mk => {
      const lv = lcf[mk], cv = ccf[mk];
      if (lv && cv) mergedCf[mk] = cfTsC > cfTsL ? cv : lv;
      else mergedCf[mk] = lv || cv;
    });
    result.cashflow = mergedCf;
    result._scalarTimestamps.cashflow = Math.max(cfTsL, cfTsC);
  }

  // Merge history (union, deduplicate by ts+action+ticker, keep latest 1000)
  const hMap = new Map();
  [...(cloud.history || []), ...(local.history || [])].forEach(h => {
    const k = (h.ts || '') + '_' + (h.action || '') + '_' + (h.ticker || '');
    if (!hMap.has(k)) hMap.set(k, h);
  });
  // The _CAPS loop below announces every trim it makes and records it in S._truncated. This
  // one did neither — it just dropped the oldest 1,000+ audit entries on every merge, which
  // is every boot, every post-save merge and every 5-minute poll. At 276 entries it is not
  // biting today; the point is that nothing would say so on the day it starts. Same shape as
  // the snapshots bug fixed in this file on 2026-08-01, and the same remedy: say what was
  // dropped. (audit 2026-08-03)
  const _hist = [...hMap.values()].sort((a, b) => (b.ts || '').localeCompare(a.ts || ''));
  if (_hist.length > HISTORY_CAP) {
    const dropped = _hist.length - HISTORY_CAP;
    console.warn('[ZAGX] history capped on merge: dropped ' + dropped + ' oldest of ' + _hist.length
      + ' (kept ' + HISTORY_CAP + ' newest, oldest kept ' + (_hist[HISTORY_CAP - 1] || {}).ts + ')');
    _recordTrim('history', dropped);
  }
  result.history = _hist.slice(0, HISTORY_CAP);

  // Merge prices: keep newest per ticker
  result.prices = { ...(cloud.prices || {}) };
  Object.entries(local.prices || {}).forEach(([tk, p]) => {
    const cp = result.prices[tk];
    if (!cp || (p.ts || 0) > (cp.ts || 0)) result.prices[tk] = p;
  });

  // Merge snapshots. Was `.slice(-50)` on Map INSERTION order — and because persistence
  // strips snapshots from the cloud payload, cloud.snapshots is always empty, so this ran
  // as "throw away 315 of local's 365 days" on every boot, every post-save merge and every
  // 5-minute poll. Nothing recorded the loss. The casualties are everything that reads the
  // series: volatility, drawdown, the benchmark chart, and calcBenchmarkDCA's per-month
  // price fallback. Keep the same 365 the writer keeps, and sort by DATE before trimming
  // so the trim drops the oldest rather than whatever happened to be inserted first.
  // (audit 2026-08-01)
  const snapMap = new Map();
  [...(cloud.snapshots || []), ...(local.snapshots || [])].forEach(s => {
    const k = (s.note || '') + '_' + (s.date || s.ts || '');
    if (!snapMap.has(k)) snapMap.set(k, s);
  });
  result.snapshots = [...snapMap.values()]
    .sort((a, b) => String(a.date || a.ts || '').localeCompare(String(b.date || b.ts || '')))
    .slice(-365);

  // Merge deleted IDs + their timestamps
  result._deletedIds = mergedDel;
  const localDelTs = local._deletionTimestamps || {};
  const cloudDelTs = cloud._deletionTimestamps || {};
  result._deletionTimestamps = { ...localDelTs, ...cloudDelTs };
  // For overlapping keys, keep the earlier timestamp (original deletion time)
  Object.keys(localDelTs).forEach(id => {
    if (cloudDelTs[id] && cloudDelTs[id] < localDelTs[id]) {
      result._deletionTimestamps[id] = cloudDelTs[id];
    }
  });

  result._lastModified = Math.max(localMod, cloudMod);
  result._lastMerge = Date.now();

  // ⚠ SEC-001 — STRIPPING ON THE WAY OUT OPENS A HOLE ON EVERY WAY BACK IN, NOT JUST IMPORT.
  //
  // `secrets.js` states the rule in its own header: *"Strip on export, PRESERVE on import.
  // Neither half is safe alone."* It was then wired into exactly ONE door — `importData`.
  //
  // The cloud merge is another door. `settings` is a SCALAR_FIELD, so it is replaced WHOLESALE
  // when the cloud side wins on timestamp — and the cloud copy went through `stripSecrets` on
  // the way up, so it has no `settings.ai.apiKey`. The device's key is deleted, the next AI
  // call fails with "AI not configured", and nothing anywhere says why.
  //
  // Measured 2026-08-15: `settings.ai.apiKey` is set on his device (provider anthropic), and
  // `_scalarTimestamps.settings` (1786655074876, 2026-08-13) is two days OLDER than
  // `_lastModified` — so any cloud row whose settings stamp is newer, or which carries no
  // per-field stamp at all and falls back to its own `_lastModified`, wins this comparison and
  // takes the key with it. A second device changing one unrelated setting is enough.
  //
  // `local` is this device's state, which is where the secrets live. `preserveSecrets` fills
  // only paths the result does not already carry, so a local win is untouched and a device with
  // no key configured gains nothing.
  preserveSecrets(local, result);

  return { state: result, conflicts: allConflicts, hasConflicts: allConflicts.length > 0 };
}

// ============ CONFLICT RESOLUTION ============

let _pendingConflicts = null;
let _mergedStateCache = null;

/**
 * Show conflict resolution modal
 */
export function showConflictModal(conflicts, mergedState) {
  _pendingConflicts = conflicts;
  _mergedStateCache = mergedState;
  const body = document.getElementById('conflictBody');
  if (!body) return;

  let html = '<p style="color:var(--t2);margin-bottom:12px">Same items were modified on both devices. Choose how to resolve:</p>';
  html += '<div style="display:flex;flex-direction:column;gap:8px">';

  conflicts.forEach((c, _i) => {
    const localDate = c.local._updatedAt ? new Date(c.local._updatedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'unknown';
    const cloudDate = c.cloud._updatedAt ? new Date(c.cloud._updatedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'unknown';
    const ticker = c.local.ticker || c.cloud.ticker || c.id;
    html += `<div style="background:var(--bg1);border:1px solid var(--bd);border-radius:8px;padding:10px">
      <div style="font-weight:600;color:var(--acc);margin-bottom:4px">${esc(c.field)} — ${esc(ticker)}</div>
      <div style="display:flex;gap:12px;font-size:11px;color:var(--t3)">
        <span>Local: ${localDate}</span><span>Cloud: ${cloudDate}</span>
      </div></div>`;
  });

  html += '</div>';
  html += `<p style="color:var(--t3);font-size:11px;margin-top:12px">${conflicts.length} conflict(s). "Auto-Merge" keeps the newer version of each conflicted item.</p>`;
  body.innerHTML = html;

  const mo = document.getElementById('moConflict');
  mo.style.display = '';
  mo.classList.add('active');
}

/**
 * Resolve conflict with specified strategy: 'merge', 'cloud', or 'local'
 */
export function resolveConflict(strategy) {
  if (!_mergedStateCache) { clMo('moConflict'); return; }

  if (strategy === 'merge') {
    // Auto-merge already applied in _mergedStateCache — conflicts resolved by newest wins
    Object.assign(S, _mergedStateCache);
  } else if (strategy === 'cloud') {
    // CE-03 Fix: Prefer cloud versions for conflicted items only
    Object.assign(S, _mergedStateCache);
    if (_pendingConflicts) {
      _pendingConflicts.forEach(c => {
        const arr = S[c.field];
        if (arr) {
          const idx = arr.findIndex(x => x.id === c.id);
          if (idx >= 0) arr[idx] = c.cloud;
          else {
            // Only re-add and clear from deletedIds if item genuinely exists in cloud
            // (not just a stale conflict record)
            if (c.cloud && c.cloud._updatedAt > 0) {
              arr.push(c.cloud);
              if (S._deletedIds) { S._deletedIds = S._deletedIds.filter(did => did !== c.id); }
            }
          }
        }
      });
    }
  } else {
    // 'local' — override conflicted items with local versions
    Object.assign(S, _mergedStateCache);
    if (_pendingConflicts) {
      _pendingConflicts.forEach(c => {
        const arr = S[c.field];
        if (arr) {
          const idx = arr.findIndex(x => x.id === c.id);
          if (idx >= 0) arr[idx] = c.local;
        }
      });
    }
  }

  _pendingConflicts = null;
  _mergedStateCache = null;
  clMo('moConflict');
  document.getElementById('moConflict').style.display = 'none';
  save();
  if (_renderAll) _renderAll();
  toast('Conflict resolved — data synced', 'a-g');
  if (_lg) _lg('sync', '—', 'Conflict resolved: ' + strategy);
}

// ============ PERSISTENCE LAYER INTEGRATION ============

/** How long a change may sit local-only before it is pushed. Also the re-arm delay. */
export const CLOUD_DEBOUNCE_MS = 15000;

let _persistFn = null;
let _saveTimer = null;
let _sbReady = false;

/**
 * Retention caps that are not in the _CAPS loop, and the record of every trim.
 *
 * _CAPS already warns and writes S._truncated. Four other trims did neither: the history merge,
 * the mongo-signal prune, and the two nested AI arrays. A cap nobody is told about is
 * indistinguishable from data that was never there — and one of them (mongo signals) deletes
 * records permanently on every save.
 *
 * _lastTrims is deliberately NOT part of S: it must not travel to Supabase. Read it from the
 * console via window.__zagxTrims, or wire it into Tools → Integrity when that check is added.
 */
export const HISTORY_CAP = 1000;
export const MONGO_SIG_CAP = 150;
export const _lastTrims = {};
function _recordTrim(key, dropped) {
  const rec = _lastTrims[key] || (_lastTrims[key] = { dropped: 0, times: 0, at: null });
  rec.dropped += dropped; rec.times += 1; rec.at = new Date().toISOString();
  if (typeof window !== 'undefined') window.__zagxTrims = _lastTrims;
}

/** Get/clear the save debounce timer (for the leave-the-page flush) */
export function _getSaveTimer() { return _saveTimer; }
export function _clearSaveTimer() { if (_saveTimer) { clearTimeout(_saveTimer); _saveTimer = null; } }
/**
 * Put the debounce back after a flush that did not land.
 *
 * The flush cleared the timer BEFORE knowing whether cloudSave succeeded. cloudSave returns
 * false without writing in five ordinary situations — offline, no session, circuit breaker
 * open, another save in flight, the load-failed guard — and each of them left the pending
 * change with no timer, no retry, and the beforeunload fallback disarmed, because that
 * fallback is itself gated on the timer existing. Switching tabs on a flaky connection was
 * enough to strand an edit in localStorage indefinitely: the cloud then stays behind, and
 * another device reads the stale copy as current. (audit 2026-08-03)
 */
export function _rearmSaveTimer(ms) {
  if (_saveTimer) return;
  _saveTimer = setTimeout(() => { _saveTimer = null; cloudSave(); }, ms || CLOUD_DEBOUNCE_MS);
}

/**
 * PUBLISH THE PENDING SAVE NOW, AND PUT THE DEBOUNCE BACK IF IT DID NOT LAND.
 *
 * The flush-then-rearm rule was written once, for the tab-leave path, and lived inside
 * `main.js:_flushOnLeave` where nothing else could reach it. DATA-011 needed the same rule at a
 * second caller (a restore, which must reach the cloud before anything can merge against it), and
 * the choice was to copy nine lines or to move them. This file has lost that argument enough
 * times — `_totalCashUSD`, `computeTotalWealthUSD`, the default stop at five sites. One function,
 * N callers.
 *
 * Clearing the timer before knowing whether the write landed is what made `_rearmSaveTimer`
 * necessary: `cloudSave` answers false without writing when offline, session-less,
 * circuit-broken, already in flight, or blocked by the load-failed guard, and the beforeunload
 * keepalive is itself gated on the timer existing — so a bare clear disarms the documented
 * fallback as well.
 *
 * Returns true only if the write actually landed. Callers may ignore it; the re-arm is not
 * optional and happens here.
 */
export function flushCloudNow(reason) {
  if (!_saveTimer) return Promise.resolve(false);   // nothing pending
  _clearSaveTimer();
  let p;
  try { p = cloudSave(); } catch (_e) { p = false; }
  return Promise.resolve(p).then(ok => {
    if (!ok) {
      console.warn('[ZAGX] cloud flush on ' + reason + ' did not land — re-arming the debounce');
      _rearmSaveTimer();
    }
    return !!ok;
  }).catch(() => { _rearmSaveTimer(); return false; });
}

/**
 * ⚠ A RESTORE MUST WIN THE MERGE IT IS ABOUT TO MEET (DATA-011).
 *
 * `restoreBackup` and `importData` install rows carrying the BACKUP'S `_updatedAt`, and `save()`
 * stamps a row only when the field is ABSENT — so a restored row keeps a stamp older than the
 * cloud's copy of the same row. Scalars do not: `save()` stamps every scalar whose JSON changed
 * with `now`. The next `mergeStates` therefore keeps the restored scalars and hands the arrays
 * back to the cloud, row by row. A blend, and nothing on screen says so.
 *
 * Measured 2026-08-16 on the live book against the 2026-08-13 auto-backup: of the 90 rows that
 * differed, the 7 the cloud had modified were all decided for the cloud, and 0 for the backup.
 *
 * Stamping every restored row with `now` makes the restore authoritative, which is what the
 * confirm dialog already promises. **The cost is real and deliberate:** it also outranks a
 * genuinely newer edit made on a second device. A restore is explicit, confirmed and destructive
 * by construction; a merge is not the place to second-guess it. (Owner's decision, 2026-08-16.)
 *
 * ⚠ **WHAT THIS DOES NOT FIX.** `mergeArrays` is a UNION: a row the cloud holds and the restored
 * state does not is re-added regardless of any timestamp. So rows created after the backup — the
 * ones a restore is meant to remove — still come back, and rows deleted after the backup still
 * cannot be resurrected, because the tombstone union outranks every clock. Only publishing before
 * a merge can happen (`flushCloudNow`) closes those. Pinned by the tests.
 */
export function restampRestoredRows(at) {
  const now_ = at || Date.now();
  let stamped = 0;
  MERGE_ARRAYS.forEach(key => {
    const arr = S[key];
    if (!Array.isArray(arr)) return;
    arr.forEach(item => {
      if (item && typeof item === 'object' && item.id) { item._updatedAt = now_; stamped++; }
    });
  });
  return stamped;
}
let _idbReady = false;

// SYNC-04: Track previous scalar field values to detect changes per-field
const _prevScalars = {};

/**
 * Seed the scalar baseline from the CURRENT state. Call after load() and after any poll/conflict
 * merge so the next save() only stamps fields the USER actually changed — otherwise the first save
 * marks every scalar "changed now" and can stomp a newer cloud scalar from another device (D-11/D-14).
 */
export function seedScalarBaseline() {
  SCALAR_FIELDS.forEach(key => { _prevScalars[key] = JSON.stringify(S[key]); });
}

/**
 * Register the persistence callback (called from persistence.js)
 */
export function _setPersistFn(fn) {
  _persistFn = fn;
}

/**
 * Set persistence layer readiness flags (called from persistence.js)
 */
export function _setReadyFlags(sbReady, idbReady) {
  _sbReady = sbReady;
  _idbReady = idbReady;
}

/**
 * STABILIZATION: Callback registrations to avoid circular imports
 * renderAll() and lg() are UI functions that state.js needs for resolveConflict
 */
let _renderAll = null;
let _lg = null;
export function _setRenderCallbacks(renderFn, lgFn) {
  _renderAll = renderFn;
  _lg = lgFn;
}

/**
 * IDB save stub (will be defined in persistence.js)
 */
let idbSaveState = async () => {};
// DATA-006. `save()` writes localStorage HERE, not through persistence.js's safeLocalSave — a
// fourth local writer that a source-grep of persistence.js can never find. Injected rather than
// imported: state.js is imported BY persistence.js, so the dependency only runs one way.
// Defaults permissive, so anything that never wires it is unchanged.
let _canWriteLocal = () => true;

/**
 * Cloud save stub (will be wired from persistence.js)
 */
let cloudSave = async () => { console.warn('[ZAGX] cloudSave stub called — not wired!'); };

/**
 * Cloud snapshot stub (will be wired from persistence.js)
 */
let _cloudSnapshot = async () => { console.warn('[ZAGX] cloudSnapshot stub called — not wired!'); };

/**
 * Set IDB, cloud save, and cloud snapshot functions from persistence.js
 * CRITICAL: Without this wiring, save() only writes to IDB + LS, NOT to Supabase.
 */
export function _setPersistenceFunctions(idb, cloud, snapshot, canWriteLocal) {
  idbSaveState = idb;
  if (typeof canWriteLocal === 'function') _canWriteLocal = canWriteLocal;
  cloudSave = cloud;
  if (snapshot) _cloudSnapshot = snapshot;
  console.log('[ZAGX] Persistence functions wired: idb=%s, cloud=%s, snapshot=%s',
    !!idb, !!cloud, !!snapshot);
}

// ============ SAVE & STORAGE ============

/**
 * Unified save: IDB (local) + LS (fallback) + Supabase (cloud, debounced)
 */
export function save() {
  // Stamp global modification time for conflict resolution
  const now_ = Date.now();
  S._lastModified = now_;
  // SYNC-04 Fix: Per-field scalar timestamps for fine-grained merge
  if (!S._scalarTimestamps) S._scalarTimestamps = {};
  // Detect which scalar fields changed since last save
  SCALAR_FIELDS.forEach(key => {
    const cur = JSON.stringify(S[key]);
    const prev = _prevScalars[key];
    if (cur !== prev) {
      // A field going from HAVING data to being undefined is a disappearance, not an
      // edit — nothing in this app deletes a whole scalar field on purpose. Stamping it
      // as a change makes the loss authoritative: the next merge sees local holding the
      // newest timestamp for that key and discards the cloud copy that still has the
      // data. Observed with benchmarkPrices, which came back after a reload with its
      // timestamp stamped and its contents gone, and then propagated that emptiness.
      // Do not stamp, do not update _prevScalars — leave the older stamp standing so a
      // surviving copy can win the merge and restore it. (audit 2026-08-01)
      if (cur === undefined && prev !== undefined && prev !== 'null') {
        console.warn('[ZAGX] scalar "' + key + '" vanished between saves — not stamping, so a surviving copy can win the next merge');
        return;
      }
      S._scalarTimestamps[key] = now_;
      _prevScalars[key] = cur;
    }
  });
  if (!S._deletedIds) S._deletedIds = [];

  // Auto-stamp any items that were just added or modified without _updatedAt
  MERGE_ARRAYS.forEach(key => {
    const arr = S[key];
    if (!Array.isArray(arr)) return;
    arr.forEach(item => {
      if (item && typeof item === 'object' && item.id && !item._updatedAt) {
        item._updatedAt = now_;
      }
    });
  });

  // CE-15 Fix: Cap unbounded arrays to prevent state bloat
  const _CAPS = { cashTxs: 2000, realized: 500, rfTxs: 200, dividends: 200, signals: 500, signalAlerts: 200, notifications: 200 };

  /**
   * THE SORT KEY HAS TO EXIST ON THE ROW.
   *
   * This loop sorted `(b.date || '').localeCompare(a.date || '')` for all seven arrays and
   * called the result "newest first". Measured on the live book 2026-08-15: **three of the
   * seven have no `date` field on a single row.**
   *
   *   signals        168 rows, 0 with `date` — the field is `sourceDate` / `createdAt`
   *   signalAlerts    27 rows, 0 with `date` — the field is `triggeredAt`
   *   notifications  200 rows, 0 with `date` — the field is `ts`
   *
   * When no row has the key, every comparison is `''.localeCompare('')` → 0. The sort is a
   * no-op, and `.slice(0, cap)` keeps whatever the INSERT ORDER happened to be:
   *
   *   `notifications` is `.unshift`-ed → newest first → the cap kept the newest. Correct, and
   *      correct only by accident. It is AT the cap today and trims on every save.
   *   `signals` and `signalAlerts` are `.push`-ed → OLDEST first → the cap would have kept the
   *      500 oldest signals and deleted the newest, exactly inverted, and permanently: both
   *      sides of the merge trim identically, so the cloud copy cannot hand them back.
   *
   * Neither is at its cap yet, so this is a mine and not a bleed. The accessor is what defuses
   * it — name the field each array actually carries, per array, and let the test hold it there.
   */
  const _CAP_TS = {
    cashTxs: r => r.date,
    realized: r => r.date,
    rfTxs: r => r.date,
    dividends: r => r.date,
    signals: r => r.sourceDate || r.createdAt,
    signalAlerts: r => r.triggeredAt || r.dismissedAt,
    notifications: r => r.ts,
  };

  Object.entries(_CAPS).forEach(([k, cap]) => {
    const arr = S[k];
    if (!Array.isArray(arr) || arr.length <= cap) return;
    const get = _CAP_TS[k] || (r => r && r.date);
    const key = r => { try { return String((r && get(r)) || ''); } catch { return ''; } };
    // A row this app cannot date is a row it must not CHOOSE to delete. Undated rows sort to
    // the front and survive; the cap exists to bound size, not to pick a victim by accident.
    // The count travels with the warning, so an undatable row is visible rather than inferred.
    arr.sort((a, b) => {
      const ka = key(a), kb = key(b);
      if (ka === kb) return 0;
      if (!ka) return -1;
      if (!kb) return 1;
      return kb.localeCompare(ka);
    });
    const undated = arr.filter(r => !key(r)).length;
    const dropped = arr.length - cap;
    const oldestKept = key(arr[cap - 1]) || 'undated';
    S[k] = arr.slice(0, cap);
    console.warn(`[ZAGX] ${k} capped: dropped ${dropped} oldest entries (kept ${cap} newest, oldest kept ${oldestKept}`
      + (undated ? `, ${undated} undated row(s) kept regardless` : '') + ')');
    // Flag that truncation occurred so reconciler doesn't auto-fix cash mismatch
    S._truncated = S._truncated || {};
    S._truncated[k] = { dropped, at: now_, undated };
    // …AND record it where the Integrity page reads from. `S._truncated` is consumed for
    // `cashTxs` and nothing else, so a trim of the other six was written down and read by no
    // one — while `retention.trims`, the surface built for exactly this, went on reporting
    // "nothing trimmed this session". Live at the moment this was found: `_truncated` held
    // `notifications −1` from minutes earlier and `__zagxTrims` did not mention it.
    _recordTrim(k, dropped);
  });

  // D-1 Fix: cap NESTED AI arrays (not top-level, so missed by _CAPS loop) to prevent slow JSONB growth
  // slice(-N) keeps the LAST N by array order, which is only "newest" if nothing ever inserts
  // out of order. Both are append-only today, so the order is left alone — what was missing is
  // the notice, same as the two above.
  if (S.aiCopilot && Array.isArray(S.aiCopilot.scanHistory) && S.aiCopilot.scanHistory.length > 200) {
    const dropped = S.aiCopilot.scanHistory.length - 200;
    S.aiCopilot.scanHistory = S.aiCopilot.scanHistory.slice(-200); // keep newest 200
    console.warn('[ZAGX] aiCopilot.scanHistory capped: dropped ' + dropped + ' oldest');
    _recordTrim('aiCopilot.scanHistory', dropped);
  }
  if (S.aiInsights && Array.isArray(S.aiInsights.items) && S.aiInsights.items.length > 100) {
    const dropped = S.aiInsights.items.length - 100;
    S.aiInsights.items = S.aiInsights.items.slice(-100);
    console.warn('[ZAGX] aiInsights.items capped: dropped ' + dropped + ' oldest');
    _recordTrim('aiInsights.items', dropped);
  }
  // Keep Mongo-sourced signals lean & bounded (precondition: avoid Supabase bloat)
  if (Array.isArray(S.signals)) {
    // ── One analyst is one analyst: undo UTF-8-read-as-GBK on the display fields. ──
    // 129 occurrences of six characters across `sourceName` and `sourceTitle` in his live book;
    // the mechanism, the scope and why `assetName` is excluded are in `utils.demojibake`.
    // ⚠ THE `!==` IS WHAT KEEPS THIS QUIET. `demojibake` hands back the SAME string when there is
    // nothing to fix, so a clean book stamps nothing — and stamping `_updatedAt` on every signal
    // every save would present the whole feed to the cloud merge as freshly edited.
    // ⚠ AND A REPAIRED ROW MUST BE STAMPED. Without it the merge sees the un-repaired copy on the
    // other device as the newer side and puts the mojibake straight back (trap #6's lesson).
    let _moji = 0;
    const _fixStr = (o, k) => {
      const v = o[k]; if (typeof v !== 'string') return false;
      const n = demojibake(v); if (n === v) return false;
      o[k] = n; _moji++; return true;
    };
    const _fixArr = (o, k) => {
      if (!Array.isArray(o[k])) return false;
      let hit = false;
      o[k].forEach((v, i) => {
        if (typeof v !== 'string') return;
        const n = demojibake(v); if (n === v) return;
        o[k][i] = n; _moji++; hit = true;
      });
      return hit;
    };
    S.signals.forEach(s => {
      if (!s) return;
      let hit = false;
      // `assetName` and `_extId` are deliberately absent — see the docblock on demojibake.
      if (_fixStr(s, 'sourceName')) hit = true;
      if (_fixStr(s, 'sourceTitle')) hit = true;
      if (_fixArr(s, 'notes')) hit = true;
      if (_fixArr(s, 'watchTriggers')) hit = true;
      if (hit) s._updatedAt = Date.now();
    });
    (S.signalSources || []).forEach(x => {
      if (!x) return;
      let hit = false;
      if (_fixStr(x, 'name')) hit = true;
      if (_fixStr(x, 'analyst')) hit = true;
      if (hit) x._updatedAt = Date.now();
    });
    if (_moji) console.warn('[ZAGX] mis-decoded text repaired: ' + _moji
      + ' string(s) across signals/signalSources (UTF-8 read as GBK, upstream)');

    // strip display-only evidencePhrases from mongo signals (regenerable from source)
    S.signals.forEach(s => { if (s && typeof s.sourceId === 'string' && s.sourceId.startsWith('mongo') && s.evidencePhrases) delete s.evidencePhrases; });
    // ── Collapse duplicate report lines BEFORE the cap. Order matters: 16 duplicate rows are 16
    // slots of the 150-row window spent on copies, so deduping first is what decides whether a
    // real signal survives the cap. See the docblock above duplicateSignalGroups.
    const _dupGroups = duplicateSignalGroups(S.signals);
    if (_dupGroups.length) {
      let _dropped = 0, _repointed = 0;
      _dupGroups.forEach(g => g.drop.forEach(id => {
        // Re-point before deleting: an alert left pointing at a dead id is an orphan, and
        // signalAlerts is a MERGE_ARRAYS member so it cannot simply be filtered either.
        (S.signalAlerts || []).forEach(a => {
          if (a && a.signalId === id) { a.signalId = g.keep; a._updatedAt = Date.now(); _repointed++; }
        });
        if (softDelete('signals', id)) _dropped++;
      }));
      const _deadAlerts = duplicateAlertIds(S.signalAlerts);
      _deadAlerts.forEach(id => softDelete('signalAlerts', id));
      console.warn('[ZAGX] duplicate signals collapsed: ' + _dropped + ' row(s) across '
        + _dupGroups.length + ' report line(s)'
        + (_repointed ? ' · ' + _repointed + ' alert(s) re-pointed' : '')
        + (_deadAlerts.length ? ' · ' + _deadAlerts.length + ' duplicate alert(s) removed' : ''));
      _recordTrim('signals.duplicate', _dropped);
      if (_deadAlerts.length) _recordTrim('signalAlerts.duplicate', _deadAlerts.length);
    }
    // cap mongo-sourced signals to newest 150 (separate from the global 500 cap)
    const mongoSigs = S.signals.filter(s => typeof s.sourceId === 'string' && s.sourceId.startsWith('mongo'));
    if (mongoSigs.length > MONGO_SIG_CAP) {
      // sort by report date (stable) so the newest reports' signals survive re-imports → preserves status, avoids churn
      mongoSigs.sort((a, b) => (b.sourceDate || b.createdAt || '').localeCompare(a.sourceDate || a.createdAt || ''));
      const keep = new Set(mongoSigs.slice(0, MONGO_SIG_CAP).map(s => s.id));
      const dropped = mongoSigs.length - MONGO_SIG_CAP;
      S.signals = S.signals.filter(s => !(typeof s.sourceId === 'string' && s.sourceId.startsWith('mongo')) || keep.has(s.id));
      // This one deletes records permanently on every save. Unlike the _CAPS loop above it
      // said nothing, so a signal the owner had read about could vanish between two views
      // with no trace. (audit 2026-08-03)
      console.warn('[ZAGX] mongo signals capped: dropped ' + dropped + ' oldest of ' + mongoSigs.length
        + ' (kept ' + MONGO_SIG_CAP + ' newest by report date)');
      _recordTrim('signals.mongo', dropped);
    }
  }

  // Prune alertedKeys older than 30 days
  if (S.alertedKeys) {
    const cutoff = now_ - 30 * 86400000;
    Object.keys(S.alertedKeys).forEach(k => {
      if (S.alertedKeys[k] < cutoff) delete S.alertedKeys[k];
    });
  }

  // Tombstone retention: SIZE-CAP ONLY (no time-expiry). A 30-day TTL let a long-offline device
  // resurrect a deletion once its tombstone expired (D-10); keeping the newest N ids is cheap (id
  // strings) and removes that hole. Cap is generous since ids are tiny.
  if (!S._deletionTimestamps) S._deletionTimestamps = {};
  (S._deletedIds || []).forEach(id => {
    if (!S._deletionTimestamps[id]) S._deletionTimestamps[id] = now_;
  });
  const DEL_CAP = 1000;
  if (S._deletedIds && S._deletedIds.length > DEL_CAP) {
    // keep the newest DEL_CAP by timestamp; drop oldest
    S._deletedIds.sort((a, b) => (S._deletionTimestamps[a] || 0) - (S._deletionTimestamps[b] || 0));
    const dropped = S._deletedIds.splice(0, S._deletedIds.length - DEL_CAP);
    dropped.forEach(id => delete S._deletionTimestamps[id]);
  }

  const json = JSON.stringify(S);

  // 1. IDB: immediate local cache (always, for offline access)
  idbSaveState(S).catch(e => {
    if (e && (e.name === 'QuotaExceededError' || e.message?.includes('quota')))
      toast('Storage full — data may not persist offline. Export a backup.', 'a-r');
  });

  // 2. LS: fast fallback — unless this device holds a copy that could not be read and nothing
  // else loaded, in which case writing would destroy the only copy there is. (DATA-006)
  if (_canWriteLocal()) {
    try {
      localStorage.setItem('zagx_v3', json);
    } catch (e) {
      if (e.name === 'QuotaExceededError') toast('LocalStorage full — export backup recommended', 'a-r');
    }
  }

  // 3. Supabase: debounced cloud sync. CLOUD_DEBOUNCE_MS (was 60s) — shrinks the window of
  // local-ahead-of-cloud drift. The visibilitychange handler in main.js flushes
  // this timer on tab hide, so most syncs happen on leave, not on the timer;
  // the hash-skip + mutex in cloudSave still prevent redundant/concurrent writes,
  // so shorter debounce does not increase writes for unchanged state.
  if (_saveTimer) clearTimeout(_saveTimer);
  _saveTimer = setTimeout(() => {
    _saveTimer = null;
    cloudSave();
  }, CLOUD_DEBOUNCE_MS);

  updateStorageBadge(json.length);
}

/**
 * Update storage badge with current size and source
 */
export function updateStorageBadge(bytes) {
  const el = document.getElementById('storageBadge');
  if (!el) return;

  const kb = Math.round(bytes / 1024);
  const mb = (bytes / 1048576).toFixed(1);
  const src = _sbReady ? 'Cloud' : _idbReady ? 'IDB' : 'LS';

  el.style.color = 'var(--t3)';
  el.textContent = (kb < 1024 ? kb + 'KB' : mb + 'MB') + ' (' + src + ')';
  el.title = src + ': ' + mb + 'MB';
}

// ============ MIGRATIONS ============

/**
 * Schema migrations: each runs once when _schemaVersion < its target version
 */
/**
 * Rewrite monthly purchase entries whose recorded quantity is not what was actually paid,
 * and move the holding by exactly the same amount.
 *
 * Two separate defects have this shape. The TR funds took a lump sum where a quantity
 * belongs (TRY 4,274 became "4 units"). The April cash rows divided by the FX rate one
 * extra time (TRY 5,343.60 became 2.6948 USD instead of 120). Different causes, identical
 * repair: the price on the entry is the one input nobody can re-derive, so it is left alone
 * and the quantity is recomputed from what the bank actually took.
 *
 * GUARDED ON THE DEFECT, NOT ON THE SCHEMA NUMBER. An entry is rewritten only when what it
 * records is under a tenth of what was paid. A month already correct, hand-fixed, or
 * arriving from an older backup or another device is left exactly as it stands — so a
 * restore or a cross-device merge can never apply the correction twice.
 *
 * @param {Object<string, Object<string, number>>} spend  ticker -> YYYY-MM -> TRY paid
 * @returns {number} entries rewritten
 */
function _repriceFromAmountPaid(spend) {
  let fixed = 0;
  Object.keys(spend).forEach(tk => {
    let dQty = 0, dCost = 0;
    Object.keys(spend[tk]).forEach(m => {
      const paid = spend[tk][m];
      const month = (S.monthlyRates || {})[m];
      const e = month && month.entries && month.entries[tk];
      // No price means no way to turn a lump sum into units. Skipping is the honest outcome:
      // a fabricated price would make the position look reconciled when it is not.
      if (!e || !(e.price > 0)) return;
      const wasCost = (e.qty || 0) * e.price;
      if (wasCost >= paid / 10) return;
      const qty = +(paid / e.price).toFixed(4);
      dQty += qty - (e.qty || 0);
      // The ROUNDED quantity, not `paid`, so the Monthly page (which multiplies qty by
      // price) and the holding can never disagree by a rounding step.
      dCost += qty * e.price - wasCost;
      e.qty = qty;
      fixed++;
    });
    if (!dQty) return;
    const h = (S.holdings || []).find(x => x && x.ticker === tk);
    if (!h) return;
    const cost = (h.qty || 0) * (h.avgCost || 0) + dCost;
    h.qty = +((h.qty || 0) + dQty).toFixed(4);
    h.avgCost = h.qty > 0 ? cost / h.qty : 0;
    h._updatedAt = Date.now();
  });
  return fixed;
}

export const MIGRATIONS = [
  {
    v: 1,
    name: 'cashBalance→cash object',
    run: () => {
      if (typeof S.cashBalance === 'number') {
        S.cash = { USD: S.cashBalance, TRY: 0, EUR: 0 };
        delete S.cashBalance;
      }
      if (!S.cash) S.cash = { USD: 0, TRY: 0, EUR: 0 };
    }
  },
  {
    v: 2,
    name: 'alertedKeys init',
    run: () => {
      if (!S.alertedKeys) S.alertedKeys = {};
    }
  },
  {
    v: 3,
    name: 'expenseDefs + cashflow init',
    run: () => {
      if (!S.expenseDefs) S.expenseDefs = [];
      if (!S.cashflow) S.cashflow = {};
    }
  },
  {
    v: 4,
    name: 'reserveFund + rfTxs init',
    run: () => {
      if (S.reserveFund == null) S.reserveFund = 0;
      if (!S.rfTxs) S.rfTxs = [];
    }
  },
  {
    v: 5,
    name: 'cashTx rebuild from source of truth',
    run: () => {
      if (!S.cashTxs || !S.trades || !S.holdings.length) return;

      const userTxs = S.cashTxs.filter(tx =>
        (tx.type === 'deposit' || tx.type === 'withdraw') &&
        !tx.note?.startsWith('Monthly IBKR deposit') &&
        !tx.note?.startsWith('Initial IBKR deposit') &&
        tx.type !== 'portfolio_buy'
      );

      const tradeTxs = S.trades.filter(t => t.status !== 'Closed').map(t => {
        const cur = t.currency || 'USD';
        const cost = t.entry * t.qty;
        return {
          id: t.id + '_cash',
          date: t.openDate || now(),
          type: 'trade_open',
          amount: -cost,
          currency: cur,
          note: 'Open ' + t.ticker + ' ' + t.side
        };
      });

      const moTxs = [];
      Object.keys(S.monthlyRates).sort().forEach(mo => {
        const r = S.monthlyRates[mo];
        const st = r.status || 'completed';
        if (st === 'reconciled' || st === 'completed') {
          const amt = S.base * (r.mul || 1);
          moTxs.push({
            id: 'dep_' + mo,
            date: mo + '-28T12:00:00',
            type: 'deposit',
            amount: amt,
            currency: 'USD',
            note: 'Monthly IBKR deposit — ' + mo
          });
          moTxs.push({
            id: 'buy_' + mo,
            date: mo + '-28T12:01:00',
            type: 'portfolio_buy',
            amount: -amt,
            currency: 'USD',
            note: 'Portfolio allocation — ' + mo
          });
        }
      });

      S.cashTxs = [...tradeTxs.reverse(), ...moTxs.reverse(), ...userTxs];

      const rc = { USD: 0, TRY: 0, EUR: 0 };
      S.cashTxs.forEach(tx => {
        const cur = tx.currency || 'USD';
        rc[cur] = (rc[cur] || 0) + tx.amount;
      });
      S.cash = { USD: rc.USD, TRY: rc.TRY, EUR: rc.EUR };
    }
  },
  {
    v: 6,
    name: 'incomeDefs init',
    run: () => {
      if (!S.incomeDefs) S.incomeDefs = [];
    }
  },
  {
    v: 7,
    name: 'budgets init',
    run: () => {
      if (!S.budgets) S.budgets = { monthlyLimit: 0, categories: {} };
    }
  },
  {
    v: 8,
    name: 'dividends init',
    run: () => {
      if (!S.dividends) S.dividends = [];
    }
  },
  {
    v: 9,
    name: 'per-month income entries',
    run: () => {
      // Convert scalar mo.income → mo.incomes[] array with a single Salary entry
      if (!S.cashflow) return;
      Object.keys(S.cashflow).forEach(k => {
        const mo = S.cashflow[k];
        if (!mo) return;
        if (!Array.isArray(mo.incomes)) mo.incomes = [];
        if (mo.incomes.length === 0 && (mo.income || 0) > 0) {
          mo.incomes.push({
            id: uid(),
            day: 1,
            amount: mo.income,
            type: 'Salary',
            note: 'Migrated from legacy field'
          });
        }
      });
    }
  },
  {
    v: 10,
    name: 'loanStart format fix + v5 idempotency flag',
    run: () => {
      // Fix loanStart: convert text month names to YYYY-MM
      const monthMap = {
        january: '01', february: '02', march: '03', april: '04', may: '05', june: '06',
        july: '07', august: '08', september: '09', october: '10', november: '11', december: '12',
        jan: '01', feb: '02', mar: '03', apr: '04', jun: '06', jul: '07', aug: '08',
        sep: '09', oct: '10', nov: '11', dec: '12'
      };

      (S.expenseDefs || []).forEach(d => {
        if (d.loanStart && typeof d.loanStart === 'string' && !/^\d{4}-\d{2}$/.test(d.loanStart)) {
          const lower = d.loanStart.toLowerCase().trim();
          const mm = monthMap[lower];
          if (mm) {
            const y = new Date().getFullYear();
            d.loanStart = y + '-' + mm;
            bumpMigrated(d);
            console.log('[ZAGX] Migration v10: fixed loanStart "' + d.name + '": ' + lower + ' → ' + d.loanStart);
          } else {
            console.warn('[ZAGX] Migration v10: cannot parse loanStart "' + d.loanStart + '" for "' + d.name + '"');
            d.loanStart = null; // clear invalid value
            bumpMigrated(d);
          }
        }
      });

      // (A `S._v5_migrated = true` flag stood here to "prevent double-run". It never could:
      //  the migration's own version number is the guard, runMigrations only runs entries above
      //  the stored _schemaVersion, and nothing ever read the flag. Removed 2026-08-03.)
    }
  },
  {
    v: 11,
    name: 'liabilities init',
    run: () => {
      if (!S.liabilities) S.liabilities = [];
    }
  },
  {
    v: 12,
    name: 'goals + achievements init',
    run: () => {
      if (!S.goals) S.goals = [];
      if (!S.goalSettings) S.goalSettings = { freedomNumber: 0, investingSince: null };
      if (!S.achievements) S.achievements = [];
    }
  },
  {
    v: 13,
    name: 'reserveFund → multi-currency object',
    run: () => {
      // Migrate from single number (TRY) to multi-currency object
      if (typeof S.reserveFund === 'number') {
        const oldVal = S.reserveFund || 0;
        S.reserveFund = { TRY: oldVal, USD: 0, EUR: 0, XAU: 0, XAG: 0, GBP: 0 };
      } else if (!S.reserveFund || typeof S.reserveFund !== 'object') {
        S.reserveFund = { TRY: 0, USD: 0, EUR: 0, XAU: 0, XAG: 0, GBP: 0 };
      } else {
        // Ensure all keys exist
        ['TRY', 'USD', 'EUR', 'XAU', 'XAG', 'GBP'].forEach(k => {
          if (S.reserveFund[k] == null) S.reserveFund[k] = 0;
        });
      }
    }
  },
  {
    v: 14,
    name: 'multi-portfolio support',
    run: () => {
      if (!S.portfolios) S.portfolios = [];
      if (S.activePortfolioId === undefined) S.activePortfolioId = null;
      // Create default Retirement portfolio from existing holdings
      if (S.portfolios.length === 0) {
        const defId = uid();
        S.portfolios.push({
          id: defId,
          name: 'Retirement',
          type: 'retirement',
          horizon: 'long',
          tags: ['dca', 'untouchable'],
          isDefault: true,
          status: 'active',
          base: S.base || 2400,
          createdAt: now(),
          _updatedAt: Date.now()
        });
        // Assign all existing holdings to default portfolio
        (S.holdings || []).forEach(h => {
          if (!h.portfolioId) { h.portfolioId = defId; bumpMigrated(h); }
        });
      }
    }
  },
  {
    v: 15,
    name: 'signal intelligence module',
    run: () => {
      if (!S.signals) S.signals = [];
      if (!S.signalSources) S.signalSources = [];
      if (!S.signalAlerts) S.signalAlerts = [];
      if (!S.tickerMap) S.tickerMap = {};
      // Seed default BIST ticker map (name → ticker)
      const defaultMap = {
        'Dogus Otomotiv': 'DOAS.IS',
        'Ford Otomotiv': 'FROTO.IS',
        'Is Yatirim Menkul': 'ISMEN.IS',
        'Aksa Akrilik': 'AKSA.IS',
        'Turk Hava Yollari': 'THYAO.IS',
        'Aselsan': 'ASELS.IS',
        'Tupras': 'TUPRS.IS',
        'Enerjisa': 'ENJSA.IS',
        'Is GYOA': 'ISGYO.IS'
      };
      Object.entries(defaultMap).forEach(([name, tk]) => {
        if (!S.tickerMap[name]) S.tickerMap[name] = tk;
      });
    }
  },
  {
    v: 16,
    name: 'lot-based holdings + SAP XETRA seed',
    run: () => {
      // Skip if SAP holding already exists (re-run safety)
      if (S.holdings.some(h => h.ticker === 'SAP')) return;

      // SAP SE historical lots — 9 months (Aug 2025 – Apr 2026)
      const sapLots = [
        { id: uid(), date: '2025-08-31', type: 'investment', qty: 4.025532, price: 233.30, cost: 939.17 },
        { id: uid(), date: '2025-08-31', type: 'drip', qty: 1.695947, price: 233.30, cost: 395.67 },
        { id: uid(), date: '2025-09-30', type: 'investment', qty: 3.955219, price: 237.45, cost: 939.17 },
        { id: uid(), date: '2025-09-30', type: 'drip', qty: 1.666324, price: 237.45, cost: 395.67 },
        { id: uid(), date: '2025-10-31', type: 'investment', qty: 4.170062, price: 225.22, cost: 939.17 },
        { id: uid(), date: '2025-10-31', type: 'drip', qty: 1.756837, price: 225.22, cost: 395.67 },
        { id: uid(), date: '2025-11-30', type: 'investment', qty: 4.458767, price: 210.63, cost: 939.17 },
        { id: uid(), date: '2025-11-30', type: 'drip', qty: 1.878467, price: 210.63, cost: 395.67 },
        { id: uid(), date: '2025-12-31', type: 'investment', qty: 4.500333, price: 208.69, cost: 939.17 },
        { id: uid(), date: '2025-12-31', type: 'drip', qty: 1.895979, price: 208.69, cost: 395.67 },
        { id: uid(), date: '2026-01-31', type: 'investment', qty: 5.486415, price: 171.18, cost: 939.17 },
        { id: uid(), date: '2026-01-31', type: 'drip', qty: 2.311413, price: 171.18, cost: 395.67 },
        { id: uid(), date: '2026-02-28', type: 'investment', qty: 5.582188, price: 168.24, cost: 939.17 },
        { id: uid(), date: '2026-02-28', type: 'drip', qty: 2.351762, price: 168.24, cost: 395.67 },
        { id: uid(), date: '2026-03-31', type: 'investment', qty: 6.130154, price: 153.20, cost: 939.17 },
        { id: uid(), date: '2026-03-31', type: 'drip', qty: 2.582619, price: 153.20, cost: 395.67 },
        { id: uid(), date: '2026-04-30', type: 'investment', qty: 6.584874, price: 149.87, cost: 986.87 },
        { id: uid(), date: '2026-04-30', type: 'drip', qty: 2.767413, price: 149.87, cost: 414.75 }
      ];

      const totalQty = sapLots.reduce((s, l) => s + l.qty, 0);
      const totalCost = sapLots.reduce((s, l) => s + l.cost, 0);
      const avgCost = totalCost / totalQty;

      const sapId = uid();
      const sapHolding = {
        id: sapId,
        ticker: 'SAP',
        name: 'SAP SE',
        type: 'Stock',
        subType: 'EU Stock',
        portfolioName: 'IBKR',
        broker: 'IBKR',
        buyWindow: '28-31',
        allocationPct: 0,
        currency: 'EUR',
        qty: totalQty,
        avgCost: Math.round(avgCost * 100) / 100,
        lots: sapLots,
        _updatedAt: Date.now()
      };
      S.holdings.push(sapHolding);

      // Create dividend records for each DRIP entry
      if (!S.dividends) S.dividends = [];
      sapLots.filter(l => l.type === 'drip').forEach(l => {
        const dvId = uid();
        S.dividends.push({
          id: dvId, date: l.date, ticker: 'SAP',
          gross: l.cost, currency: 'EUR',
          whtPct: 0, whtAmt: 0, net: l.cost,
          note: `SAP DRIP — ${l.qty.toFixed(4)} shares @ €${l.price.toFixed(2)}`,
          drip: true,
          _updatedAt: Date.now()
        });
        // DRIP cashTx pair (deposit + withdraw) for audit trail
        S.cashTxs.unshift({
          id: uid(), date: l.date + 'T12:00:00', type: 'deposit',
          amount: l.cost, currency: 'EUR',
          note: 'Dividend: SAP (DRIP)', _updatedAt: Date.now()
        });
        S.cashTxs.unshift({
          id: uid(), date: l.date + 'T12:01:00', type: 'withdraw',
          amount: -l.cost, currency: 'EUR',
          note: 'DRIP reinvest: SAP', _updatedAt: Date.now()
        });
      });

      console.log('[ZAGX] Migration v16: SAP holding created with ' + sapLots.length + ' lots, qty=' + totalQty.toFixed(4) + ', avgCost=€' + avgCost.toFixed(2));
    }
  },
  {
    v: 17,
    name: 'SAP XETRA dedicated portfolio',
    run: () => {
      if (!S.portfolios) S.portfolios = [];

      // Create SAP XETRA portfolio if it doesn't exist yet
      const existing = S.portfolios.find(p => p.name === 'SAP XETRA');
      const sapPortId = existing ? existing.id : uid();

      if (!existing) {
        S.portfolios.push({
          id: sapPortId,
          name: 'SAP XETRA',
          type: 'investment',
          horizon: 'long',
          tags: ['dca', 'eu-stock', 'drip'],
          isDefault: false,
          status: 'active',
          base: 0,
          createdAt: now(),
          _updatedAt: Date.now()
        });
        console.log('[ZAGX] Migration v17: Created SAP XETRA portfolio');
      }

      // Fix SAP holding — assign to new portfolio, correct broker/portfolioName
      const sapH = S.holdings.find(h => h.ticker === 'SAP');
      if (sapH) {
        sapH.portfolioId = sapPortId;
        sapH.portfolioName = 'SAP XETRA';
        sapH.broker = 'XETRA';
        sapH._updatedAt = Date.now();
        console.log('[ZAGX] Migration v17: SAP holding assigned to SAP XETRA portfolio');
      }
    }
  },
  {
    v: 18,
    name: 'One-time income array',
    run: () => {
      if (!S.oneTimeIncome) S.oneTimeIncome = [];
    }
  },
  {
    v: 19,
    name: 'Bonus plans and quarters',
    run: () => {
      if (!S.bonusPlans) S.bonusPlans = [];
      if (!S.bonusQuarters) S.bonusQuarters = [];

      // Seed FY26 plan if empty (calendar year 2026, Q1=Jan-Mar … Q4=Oct-Dec)
      if (!S.bonusPlans.length) {
        const fy26id = uid();
        S.bonusPlans.push({
          id: fy26id, label: 'FY26', fiscalYear: 'FY26',
          startDate: '2026-01-01', endDate: '2026-12-31',
          targetEUR: 74028, currency: 'EUR',
          allocFramework: [
            { label: 'Liquid (deposits/daily)', pct: 22.5, color: '#4ecdc4' },
            { label: 'Portfolio (IBKR + Garanti)', pct: 42.5, color: '#3b82f6' },
            { label: 'Tactical (BTC/BIST)', pct: 22.5, color: '#f59e0b' },
            { label: 'Personal', pct: 12.5, color: '#8b5cf6' }
          ],
          _createdAt: Date.now()
        });

        // Seed FY26 Q1 (Jan-Mar 2026) — actual payment received 2026-05-22
        S.bonusQuarters.push(
          { id: uid(), planId: fy26id, quarter: 'Q1', status: 'paid',
            grossEUR: 17816, fxRate: 52.90, grossTRY: 942397, netTRY: 605042.12, netBrutRatio: 0.642,
            earnedDate: '2026-03-31', paidDate: '2026-05-22', note: 'FY26 Q1 — 24.1% of target' }
        );
      }
    }
  },
  {
    v: 20,
    name: 'AI categorization rules + settings',
    run: () => {
      if (!S.aiRules) S.aiRules = [];
      if (!S.settings.ai) S.settings.ai = {};
    }
  },
  {
    v: 21,
    name: 'AI portfolio insights cache',
    run: () => {
      if (!S.aiInsights) S.aiInsights = { items: [], generatedAt: null, model: '', portfolioHash: '' };
    }
  },
  {
    v: 22,
    name: 'AI trade journal entries on trades',
    run: () => {
      (S.trades || []).forEach(t => { if (!t.journal) { t.journal = []; bumpMigrated(t); } });
    }
  },
  {
    v: 23,
    name: 'Push notification settings + notification history',
    run: () => {
      if (!Array.isArray(S.notifications)) S.notifications = [];
      if (!S.settings.pushNotif) {
        S.settings.pushNotif = {
          sound: false,
          quietStart: '23:00',
          quietEnd: '07:00',
          quietEnabled: false,
          types: { tradeAlert: true, signalAlert: true, tdMaturity: true, priceTarget: true, portfolio: true }
        };
      }
    }
  },
  // ── v24: AI Copilot data model ──
  {
    v: 24, name: 'aiCopilot',
    run() {
      if (!S.aiCopilot) {
        S.aiCopilot = { recommendations: [], scenarios: [], alerts: [], lastScan: null, scanHistory: [] };
      }
    }
  },
  // ── v25: Rebalancing engine ──
  {
    v: 25, name: 'rebalancing',
    run() {
      if (!S.rebalancing) {
        S.rebalancing = {
          // Target bands per asset type (retirement portfolio)
          targets: {
            'US ETF':       { lo: 30, hi: 36 },
            'US Stock':     { lo: 15, hi: 18 },
            'TR Fund':      { lo: 20, hi: 24 },
            'TR Stock':     { lo: 15, hi: 18 },
            'Commodity':    { lo: 10, hi: 12 },
            'Cash':         { lo: 10, hi: 12 }
          },
          // Top-level model — each non-retirement portfolio gets a unit band relative to retirement (100 units)
          // Key = portfolio name (lowercase), value = { lo, hi, emergencyLo, emergencyHi }
          // New portfolios added via UI will auto-register here with default 80-150 band
          topLevel: {
            crypto: { lo: 80, hi: 150, emergencyLo: 65, emergencyHi: 180 },
            sap:    { lo: 80, hi: 150, emergencyLo: 65, emergencyHi: 180 }
          },
          // Retirement portfolio identifier — matched by name containing this string
          retirementMatch: 'retirement',
          // Emergency thresholds
          emergencyDriftPP: 20,   // ±20pp absolute drift triggers emergency
          emergencyDropPct: 20,   // -20% total portfolio drop triggers emergency
          // Review schedule
          reviewMonths: [5, 11], // May + November (0-indexed: 4=May, 10=Nov → 1-indexed: 5, 11)
          reviewDayStart: 28,     // Review window: 28th to end of month
          // DCA lock
          dcaLocked: false,
          dcaLockedReason: null,
          // Active rebalance actions (DCA adjustments in progress)
          activeActions: [],
          // Review history
          reviews: [],
          // Last drift snapshot
          lastDrift: null,
          lastDriftTs: null
        };
      }
    }
  },
  // ─── v26: Consolidate duplicate crypto holdings (same ticker + portfolioId) ───
  {
    v: 26,
    name: 'Consolidate duplicate crypto holdings per ticker+portfolio',
    run() {
      const holdings = S.holdings || [];
      if (!holdings.length) return;

      // Only consolidate Crypto-type holdings — other portfolios may intentionally have
      // multiple entries per ticker (e.g. lot-based tracking in Retirement)
      const groups = {};
      holdings.forEach(h => {
        if (!h || h._del) return;
        const tp = (h.type || '').toLowerCase();
        if (tp !== 'crypto') return; // ONLY crypto holdings
        const key = (h.ticker || '') + '|' + (h.portfolioId || '');
        if (!groups[key]) groups[key] = [];
        groups[key].push(h);
      });

      let consolidated = 0;

      Object.values(groups).forEach(group => {
        if (group.length <= 1) return; // no duplicates

        // Pick the first as survivor, merge others into it
        const survivor = group[0];
        let totalQty = 0;
        let totalCost = 0; // in local currency

        group.forEach(h => {
          const qty = parseFloat(h.qty) || 0;
          const avg = parseFloat(h.avgCost) || 0;
          totalQty += qty;
          totalCost += qty * avg;
        });

        // Weighted average cost
        survivor.qty = +totalQty.toFixed(8); // high precision for crypto
        survivor.avgCost = totalQty > 0 ? +(totalCost / totalQty).toFixed(8) : 0;
        survivor._updatedAt = new Date().toISOString();

        // Merge lots if any exist
        const allLots = [];
        group.forEach(h => {
          if (h.lots && Array.isArray(h.lots)) {
            allLots.push(...h.lots);
          }
        });
        if (allLots.length) survivor.lots = allLots;

        // Soft-delete the duplicates (skip survivor)
        for (let i = 1; i < group.length; i++) {
          group[i]._del = true;
          group[i]._updatedAt = new Date().toISOString();
          group[i]._mergedInto = survivor.id;
          consolidated++;
        }
      });

      if (consolidated > 0) {
        console.log(`[v26] Consolidated ${consolidated} duplicate holdings`);
      }
    }
  },
  // ─── v27: Re-run crypto consolidation with explicit save() ───
  // v26 ran but changes weren't persisted to Supabase (no save() call).
  // This re-runs the same logic and forces an immediate save.
  {
    v: 27,
    name: 'Re-consolidate crypto holdings + force save',
    run() {
      const holdings = S.holdings || [];
      if (!holdings.length) return;

      const groups = {};
      holdings.forEach(h => {
        if (!h || h._del) return;
        const tp = (h.type || '').toLowerCase();
        if (tp !== 'crypto') return;
        const key = (h.ticker || '') + '|' + (h.portfolioId || '');
        if (!groups[key]) groups[key] = [];
        groups[key].push(h);
      });

      let consolidated = 0;

      Object.values(groups).forEach(group => {
        if (group.length <= 1) return;

        const survivor = group[0];
        let totalQty = 0;
        let totalCost = 0;

        group.forEach(h => {
          const qty = parseFloat(h.qty) || 0;
          const avg = parseFloat(h.avgCost) || 0;
          totalQty += qty;
          totalCost += qty * avg;
        });

        survivor.qty = +totalQty.toFixed(8);
        survivor.avgCost = totalQty > 0 ? +(totalCost / totalQty).toFixed(8) : 0;
        survivor._updatedAt = new Date().toISOString();

        const allLots = [];
        group.forEach(h => {
          if (h.lots && Array.isArray(h.lots)) allLots.push(...h.lots);
        });
        if (allLots.length) survivor.lots = allLots;

        for (let i = 1; i < group.length; i++) {
          group[i]._del = true;
          group[i]._updatedAt = new Date().toISOString();
          group[i]._mergedInto = survivor.id;
          consolidated++;
        }
      });

      if (consolidated > 0) {
        console.log(`[v27] Consolidated ${consolidated} duplicate crypto holdings — forcing save`);
        try { save(); } catch (_e) { console.warn('[v27] save() after consolidation failed:', _e); }
      }
    }
  },
  // ─── v28: Crypto consolidation — physically remove duplicates ───
  // v26/v27 set _del:true but persistence didn't flush to cloud.
  // v28: physically splice duplicates out of S.holdings array + force save.
  // persistence.js now also calls idbSave+cloudSave after any schema advance.
  {
    v: 28,
    name: 'Physically remove duplicate crypto holdings',
    run() {
      const holdings = S.holdings || [];
      if (!holdings.length) return;

      // Group crypto holdings by ticker+portfolioId
      const groups = {};
      holdings.forEach((h, idx) => {
        if (!h || h._del) return;
        const tp = (h.type || '').toLowerCase();
        if (tp !== 'crypto') return;
        const key = (h.ticker || '') + '|' + (h.portfolioId || '');
        if (!groups[key]) groups[key] = [];
        groups[key].push({ h, idx });
      });

      const toRemoveIdxs = new Set();
      let consolidated = 0;

      Object.entries(groups).forEach(([key, entries]) => {
        if (entries.length <= 1) return;
        console.log(`[v28] Merging ${entries.length} ${key.split('|')[0]} holdings`);

        const survivor = entries[0].h;
        let totalQty = 0;
        let totalCost = 0;

        entries.forEach(({ h }) => {
          const qty = parseFloat(h.qty) || 0;
          const avg = parseFloat(h.avgCost) || 0;
          totalQty += qty;
          totalCost += qty * avg;
          console.log(`  → ${h.ticker} id=${h.id} qty=${qty} avg=${avg} cost=${(qty * avg).toFixed(2)}`);
        });

        survivor.qty = +totalQty.toFixed(8);
        survivor.avgCost = totalQty > 0 ? +(totalCost / totalQty).toFixed(8) : 0;
        survivor._updatedAt = new Date().toISOString();
        console.log(`  ✓ Survivor: qty=${survivor.qty} avgCost=${survivor.avgCost}`);

        // Merge lots
        const allLots = [];
        entries.forEach(({ h }) => {
          if (h.lots && Array.isArray(h.lots)) allLots.push(...h.lots);
        });
        if (allLots.length) survivor.lots = allLots;

        // Mark duplicates for physical removal (skip index 0 = survivor)
        for (let i = 1; i < entries.length; i++) {
          toRemoveIdxs.add(entries[i].idx);
          consolidated++;
        }
      });

      if (consolidated > 0) {
        // Physically remove from array (iterate in reverse to preserve indices)
        const sortedIdxs = [...toRemoveIdxs].sort((a, b) => b - a);
        sortedIdxs.forEach(idx => {
          console.log(`[v28] Removing holdings[${idx}] (${S.holdings[idx]?.ticker})`);
          S.holdings.splice(idx, 1);
        });
        console.log(`[v28] Done — removed ${consolidated} duplicates, ${S.holdings.length} holdings remain`);
      } else {
        console.log('[v28] No duplicate crypto holdings found');
      }
    }
  },
  {
    v: 29,
    name: 'Mortgage def default paidBy = mixed (karma)',
    run: () => {
      (S.expenseDefs || []).forEach(d => {
        if (d.paidBy == null && d.type === 'Loan' && /mortgage/i.test(d.name || '')) {
          d.paidBy = 'mixed';
          bumpMigrated(d);
        }
      });
    }
  },
  {
    v: 30,
    name: "Restore dividends.kind='match' stripped by backup import",
    // `kind` was missing from _IMPORT_KEYS until 2026-08-02, so any dividend that went
    // through an import lost it. Live effect: the 2026-06-30 SAP row (€414.75) was the one
    // casualty — eleven identical monthly employer contributions, ten flagged 'match' and
    // excluded from passive income, one silently counted as investment income.
    //
    // The rule is deliberately narrow. Restore only when every one of these holds:
    //   - no kind at all (never overwrite an explicit value)
    //   - drip and zero withholding — the shape of a plan contribution, not a real dividend
    //   - the SAME ticker already carries at least one kind:'match' row, so we are
    //     completing a known series rather than inventing a category
    //   - the ticker's holding has an 'investment' lot, i.e. it is an employer plan holding
    // Anything less specific risks relabelling a genuine dividend as free shares, which
    // would understate income instead of overstating it. Both are wrong; neither is better.
    run: () => {
      const divs = S.dividends || [];
      const matchedTickers = new Set(divs.filter(d => d && d.kind === 'match').map(d => d.ticker));
      const planTickers = new Set(
        (S.holdings || [])
          .filter(h => (h.lots || []).some(l => l && l.type === 'investment'))
          .map(h => h.ticker)
      );
      let fixed = 0;
      divs.forEach(d => {
        if (!d || d.kind) return;
        if (d.drip !== true) return;
        if ((d.whtPct || 0) !== 0) return;
        if (!matchedTickers.has(d.ticker) || !planTickers.has(d.ticker)) return;
        d.kind = 'match';
        d._updatedAt = Date.now();
        fixed++;
      });
      if (fixed > 0) console.warn(`[v30] Restored kind='match' on ${fixed} dividend row(s)`);
    }
  },
  {
    v: 31,
    name: 'Recompute avgCost excluding DRIP lots',
    // saveAddLot and saveDividend both averaged over EVERY lot, DRIP included, while the
    // cost-basis readers (math.js hTotUSD/hTotLocal) exclude DRIP because free shares are a
    // return, not capital. The two disagreed on the same holding. Live: SAP avgCost read
    // €180.73 against a true €127.18, overstating that position's cost basis by €4,410
    // (~$5,074) and hiding an equal amount of gain. (audit 2026-08-02)
    run: () => {
      let fixed = 0;
      (S.holdings || []).forEach(h => {
        const lots = h.lots || [];
        if (!lots.some(l => l && l.type === 'drip')) return;
        const qty = lots.reduce((s, l) => s + (l.qty || 0), 0);
        if (!(qty > 0)) return;
        const cost = lots.reduce((s, l) => l && l.type === 'drip' ? s : s + (l.cost || (l.qty || 0) * (l.price || 0)), 0);
        const next = cost / qty;
        if (Math.abs((h.avgCost || 0) - next) > 1e-9) {
          h.avgCost = next;
          h._updatedAt = Date.now();
          fixed++;
        }
      });
      if (fixed > 0) console.warn(`[v31] Recomputed avgCost on ${fixed} holding(s) with DRIP lots`);
    }
  },
  {
    v: 32,
    name: 'Seed the declared opening month (2026-01)',
    // 31 holdings were entered as a single averaged lump because the purchases predate the
    // app and cannot be reconstructed. That is the edge of the record, not missing data —
    // but without a date their currency effect is uncomputable, and that unattributable
    // amount is what keeps the capital reconciliation from closing.
    //
    // The owner declared 2026-01 on 2026-08-02: it is the earliest month monthlyRates
    // carries (USD/TRY 42.94), and Jan–Mar are `legacy` with no purchase entries, which is
    // what an opening balance entered in that window looks like.
    //
    // Seeded, not inferred — and only when unset, so his own value always wins. Everything
    // computed from it is labelled `declared`, because a date the owner states and a date
    // the app recorded are different kinds of fact and must never render the same.
    run: () => {
      if (!S.settings) S.settings = {};
      if (!S.settings.basisMonth) S.settings.basisMonth = '2026-01';
    }
  },
  {
    v: 33,
    name: 'Owner-supplied FX rates for 2025-10',
    // Yahoo's monthly series has no October 2025 bucket for either TRY pair — a real hole
    // upstream, not a fetch failure, and fx-history correctly left the month uncomputable
    // rather than interpolating one. Without it the SAP plan's October lot (EUR 939.17 +
    // EUR 395.67) could not be priced at its own rate.
    //
    // The owner supplied both closes on 2026-08-02: USD/TRY 41.9677, EUR/TRY 48.5493.
    // Recorded here with provenance rather than fetched, because there is nowhere to fetch
    // them from — and written only when absent, so a later backfill or a hand edit wins.
    run: () => {
      if (!S.monthlyRates) S.monthlyRates = {};
      const m = S.monthlyRates['2025-10'] || (S.monthlyRates['2025-10'] = { status: 'legacy', entries: {} });
      if (!(m.usdtry > 0)) m.usdtry = 41.9677;
      if (!(m.eurtry > 0)) m.eurtry = 48.5493;
    }
  },
  {
    v: 34,
    name: 'Owner-reconstructed opening dates for the ten largest pre-app positions',
    // These positions were entered into SPEKTRX as one averaged lump because the purchases
    // predate the app. On 2026-08-03 the owner reconstructed them from his own records:
    //
    //   BTC   2026-01-05   0.059751 @ $92,919.37      ISGYO 2026-01-11    702 @ TRY 21.56
    //   ETH   2026-01-04   1.568852 @ $3,123.49       TUPRS 2026-01-15     96 @ TRY 206.76
    //   ASELS 2026-01-05        134 @ TRY 231.21      XRP   2026-01-19  237.651798 @ $1.94
    //   XAU   2026-01-05       4.61 @ TRY 6,050.00    QQQ   2026-01-21   0.7151 @ $608.20
    //   VOO   2026-01-02       0.81 @ $625.00         TEJ   2026-02-06  9,579 @ TRY 1.88
    //
    // Nine of ten fell in January, which is what the portfolio-wide declaration already
    // assumed — but TEJ was February, and a month-wide default would have priced it at
    // January's rate. That single position is why the per-holding override exists.
    //
    // Only the DATE is written. His quantities and unit prices differ from the app's stored
    // averages by $25.96 in total, all of it in the three crypto positions, and correcting a
    // cost basis is a separate decision with its own consequences — not something to slip
    // into a migration about dates. Written only when absent, so a later edit wins.
    run: () => {
      const DATES = {
        BTC: '2026-01-05', ETH: '2026-01-04', ASELS: '2026-01-05', XAU: '2026-01-05',
        VOO: '2026-01-02', XRP: '2026-01-19', QQQ: '2026-01-21', TUPRS: '2026-01-15',
        TEJ: '2026-02-06', ISGYO: '2026-01-11',
      };
      let n = 0;
      (S.holdings || []).forEach(h => {
        if (!h || h.basisDate || !DATES[h.ticker]) return;
        h.basisDate = DATES[h.ticker];
        h._updatedAt = Date.now();
        n++;
      });
      if (n > 0) console.warn(`[v34] Set opening date on ${n} holding(s)`);
    }
  },
  {
    v: 35,
    name: 'TEJ, AFA and YKT: the amount paid, not the number of digits typed',
    // Three funds recorded a purchase of 1-4 UNITS every month - TRY 2 to 5 - while the plan
    // called for TRY 3,200 to 5,450 and the bank actually debited it. Four months, three
    // funds, twelve entries, every one wrong the same way: a quantity was typed where the
    // real purchase was a lump sum. The positions read 9,586 / 12,308 / 14,061 units in the
    // app against 17,748 / 29,512 / 31,107 on the Garanti statement.
    //
    // The owner supplied the bank's TRY figures on 2026-08-03. They are not approximately the
    // plan: sixteen of the eighteen amounts he listed (Feb through Jul) land within TRY 9 of
    // budget x allocation, and the June/July figures reproduce the approved 1.216x TR Fund
    // boost exactly. They agree with a model they were never fitted to, which is what makes
    // them usable as a correction rather than a guess. AFA's July TRY 5,150 is TRY 295 under
    // that model and is deliberate - the transfer went short, owner-confirmed.
    //
    //   fund   Apr     May     Jun     Jul
    //   TEJ    4,274   4,320   5,358   5,450
    //   AFA    4,274   4,328   5,358   5,150
    //   YKT    3,206   3,246   4,019   4,084
    //
    // COST IS THE FACT, QUANTITY IS DERIVED. The statement says what was paid; nobody has the
    // execution NAV. The price already on each entry is the best available and is left
    // untouched, so quantity = paid / stored price. Afterwards the positions still differ
    // from Garanti by +2.1% / +0.7% / -1.0% on quantity and under 1% on cost, and the sign
    // differs per fund - the signature of NAV-date slippage, not of a systematic error. The
    // owner chose (2026-08-03) to leave that residue in the undated pre-April lump rather
    // than force a match: these four months are now measured facts and the lump is an
    // estimate, and uncertainty belongs on the estimate.
    //
    // GUARDED ON THE DEFECT, NOT ON THE VERSION. An entry is rewritten only when what it
    // records is less than a tenth of what was paid - the shape of the bug. A month already
    // correct, hand-fixed, or re-run is left exactly as it stands.
    run: () => {
      const fixed = _repriceFromAmountPaid({
        TEJ: { '2026-04': 4274, '2026-05': 4320, '2026-06': 5358, '2026-07': 5450 },
        AFA: { '2026-04': 4274, '2026-05': 4328, '2026-06': 5358, '2026-07': 5150 },
        YKT: { '2026-04': 3206, '2026-05': 3246, '2026-06': 4019, '2026-07': 4084 },
      });
      if (fixed > 0) console.warn(`[v35] Rewrote ${fixed} monthly entr(ies) from the bank's TRY amounts`);
    }
  },
  {
    v: 36,
    name: 'April USD and EUR: divided by the rate one extra time',
    // April's cash purchases recorded 2.6948 USD and 2.0257 EUR. The plan called for 5% of a
    // TRY 106,872 budget on each — TRY 5,343.60, which at 44.53 is 120 USD and at 51.3610 is
    // 104.04 EUR. Both stored numbers are those correct figures divided by the rate a SECOND
    // time: 120 / 44.53 = 2.6948 exactly, 104.04 / 51.3610 = 2.0257 exactly. May, June and
    // July are all recorded correctly, so this is one month's slip, not a broken code path.
    //
    // Confirmed twice by the owner on 2026-08-03: that he did buy 120 USD and ~104 EUR on
    // 1 April, and that his real Garanti balances are 876.81 USD and 839.15 EUR — which is
    // what these positions become here, and is the check that matters. A purchase he
    // remembers and a balance that agrees with it are two different facts; the correction
    // needed both.
    //
    // Same repair as v35 and the same guard, so a restore or a merge cannot double it.
    run: () => {
      // Both rows take the same TRY amount because both are 5% of the same April budget.
      // Expressed as TRY rather than as units so the arithmetic uses the rate already stored
      // on each entry — the EUR figure is not a round number and must not be typed as one.
      const APRIL_LEG = 5343.60;
      const fixed = _repriceFromAmountPaid({
        USD: { '2026-04': APRIL_LEG },
        EUR: { '2026-04': APRIL_LEG },
      });
      if (fixed > 0) console.warn(`[v36] Rewrote ${fixed} April cash entr(ies) — double FX division`);
    }
  },
  {
    v: 37,
    name: 'TEJ: 7,539 units held at a second bank the app never knew about',
    // The owner found these on 2026-08-03 while reconciling against his Garanti statement:
    // 7,539 units at an average of TRY 1.41, bought 2025-06-11 at AKBANK. The app's TEJ row
    // is a Garanti row and always was, so this position was invisible — not mis-recorded,
    // absent. TRY 10,630 of cost basis.
    //
    // RECORDED AS A DATED LOT, not folded into the opening lump. The lump carries
    // basisDate 2026-02-06 (the owner's own reconstruction of the 9,579 Garanti units), and
    // June 2025 is eight months earlier at a materially different USD/TRY. Blending the two
    // would make one declared date stand for two purchases it cannot both describe. A lot
    // keeps its own date; fx-effect now counts lots, monthly entries and the lump as three
    // separate contributions rather than letting the first silence the other two.
    //
    // `broker` becomes 'Garanti + Akbank'. Without it the next reconciliation reads 25,666
    // in the app against 17,748 on the Garanti screen and re-opens an investigation that is
    // already finished.
    //
    // NOTE: 2025-06 needs a USD/TRY rate before this lot can be priced. Press Fetch rates
    // after deploying, or the position is dated and still uncomputable.
    run: () => {
      const h = (S.holdings || []).find(x => x && x.ticker === 'TEJ');
      if (!h) return;
      const LOT = { date: '2025-06-11', qty: 7539, price: 1.41, cost: 10629.99, type: 'investment', note: 'Akbank' };
      if (!Array.isArray(h.lots)) h.lots = [];
      // Guarded on the lot itself, so a restore or a cross-device merge cannot add it twice.
      if (h.lots.some(l => l && l.date === LOT.date && Math.abs((l.qty || 0) - LOT.qty) < 0.5)) return;
      const cost = (h.qty || 0) * (h.avgCost || 0) + LOT.cost;
      h.lots.push(LOT);
      h.qty = +((h.qty || 0) + LOT.qty).toFixed(4);
      h.avgCost = h.qty > 0 ? cost / h.qty : 0;
      h.broker = 'Garanti + Akbank';
      h._updatedAt = Date.now();
      console.warn(`[v37] TEJ +${LOT.qty} units (Akbank, 2025-06-11) — position now ${h.qty}`);
    }
  },
  {
    v: 38,
    name: 'SMH: a July purchase that never happened',
    // The app held two SMH entries at the June/July turn — 0.1570 @ $611.61 for June and
    // 0.1519 @ $631.98 for July. The IBKR trade log for 2026-06-25..07-05 has exactly one
    // SMH fill: 2026-06-29, gross $95.96 at $611.59, which is 0.156901 units. That is the
    // June entry to within $0.06. Nothing corresponds to the July one.
    //
    // So July's $96 was recorded as deployed and never left the account. Removing the entry
    // also removes it from the monthly plan-vs-actual, where SMH read as 100% of plan for a
    // month in which nothing was bought — the more damaging half of the error, because a
    // missing purchase that reports as made is invisible by construction.
    //
    // Guarded on the exact stored quantity: if it has already been removed or corrected by
    // hand, this does nothing.
    run: () => {
      const m = (S.monthlyRates || {})['2026-07'];
      const e = m && m.entries && m.entries.SMH;
      if (!e || Math.abs((e.qty || 0) - 0.1519) > 1e-6) return;
      const cost = (e.qty || 0) * (e.price || 0);
      delete m.entries.SMH;
      const h = (S.holdings || []).find(x => x && x.ticker === 'SMH');
      if (h) {
        const newCost = (h.qty || 0) * (h.avgCost || 0) - cost;
        h.qty = +((h.qty || 0) - e.qty).toFixed(4);
        h.avgCost = h.qty > 0 ? newCost / h.qty : 0;
        h._updatedAt = Date.now();
      }
      console.warn(`[v38] SMH -${e.qty} units — July entry had no IBKR fill behind it`);
    }
  },
  {
    v: 39,
    name: 'The US sleeve, rebuilt from the IBKR trade log',
    // Until now the thirteen IBKR positions were recorded from the PLAN: budget x allocation,
    // entered by hand at month turn. The owner supplied the actual activity statement for
    // 2026-01-05..07-31 on 2026-08-03, and it disagrees in three separate ways.
    //
    // 1. COPX AND CLOU HAD NO PRE-APRIL COST AT ALL. Their entire recorded cost equalled the
    //    four April-July entries to the cent — $300.70 and $150.35 — against IBKR's $521.96
    //    and $265.57. Three fills each (2026-01-16, 02-18, 03-24) were simply never entered.
    //    The other eleven positions' opening lumps match IBKR to the cent INCLUDING
    //    commission, so this was two positions skipped, not a systematic failure.
    //    Consequence while it stood: the app's two best performers were artifacts — CLOU
    //    read +105.59% against a real +18.4%, COPX +70% against a real -3.9%.
    //
    // 2. COMMISSION WAS NEVER RECORDED. Every fill carries exactly 1.0%. The plan entries
    //    stored the gross price, so all thirteen positions understated cost by $2-5 each,
    //    $39 in total, growing every month. Cost is the denominator of XIRR and of unrealized
    //    P&L, so every return the app printed was high by construction. Each entry's price is
    //    now basis / quantity — what the position actually cost per unit, not what the screen
    //    quoted.
    //
    // 3. THE PLAN QUANTITIES WERE NEVER THE FILLED QUANTITIES. Close, never equal.
    //
    // WHICH FILL BELONGS TO WHICH MONTH. The buy ritual runs at month turn, so a fill dated
    // 2026-06-29 belongs to JULY's allocation and one dated 2026-06-18 to June's. That
    // mapping is not inferred here — it is what the app's own entry dates already say
    // (July's batch is stamped 2026-06-30). AVGO is the one case with two fills on the same
    // day; they are split by matching each to the price the app already recorded, 368.53 to
    // June and 373.82 to July.
    //
    // ZAP ($80.80, 2026-07) is in the same statement and is deliberately NOT here: the owner
    // ruled it out of scope on 2026-08-03. Net worth is $80 light on purpose.
    //
    // GUARDED ON THE HOLDING TOTAL, not the schema number: if a position already carries the
    // broker's cost to within a cent, nothing is touched.
    run: () => {
      // ticker -> plan month -> [filled quantity, net cost per unit incl. commission]
      const FILLS = {
        AVGO: { '2026-01': [0.143, 338.8045], '2026-02': [0.1458, 332.407892], '2026-03': [0.1492, 324.745165], '2026-04': [0.1204, 402.3436], '2026-05': [0.1117, 433.694003], '2026-06': [0.3011, 368.531159], '2026-07': [0.2996, 373.817787] },
        CLOU: { '2026-01': [1.7, 21.851638], '2026-02': [1.8816, 19.323059], '2026-03': [1.8068, 20.123773], '2026-04': [1.791, 20.301], '2026-05': [1.6395, 22.281396], '2026-06': [1.9595, 21.647434], '2026-07': [1.7043, 23.704202] },
        COPX: { '2026-01': [0.9, 81.7191], '2026-02': [0.8339, 87.2034], '2026-03': [1.0117, 71.875113], '2026-04': [0.8384, 86.7287], '2026-05': [0.8677, 83.804762], '2026-06': [0.9628, 88.113167], '2026-07': [0.9467, 76.810503] },
        CVX: { '2026-01': [0.3, 166.28135], '2026-02': [0.2632, 184.1836], '2026-04': [0.2617, 185.2441], '2026-05': [0.2581, 187.829676], '2026-06': [0.6329, 176.948031], '2026-07': [0.6494, 172.450219] },
        DBA: { '2026-01': [2.3, 25.856], '2026-02': [2.3136, 26.192557], '2026-03': [2.2403, 27.049419], '2026-04': [2.2184, 27.316907], '2026-05': [2.1352, 28.381003], '2026-06': [2.6227, 26.956903], '2026-07': [2.248, 26.956903] },
        ENB: { '2026-01': [0.7596, 47.863496], '2026-03': [0.6654, 54.641], '2026-04': [0.6876, 52.8735], '2026-05': [0.6572, 55.317703], '2026-06': [1.5311, 54.748752], '2026-07': [1.4853, 56.438803] },
        MSFT: { '2026-01': [0.0805, 451.653947], '2026-02': [0.0913, 398.120815], '2026-03': [0.1257, 385.627216], '2026-04': [0.0858, 423.493], '2026-05': [0.0885, 410.484203], '2026-06': [0.219, 382.698919], '2026-07': [0.2207, 379.780203] },
        NVDA: { '2026-01': [0.2541, 190.6577], '2026-02': [0.2527, 191.799], '2026-03': [0.2741, 176.830785], '2026-04': [0.2377, 203.919], '2026-05': [0.2403, 201.686903], '2026-06': [0.5312, 208.932533], '2026-07': [0.5693, 194.955844] },
        QQQ: { '2026-01': [0.2317, 625.795926], '2026-02': [0.2374, 610.6756], '2026-03': [0.246, 589.238307], '2026-04': [0.2222, 652.47045], '2026-05': [0.2095, 692.123273], '2026-06': [0.2315, 738.569657], '2026-07': [0.2017, 718.747861] },
        SMH: { '2026-01': [0.2351, 412.3628], '2026-02': [0.2326, 416.7159], '2026-03': [0.2468, 392.809122], '2026-04': [0.206, 470.6297], '2026-05': [0.1784, 543.329503], '2026-07': [0.1569, 617.705903] },
        VOO: { '2026-01': [0.2649, 637.885009], '2026-02': [0.2655, 636.336478], '2026-03': [0.2796, 604.416466], '2026-04': [0.2573, 656.736514], '2026-05': [0.2506, 674.340426], '2026-06': [0.2878, 691.264638], '2026-07': [0.2483, 680.617325] },
        XLE: { '2026-01': [1.4802, 49.126265], '2026-03': [2.0, 60.3], '2026-04': [1.3077, 55.608223], '2026-05': [1.2531, 58.030573], '2026-06': [1.5514, 54.685379], '2026-07': [1.3345, 54.490006] },
        XLV: { '2026-01': [0.45, 158.1963], '2026-02': [0.458, 158.759901], '2026-03': [0.4997, 145.508721], '2026-04': [0.4883, 148.9144], '2026-05': [0.495, 146.894201], '2026-06': [0.557, 152.308008], '2026-07': [0.4487, 162.064608] },
      };
      // ticker -> [total quantity, total net cost] straight off the statement
      const TOTAL = {
        AVGO: [1.2708, 465.21],
        CLOU: [12.4827, 265.57],
        COPX: [6.3612, 521.96],
        CVX: [2.3653, 419.3],
        DBA: [16.0782, 433.16],
        ENB: [5.7862, 313.08],
        MSFT: [0.9115, 361.47],
        NVDA: [2.3594, 464.29],
        QQQ: [1.58, 1040.85],
        SMH: [1.2558, 581.62],
        VOO: [1.854, 1212.83],
        XLE: [8.9269, 496.31],
        XLV: [3.3967, 519.59],
      };
      let touched = 0;
      Object.keys(TOTAL).forEach(tk => {
        const h = (S.holdings || []).find(x => x && x.ticker === tk);
        if (!h) return;
        const [q, cost] = TOTAL[tk];
        if (Math.abs((h.qty || 0) * (h.avgCost || 0) - cost) < 0.01 && Math.abs((h.qty || 0) - q) < 1e-6) return;
        Object.entries(FILLS[tk] || {}).forEach(([m, [fq, fp]]) => {
          const row = (S.monthlyRates || {})[m];
          if (!row) return;                       // never invent a month
          if (!row.entries) row.entries = {};
          row.entries[tk] = { ...(row.entries[tk] || {}), qty: fq, price: fp };
        });
        h.qty = q;
        h.avgCost = cost / q;
        h._updatedAt = Date.now();
        touched++;
      });
      if (touched > 0) console.warn(`[v39] ${touched} IBKR position(s) rebuilt from the trade log`);
    }
  },
  {
    v: 40,
    name: 'Five positions that took their correction twice',
    // WHAT HAPPENED. v35 and v36 both ADDED a delta to a holding and both guarded that add
    // by inspecting the monthly ENTRIES — a different object from the one they were
    // changing. Each ran a second time against a state whose entries had reverted while the
    // holdings had not (the cloud-behind-local drift this codebase already knows about), the
    // entry guard passed, and the holding took the same delta again. Measured live on
    // 2026-08-03, every excess exactly equal to the original correction:
    //
    //   TEJ  34,206.4852 / TRY 67,426.55   should be 25,665.7426 / TRY 48,040.51
    //   AFA  47,148.8970 / TRY 50,496.57   should be 29,728.4485 / TRY 31,403.00
    //   YKT  47,546.3334 / TRY 41,740.14   should be 30,803.6667 / TRY 27,197.30
    //   USD     994.1104 / TRY 44,777.37   should be    876.8052 / TRY 39,553.78
    //   EUR     941.1686 / TRY 48,901.99   should be    839.1543 / TRY 43,662.31
    //
    // TRY 63,485 of assets that were never bought. The entries themselves were fine — they
    // are written absolutely (qty = paid / price), so re-running only rewrote them to the
    // same value. Only the holding, the one thing updated by addition, drifted.
    //
    // THE RULE THIS ESTABLISHES. A migration that moves money must be ABSOLUTE, not
    // RELATIVE. "Add this delta" applied twice is silently wrong; "set this to this value"
    // applied twice is a no-op. A guard on anything other than the value being written is
    // not a guard, it is a correlation. v39 already followed this — it compares the holding
    // against the broker's own total, which is why COPX, CLOU, SMH and VOO came through the
    // same double-load untouched. v37 also survived, because its guard was on the lot it
    // adds rather than on something nearby.
    //
    // This migration is therefore written as targets. Run it once, twice or after a restore
    // from any point in this sequence and the result is identical.
    run: () => {
      const TARGET = {
        TEJ: [25665.7426, 48040.51],
        AFA: [29728.4485, 31403.00],
        YKT: [30803.6667, 27197.30],
        USD: [876.8052, 39553.78],
        EUR: [839.1543, 43662.31],
      };
      let fixed = 0;
      Object.keys(TARGET).forEach(tk => {
        const h = (S.holdings || []).find(x => x && x.ticker === tk);
        if (!h) return;
        const [q, cost] = TARGET[tk];
        if (Math.abs((h.qty || 0) - q) < 1e-4 && Math.abs((h.qty || 0) * (h.avgCost || 0) - cost) < 0.01) return;
        h.qty = q;
        h.avgCost = cost / q;
        h._updatedAt = Date.now();
        fixed++;
      });
      if (fixed > 0) console.warn(`[v40] ${fixed} holding(s) reset to their measured value`);
    }
  },
  {
    v: 41,
    name: 'Trades record where they are held',
    // Every trade in the book carried `broker: null`. feeModelFor resolves from the broker,
    // because that is the fact — the asset type only correlates — so with nothing recorded
    // the model was UNKNOWN and every exit booked gross proceeds. Realized P&L on those
    // trades read high by the exit commission, and the app could not say by how much.
    //
    // All 17 are TRY-denominated Turkish instruments in one account. Owner-confirmed
    // 2026-08-03: "hepsi garanti". Setting it makes the EXISTING models apply on both sides —
    // BIST equities 0.195%, gram gold 0.2% transaction tax — with no new fee logic.
    //
    // ABSOLUTE, per the rule v40 established. It writes a value, and it guards on the value
    // being written: a trade that already names a broker is left alone, so a later correction
    // by hand survives this running again. Nothing numeric is recomputed — realized records
    // keep their stored pnl/pnlUSD, because those were measured at the time and a broker
    // field added afterwards does not change what was actually received.
    run: () => {
      let set = 0;
      (S.trades || []).forEach(t => {
        if (!t || t._del || t.broker) return;
        t.broker = 'Garanti';
        t._updatedAt = Date.now();
        set++;
      });
      if (set > 0) console.warn(`[v41] broker set on ${set} trade(s) — exit commissions now modelled`);
    }
  },
  {
    v: 42,
    name: 'One date format for signals',
    // Signal imports stored `sourceDate` verbatim from the source page, so the book held two
    // formats: '2026-07-30' from the Mongo feed and 'May 2, 2026' from the HTML importer.
    // Sorted as strings, every textual month outranks every ISO date ('M' > '2') — which is
    // exactly how the integrity check reported a four-day-old feed as three months stale on
    // 2026-08-03. parseSignalHTML now normalises at the door; this converts what is already
    // stored.
    //
    // ABSOLUTE, per the rule v40 established: it writes a computed value, and ISO in gives ISO
    // out, so running it again is a no-op. A string with no explicit YEAR is left exactly as
    // it is — inferring one would be fabricating the fact the sort depends on.
    run: () => {
      let fixed = 0, skipped = 0;
      (S.signals || []).forEach(sig => {
        if (!sig || !sig.sourceDate) return;
        const raw = String(sig.sourceDate);
        if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return;
        const iso = _isoDateForMigration(raw);
        if (!iso) { skipped++; return; }
        sig.sourceDate = iso;
        sig._updatedAt = Date.now();
        fixed++;
      });
      if (fixed || skipped) {
        console.warn(`[v42] sourceDate normalised on ${fixed} signal(s)`
          + (skipped ? `; ${skipped} left as-is (no explicit year — not guessed)` : ''));
      }
    }
  },
  {
    v: 43,
    name: 'The three trades that say Akbank on them',
    // v41 wrote broker:'Garanti' on all 17 trades on the strength of the owner's recollection
    // ("hepsi garanti"). Three of them carry a note in their own record that says otherwise:
    // OYAKC 'Akbank', MIATK 'Akbank', ASELS 'Akbank-2026-05-22'. I had the records and did not
    // read them, which is the same mistake in the other direction — a stated fact and a
    // recorded fact are not interchangeable, and when they disagree the record wins.
    //
    // Nothing numeric moves. Owner-confirmed 2026-08-04: BIST commission is identical across
    // Turkish banks ("bist tüm tr bankaları için aynı"), so feeModelFor resolves to the same
    // 0.195% either way. This corrects a label, so that the next time these three are read the
    // note and the field agree.
    //
    // ABSOLUTE: it derives the value from the note ON the trade, not from a hardcoded id list,
    // and only overwrites the exact wrong value v41 wrote. A trade already reading 'Akbank',
    // or naming some third broker, is left alone.
    run: () => {
      let fixed = 0;
      (S.trades || []).forEach(t => {
        if (!t || t._del) return;
        const note = String(t.notes || t.note || '');
        if (!/^akbank\b/i.test(note.trim())) return;
        if (t.broker !== 'Garanti') return;
        t.broker = 'Akbank';
        t._updatedAt = Date.now();
        fixed++;
      });
      if (fixed > 0) console.warn(`[v43] broker corrected to Akbank on ${fixed} trade(s) — their own note said so`);
    }
  },
  {
    v: 50,
    name: 'The dividends whose cash rows were never linked to them',
    // `deleteDividend` removes a dividend's cash rows by the precise `_divId` link, and falls
    // back to a NOTE-SUBSTRING match for records written before that link existed. Measured on
    // the live book 2026-08-14: **all eleven dividend records are legacy and not one cash row
    // carries a `_divId`**, so every delete takes the fallback — and `note.includes('dividend:
    // sap')` matches all eleven credits and all eleven reinvests. Deleting ONE dividend would
    // have destroyed the cash audit trail of the other ten. That is D-7, recorded as fixed,
    // and the fix only ever protected records that did not exist yet.
    //
    // `dv.lotId` is absent for the same reason, so the DRIP lot is not removed either: the
    // shares stay while their dividend record goes.
    //
    // The back-link is deterministic here and this migration refuses to guess anywhere it is
    // not: ticker + date + amount identifies exactly one credit row, one reinvest row and one
    // drip lot for each of the eleven. More than one candidate, or none, and the record is left
    // alone — an unlinked record still works through the (now narrowed) fallback, and a wrong
    // link would delete someone else's row.
    run: () => {
      let rows = 0, lots = 0, skipped = 0;
      const near = (a2, b2) => Math.abs(Math.abs(a2) - Math.abs(b2)) < 0.01;
      const day = v => String(v || '').slice(0, 10);
      (S.dividends || []).forEach(dv => {
        if (!dv || !dv.id || !dv.ticker || !dv.date) return;
        const tk = String(dv.ticker).toLowerCase();
        const net = Number(dv.net) || 0;

        // ── the cash rows ──
        ['dividend: ' + tk, 'drip reinvest: ' + tk].forEach(prefix => {
          const cand = (S.cashTxs || []).filter(tx => tx && !tx._divId
            && String(tx.note || '').toLowerCase().startsWith(prefix)
            && day(tx.date) === day(dv.date) && near(tx.amount, net));
          if (cand.length === 1) { cand[0]._divId = dv.id; bumpMigrated(cand[0]); rows++; }
          else if (cand.length > 1) skipped++;
        });

        // ── the DRIP lot ──
        if (dv.drip && !dv.lotId) {
          const hs = (S.holdings || []).filter(h => h && h.ticker === dv.ticker);
          const h = hs.find(x => (x.currency || '') === (dv.currency || '')) || hs[0];
          const cand = h && (h.lots || []).filter(l => l && l.type === 'drip' && day(l.date) === day(dv.date));
          if (cand && cand.length === 1) { dv.lotId = cand[0].id; dv.lotHoldingId = h.id; bumpMigrated(dv); lots++; }
          else if (cand && cand.length > 1) skipped++;
        }
      });
      if (rows || lots || skipped) {
        console.warn(`[v50] linked ${rows} dividend cash row(s) and ${lots} DRIP lot(s); ${skipped} ambiguous, left unlinked`);
      }
    }
  },
  {
    v: 54,
    name: 'Undo the reconcile pump — the invented exit rows and the cash they carried',
    // ⚠⚠⚠ CLEANING UP AFTER v53 + A LATENT DEFECT IN `reconcile.js`, 2026-08-17.
    //
    // v53 back-filled `tradeId` onto five realized rows. `reconcile.js` section 2 matched a
    // realized row that HAS a `tradeId` by ID ALONE — a ternary, not an `||` — and no writer has
    // ever stamped `tradeId` on a `trade_exit` cashTx. So four of the five stopped matching their
    // REAL exit rows, `missing_exit_tx` fired `critical`, and `main.js` auto-applied the repair
    // on every load: `S.cash[cur] += proceeds`, a row pushed, `save()`, `cloudSave()`. The pushed
    // row carried no `tradeId` either, so it never satisfied its own detector.
    //
    //   MEASURED on the live book: EREGL 62,040.00 + AKBNK 38,925.00 + GWIND 33,400.00 +
    //   XAU 683,404.84 = ₺817,769.84 and 4 rows PER LOAD. Eight loads:
    //   TRY 396,469.31 -> 6,938,628.01 (+6,542,158.72), cashTxs 120 -> 152 (+32).
    //   USD and EUR untouched — none of the four rows is in those currencies.
    //
    // WHAT THIS REMOVES, and why it cannot take a real row with it: a `[Reconciled] <TICKER> exit`
    // row is deleted ONLY when a NON-reconciled `trade_exit` row for the same ticker exists within
    // 60 seconds of it — which is the reconciler's own matching rule, and it is true by
    // construction for every row the pump invented (the real row was always there; the detector
    // had just stopped being able to see it). A `[Reconciled]` row with no real twin is a genuine
    // repair somebody clicked, and it is LEFT ALONE.
    //
    // Rows are TOMBSTONED, not merely spliced: `mergeArrays` is a union, so a cloud copy holding
    // the pumped state would put all 32 straight back on the next merge (DATA-011's neighbour).
    //
    // The balance is then RE-DERIVED from the surviving rows rather than decremented, because
    // subtracting 32 floats lands two kuruş away from the truth and `cash_mismatch` would flag it.
    // "The transactions are the source of truth" is the reconciler's own rule.
    run: () => {
      const txs = S.cashTxs || [];
      const isPump = tx => tx && tx.type === 'trade_exit' && /^\[Reconciled\] /.test(String(tx.note || ''));
      const near = (a, b) => Math.abs(new Date(a).getTime() - new Date(b).getTime()) < 60000;
      const doomed = txs.filter(tx => {
        if (!isPump(tx)) return false;
        const m = /^\[Reconciled\] (\S+) exit/.exec(String(tx.note));
        if (!m) return false;
        const ticker = m[1];
        return txs.some(o => o && o !== tx && o.type === 'trade_exit' && !isPump(o)
          && String(o.note || '').includes(ticker) && near(o.date, tx.date));
      });
      if (!doomed.length) return;
      const curs = new Set(doomed.map(tx => tx.currency || 'USD'));
      const gone = new Set(doomed.map(tx => tx.id));
      doomed.forEach(tx => tombstoneId(tx.id));
      S.cashTxs = txs.filter(tx => !gone.has(tx.id));
      const sums = cashSumByCurrency(S.cashTxs);
      curs.forEach(c => { if (!S.cash) S.cash = {}; S.cash[c] = sums[c] || 0; });
      console.warn(`[v54] removed ${doomed.length} invented reconcile row(s); `
        + [...curs].map(c => `${c} cash re-derived to ${S.cash[c]}`).join(', '));
    }
  },
  {
    v: 53,
    name: 'The realized exits that never recorded which trade they closed',
    // The same shape as v50 and v52 — an inverse cannot know what to undo, and a reader cannot
    // know what to show, unless the forward path wrote the link down. `trades.js` has stamped
    // `tradeId` since the field existed; `closeDailyTd` and `confirmMaturity` never did, and rows
    // written before the field exists have none either.
    //
    // MEASURED on `backup/zagx-2026-08-16.json`: of 21 realized rows, **13 are portfolio holding
    // exits** (correctly linkless — there is no trade), **3 carry a tradeId**, and **5 belong to a
    // trade and do not say so**: EREGL 04-13, AKBNK 04-17, GWIND 04-30, TRYVD2 04-30, XAU 05-06.
    // Each resolves to exactly one closed trade of that ticker, on the same day.
    //
    // ⚠ WHY IT IS WORTH A MIGRATION RATHER THAN LEAVING THE FALLBACK TO COPE. `TRYVD2` has three
    // trade records — one closed, TWO OPEN. The moment a second one matures the book holds two
    // unlinked TRYVD2 rows and two closed TRYVD2 trades, and a ticker fallback matches every row
    // to every trade: each deposit's journal entry sums BOTH deposits' interest. The fallback is
    // not wrong today, it is unopposed.
    //
    // ⚠ AND IT IS SAFE AGAINST THE WORLD MOVING ON (rule #45, the reason v40 is dangerous). It
    // hard-codes no ticker, no date and no amount; it writes a LINK and never a quantity, so even
    // a wrong one cannot move money; it only ever touches a row that has no link, so a second run
    // is a no-op; and where the match is not unique it writes nothing and says so.
    run: () => {
      let linked = 0, ambiguous = 0;
      (S.realized || []).forEach(r => {
        if (!r || r.tradeId) return;
        if (realizedOrigin(r) === 'portfolio') return;
        const t = tradeForRealized(r, S.trades);
        if (t) { r.tradeId = t.id; bumpMigrated(r); linked++; }
        else if ((S.trades || []).some(x => x && x.ticker === r.ticker && x.status === 'Closed')) ambiguous++;
      });
      if (linked || ambiguous) {
        console.warn(`[v53] linked ${linked} realized exit(s) to their trade; ${ambiguous} ambiguous, left unlinked`);
      }
    }
  },
  {
    v: 52,
    name: 'The reserve-fund rows that never recorded whether cash funded them',
    // MOVE-002, the last instance of the shape `saveAddLot` learned on 2026-08-07, migration v50
    // learned for dividends and v51 for liability payments: **an inverse cannot know what to undo
    // unless the forward path wrote it down.**
    //
    // `rfAction` asks "from app cash or external?", moves `S.cash[cur]`, writes the `rfTxs` row
    // and — only when funded from cash — an `rf_deposit` / `rf_withdraw` cashTx. The `rfTxs` row
    // recorded NONE of that: `{ id, date, type, amount, currency, note }`. So `rfDeleteTx`
    // reversed the reserve leg alone, and `rfEditTx` did not even do that much for cash.
    //
    // Measured on the live book 2026-08-14: **5 rfTxs, ZERO `rf_*` cashTxs.** Every one of the
    // five is provably external — four dated 2026-05-02 noted "Akbank", one ₺34,000 on 05-24.
    // So there is nothing to link here, and this migration is written for the rows that exist on
    // any other device and for the ones this book will grow. It links only what it can prove:
    // exactly one `rf_*` cashTx of the same day, direction and TRY-or-native amount. Anything
    // ambiguous is left alone and `rfDeleteTx` refuses rather than half-undoing it.
    run: () => {
      let linked = 0, skipped = 0;
      const day = v => String(v || '').slice(0, 10);
      const near = (a, b) => Math.abs(Math.abs(a) - Math.abs(b)) < 0.01;
      // No new field on the cash row: a cash leg already spoken for is tracked here, so two
      // reserve rows of the same day and amount cannot both claim it — across re-runs as well,
      // because the set is seeded from the links that already exist.
      const claimed = new Set((S.rfTxs || []).map(t => t && t._cashTxId).filter(Boolean));
      (S.rfTxs || []).forEach(tx => {
        if (!tx || !tx.id || tx.funded) return;
        const want = 'rf_' + tx.type;
        const cand = (S.cashTxs || []).filter(c => c && c.type === want && c.id
          && !claimed.has(c.id)
          && day(c.date) === day(tx.date)
          && near(c.amount, Number(tx.amount) || 0));
        if (cand.length === 1) {
          tx.funded = 'cash';
          tx._cashTxId = cand[0].id;
          bumpMigrated(tx);
          claimed.add(cand[0].id);
          linked++;
        } else if (cand.length > 1) skipped++;
      });
      if (linked || skipped) {
        console.warn(`[v52] linked ${linked} reserve-fund row(s) to their cash leg; ${skipped} ambiguous, left unlinked`);
      }
    }
  },
  {
    v: 51,
    name: 'The liability payments whose cash rows were never linked to them',
    // Second door of the MOVE-003 sweep, and it was worse than the first. `4e187ee` puts a badge
    // reading "Liabilities" on the two liability-payment rows — ₺777,875 and ₺683,404.84,
    // ₺1,461,279.84 together — telling him that is where they can be undone.
    //
    // Measured on the live book 2026-08-14: **there is no un-pay anywhere in the app.**
    // `payLiability` pushes onto `d.payments`, reduces `remainingAmount`, debits `S.cash.TRY` and
    // writes the cash row. Nothing removes a payment. The Liabilities table does not even RENDER
    // the payments, so there is no control to press. The only delete on that page,
    // `deleteLiability`, removes the record and every payment with it, touches NO cash and NO
    // ledger row — leaving both rows orphaned, and still badged "Liabilities" because
    // `cashTxOrigin` reads them by note prefix. Both liabilities are already `closed`.
    //
    // `deletePayment` is the door, and it needs to know WHICH row to reverse. The forward path
    // now stamps `_liabId` / `_paymentId`; this back-links the rows written before it, by the
    // same rule v50 used: name + day + TRY amount identifies exactly one row for each payment.
    // More than one candidate and the payment is left alone — `deletePayment` refuses rather
    // than reversing a row it cannot prove is the right one.
    run: () => {
      let rows = 0, skipped = 0;
      const near = (a2, b2) => Math.abs(Math.abs(a2) - Math.abs(b2)) < 0.01;
      const day = v => String(v || '').slice(0, 10);
      (S.liabilities || []).forEach(l => {
        if (!l || !l.id || !Array.isArray(l.payments)) return;
        const prefix = ('liability payment: ' + String(l.name || '')).toLowerCase();
        l.payments.forEach(p => {
          if (!p || !p.id) return;
          const cand = (S.cashTxs || []).filter(tx => tx && !tx._paymentId
            && String(tx.note || '').toLowerCase().startsWith(prefix)
            && day(tx.date) === day(p.date) && near(tx.amount, Number(p.tryEquiv) || 0));
          if (cand.length === 1) { cand[0]._liabId = l.id; cand[0]._paymentId = p.id; bumpMigrated(cand[0]); rows++; }
          else if (cand.length > 1) skipped++;
        });
      });
      if (rows || skipped) {
        console.warn(`[v51] linked ${rows} liability payment cash row(s); ${skipped} ambiguous, left unlinked`);
      }
    }
  },
  {
    v: 49,
    name: 'One field, two quantities — new money and redeployed capital',
    // `cashflow[k].portfolioComm` means NEW MONEY out of that month's income, and nothing
    // else. `commitment = portfolioComm − transferred` (cashflow.js) subtracts it from free
    // TRY as a claim on income — and the ledger has ALREADY debited any capital that came
    // from a sale. On 2026-08-08 a rebalance month's redeployed proceeds were typed into the
    // same box, so the same ₺113,836 was subtracted twice: free TRY read ₺134,439 against a
    // true ₺248,275 for five days, and that figure reaches the Money hero, the cash
    // projection, TRY IDLE SHARE, the liability payment preview and the LLM prompt.
    //
    // The field now has a sibling that carries the other quantity and enters no subtraction
    // anywhere. This is deliberately ABSOLUTE rather than a seed addition: every month that
    // already exists gets the key, so no consumer has to ask whether a month predates the
    // split, and `typeof mo.redeployed === 'number'` is a fact about every month object.
    //
    // It does NOT try to guess a split for a month that was plugged. It cannot: the plug and
    // the plan are the same number to anything reading the state. The one month this
    // happened to was corrected by hand on 2026-08-13 (₺227,668 → ₺113,832) and verified
    // against the live figures. `cashflow.commitmentPlan` (M3) is what catches the next one.
    run: () => {
      let n = 0;
      Object.values(S.cashflow || {}).forEach(mo => {
        if (!mo || typeof mo !== 'object') return;
        if (typeof mo.redeployed !== 'number' || !isFinite(mo.redeployed)) { mo.redeployed = 0; n++; }
      });
      if (n) console.warn(`[v49] redeployed:0 stamped on ${n} cash-flow month(s)`);
    }
  },
  {
    v: 48,
    name: 'The income credit that could not survive a reload',
    // 9eaa3f7 made post-anchor income rows `_audit: false` and credit S.cash.TRY, tracking the
    // applied figure in `_credited`. Two independent audits found the same blocker within the
    // hour: `backfillSalaryCashTxs` runs on EVERY app start, flips `_incomeId` rows back to
    // `_audit: true`, and then rewrites S.cash[cur] from the sum of non-audit rows. The credit
    // did not survive a reload, and the pair oscillated — one boot down a salary, the next save
    // back up — writing a plausible "Cash TRY drift" history line each time.
    //
    // The balance is derived again (projectedCashTRY). This clears the residue so a state that
    // ran the credited version lands where the derived model expects: income is audit-only.
    // The balance itself is left alone — backfillSalaryCashTxs recomputes it from the ledger on
    // the same boot, which is exactly the behaviour this migration is handing back to.
    run: () => {
      let n = 0;
      (S.cashTxs || []).forEach(tx => {
        if (!tx || !tx._incomeId) return;
        if (tx._credited != null) { delete tx._credited; bumpMigrated(tx); n++; }
        if (tx._audit !== true) { tx._audit = true; bumpMigrated(tx); n++; }
      });
      // The credited build ALSO stamped the cash-flow ITEM (`_deducted` / `_deductedFrom`) and
      // wrote an `Expense: …` withdraw row for every income-paid bill ticked while it was live.
      // The first pass cleared only the cashTx side, which left the item counted a second time
      // by the derived paidOut, and left the undo branch able to credit money back that a later
      // reconciliation had already absorbed. Only rows the credited build wrote are touched:
      // a genuine `paidBy:'savings'` deduction keeps its marker and its row.
      // ── DATA-003, and a second defect underneath it. Both closed 2026-08-14. ──
      //
      // 1. THE DETECTION WAS WRONG. The original condition was
      //    `paidBy !== 'savings' && (_deducted != null || _deductedFrom != null)` — but
      //    `cfToggle` writes exactly that on EVERY confirmed income-paid item:
      //
      //      it._deducted = it.paidBy === 'savings' && (…cash|reserve) ? it.amount : 0;
      //      it._deductedFrom = it._deducted ? it.paidBySource : null;
      //
      //    so `_deducted: 0` / `_deductedFrom: null` is ordinary operation, not residue.
      //    Measured on the live book at schema 52: **31 of 35 items match the old condition
      //    right now**, every one of them written by normal ticking. A restore from any backup
      //    older than v48 would have built 31 `Expense: …` notes and deleted every matching row.
      //
      //    The narrowed test is the combination normal operation CANNOT produce: a positive
      //    deduction on an item that was not paid from savings. `cfToggle` only ever sets
      //    `_deducted > 0` when `paidBy === 'savings'`, so this is the credited build's
      //    signature and nothing else's. It also makes the migration idempotent against the
      //    app's own behaviour, which the original was not.
      //
      //    (`_deducted: 0` is left in place. It contributes nothing to the derived paidOut —
      //    which is why clearing it was never the part that mattered.)
      //
      // 2. THE REMOVAL HAD NO TOMBSTONE. A bare `filter` on a MERGE_ARRAYS array is the one
      //    thing `softDelete`'s own doc-comment forbids: the next cloud merge resurrects the
      //    rows, `backfillSalaryCashTxs` rebuilds `S.cash.TRY` from the ledger including them,
      //    and v48 can never run again to re-clear them because the schema has moved past it.
      //    Permanent, and worth ₺30k–50k on a book that had them. Now tombstoned by id.
      const notes = new Set();
      Object.entries(S.cashflow || {}).forEach(([mk, mo]) => {
        ((mo && mo.items) || []).forEach(it => {
          if (!it || it.paidBy === 'savings') return;
          if (!((Number(it._deducted) || 0) > 0)) return;
          notes.add('Expense: ' + (it.name || '') + ' (' + mk + ')');
          delete it._deducted; delete it._deductedFrom; n++;
        });
      });
      if (notes.size) {
        const doomed = (S.cashTxs || []).filter(tx => tx && tx.type === 'withdraw' && tx.currency === 'TRY' && notes.has(String(tx.note || '')));
        doomed.forEach(tx => tombstoneId(tx.id));
        const gone = new Set(doomed.map(tx => tx.id));
        S.cashTxs = (S.cashTxs || []).filter(tx => !(tx && gone.has(tx.id)));
        n += doomed.length;
      }
      if (n) console.warn(`[v48] ${n} field(s)/row(s) from the credited build cleared`);
    }
  },
  {
    v: 47,
    name: 'The first reconciliation kept no total',
    // The Reconcile TRY that shipped in 726394f stored { banks, at }. 9eaa3f7 added `total`
    // and `lastDrift`, because the checks and the projection panel need to distinguish the
    // READING from the live balance. The owner had already reconciled under the first version,
    // so his record had no total and the panel rendered "Banks last read 08 Aug at —".
    //
    // The total is recoverable: it is the sum of the per-bank figures he entered. `lastDrift`
    // is NOT recoverable — nothing recorded what the balance was before the correction — so it
    // stays absent and cash.bankSplit reports "correction not recorded" rather than inventing
    // a zero, which would read as "the app was perfectly in line" when nobody knows.
    //
    // ABSOLUTE: derives from the banks on the record, fills only what is missing, idempotent.
    run: () => {
      const rec = S.cashRecon && S.cashRecon.TRY;
      if (!rec || !rec.banks || typeof rec.banks !== 'object') return;
      const names = Object.keys(rec.banks);
      if (!names.length) return;
      if (typeof rec.total === 'number' && isFinite(rec.total)) return;
      rec.total = names.reduce((a, k) => a + (Number(rec.banks[k]) || 0), 0);
      console.warn(`[v47] cashRecon.TRY.total backfilled from ${names.length} bank figure(s): ${rec.total}`);
    }
  },
  {
    v: 46,
    name: 'The TRY bank list reaches a state that already exists',
    // v45's sibling defect, shipped the same day and caught by the owner within the hour.
    // `settings.cashBanksTRY` was added to the SEED, and the SEED is what a NEW state starts
    // from — it is not applied to a state that already exists. So Reconcile TRY read an empty
    // list on the only state that matters and dead-ended on "No TRY banks configured".
    //
    // ABSOLUTE: seeds the list only when it is missing or empty, never overwrites one the
    // owner has edited, and does not care what the previous value was. Also creates the
    // cashRecon envelope so the integrity checks read UNKNOWN (never reconciled) rather than
    // tripping over a missing object.
    run: () => {
      if (!S.settings || typeof S.settings !== 'object') S.settings = {};
      if (!Array.isArray(S.settings.cashBanksTRY) || S.settings.cashBanksTRY.length === 0) {
        S.settings.cashBanksTRY = ['Garanti', 'Akbank'];
        console.warn('[v46] settings.cashBanksTRY seeded: Garanti, Akbank');
      }
      if (!S.cashRecon || typeof S.cashRecon !== 'object') S.cashRecon = {};
      if (!S.cashRecon.TRY || typeof S.cashRecon.TRY !== 'object') {
        S.cashRecon.TRY = { banks: {}, at: null };
      }
    }
  },
  {
    v: 45,
    name: 'One inflation rate, not two',
    // `S.trInflation` and `S.settings.trInflation` both existed. The Settings input wrote the
    // second; only the time-deposit real-return preview read the first. On 2026-08-08 they held
    // 0.42 and 0.321 — the same app quoted Turkish inflation as 42% and 32.1% on two screens,
    // and the 42% copy travelled into an AI insight as a stated fact about the deposit sleeve.
    // Actual July-2026 CPI was 33.5%.
    //
    // The reader is now pointed at settings (trades.js). This carries any legacy top-level value
    // across so a state that only ever had the old field does not silently fall back to a default,
    // then deletes it so a second copy cannot come back.
    run: () => {
      if (!S.settings || typeof S.settings !== 'object') S.settings = {};
      const legacy = S.trInflation;
      if (S.settings.trInflation == null && typeof legacy === 'number' && isFinite(legacy)) {
        S.settings.trInflation = legacy;
        console.warn(`[v45] trInflation hoisted from the legacy top-level field: ${legacy}`);
      }
      if ('trInflation' in S) delete S.trInflation;
    }
  },
  {
    v: 44,
    name: 'The four credit cards become definitions',
    // The owner's cash-flow workflow is: open a month, enter that month's income, and the
    // month fills itself in. That is exactly what happens — for the three things that have an
    // expenseDef. All three are Loans. There has never been a Card definition, so Axess, Pgs,
    // Bonus and Maximum were typed in by hand every month through the "+" button, and every
    // card row in the book carries defId:null to prove it: cfGenItems, the only generator,
    // always stamps a real id.
    //
    // That is why August ended up with four items where April–July had seven. The lost-edit
    // bug (cashflow.js, same day) is why two of them vanished; this is why they had to be
    // typed at all. Owner-approved 2026-08-04: define the four, amount unset so each month asks
    // for the amount rather than asserting one.
    //
    // Days are the most frequent value across Apr–Jul — Axess 1/4/1/2, Pgs 12/10/10/12,
    // Bonus 13/11/11/13, Maximum 15/15/15/16. A statement date moves; the day on the row is
    // editable per month, and the definition only supplies the starting position.
    //
    // ABSOLUTE. Guarded on the value being written: a definition whose name already exists is
    // not duplicated, so this is a no-op on the second run and on a device that already synced
    // it. Closed months are never touched — the owner declined the historical backfill, and a
    // migration that rewrites a closed month is a migration that restates settled money.
    //
    // The second half is what stops the fix from creating the mess it prevents: an OPEN month
    // already holding a hand-typed 'Axess' would get a second, generated Axess the next time
    // the modal opened. Adopting the matching row onto the new id makes it the same row.
    run: () => {
      const WANTED = [
        { name: 'Axess', day: 2 },
        { name: 'Pgs', day: 10 },
        { name: 'Bonus', day: 11 },
        { name: 'Maximum', day: 15 },
      ];
      if (!Array.isArray(S.expenseDefs)) S.expenseDefs = [];
      const norm = x => String(x || '').trim().toLowerCase();
      const existing = new Map(S.expenseDefs.map(d => [norm(d && d.name), d]));
      let made = 0;
      WANTED.forEach(w => {
        if (existing.has(norm(w.name))) return;
        const def = {
          id: uid(), name: w.name, day: w.day, type: 'Card', note: '',
          fixed: false, fixedAmount: null, active: true, paidBy: 'income',
          _updatedAt: Date.now(),
        };
        S.expenseDefs.push(def);
        existing.set(norm(w.name), def);
        made++;
      });
      // Adopt the hand-typed rows in months that are still open, so the generator recognises
      // them instead of adding a duplicate beside them.
      let adopted = 0;
      Object.keys(S.cashflow || {}).forEach(k => {
        const mo = S.cashflow[k];
        if (!mo || mo.closed || !Array.isArray(mo.items)) return;
        mo.items.forEach(it => {
          if (!it || it.defId) return;
          const def = existing.get(norm(it.name));
          if (!def || def.type !== 'Card') return;
          it.defId = def.id;
          adopted++;
        });
      });
      if (made || adopted) {
        console.warn(`[v44] ${made} card definition(s) created`
          + (adopted ? `; ${adopted} hand-typed row(s) in open months adopted onto them` : ''));
      }
    }
  },
  {
    v: 55,
    name: 'Portfolio sizing declared, and the SAP mandate corrected',
    // TWO THINGS, and the second is a live-data correction rather than a new field.
    //
    // (a) `portfolio.sizing` -- how a sleeve's target is arrived at. See core/portfolio-policy.js
    //     for why this is not `mandate` and why it has no default. Only the three sleeves whose
    //     discipline the owner has actually stated are set here. Anything else stays UNDECLARED
    //     on purpose, so `integrity` reports UNKNOWN and the bonus engine warns, instead of a
    //     silent 'weighted' deciding money.
    //
    // (b) SAP XETRA carried `mandate: 'active'`, which licenses the advisory to recommend exiting
    //     it. It is a standing monthly instruction whose entire purpose is savings discipline --
    //     the owner, 2026-09-02: it protects him from himself and he does not want to stop it.
    //     The `portfolio.mandate` integrity check passed green throughout, because it grades
    //     whether the field is DECLARED, not whether it is RIGHT. The ATH trigger replaces the
    //     exit licence: it ALERTS at EUR 272 on XETRA and never emits a sell directive.
    //
    // Currency is pinned on the trigger because SAP quotes in EUR on XETRA and in USD as an ADR;
    // `evaluateTrigger` refuses to compare across the two rather than firing ~16% early.
    //
    // Idempotent by construction: a sleeve that already declares a sizing is left alone, so the
    // owner's own later edit survives every subsequent load.
    // ⚠ ROUND 2 (2026-09-02): the first version matched by NAME REGEX. Demonstrated failures:
    //   a sleeve called "Sapphire Growth" captured SAP's mandate flip and its EUR-272 trigger
    //   while the real SAP XETRA kept `mandate:'active'`; a CLOSED "SAP XETRA (closed 2019)"
    //   row listed first captured them instead of the live one; and "Crypto Hedge (short)"
    //   was handed the halving accumulate clock. So the sleeve is identified by WHAT IT HOLDS,
    //   which is a fact, and the name is only a last resort behind an exact match.
    run: () => {
      const ports = (S.portfolios || []).filter(p => p && p.status !== 'closed');
      const holdings = (S.holdings || []).filter(h => h && !h._del);
      const openIds = new Set(ports.map(p => p.id));

      // ⚠ ROUND 3. Round 2 identified the sleeve by what it holds and that was worse, not
      //   better: `holdings.find(t => t.ticker === 'SAP')` takes ARRAY ORDER, so a legacy USD
      //   ADR row listed first pointed the mandate flip and the EUR trigger at Retirement
      //   while the real SAP XETRA silently kept its exit licence -- and the new
      //   `portfolio.accumulateTrigger` check then read "no accumulate sleeves", so the row
      //   built to catch that silence was itself silent. "Holds only Crypto" over-matched the
      //   same way, handing the halving clock to a hedge sleeve and to a kids' account.
      //
      //   So: the EXACT name is the primary key. Round 1's defect was the FUZZY /sap/i regex,
      //   never the exact string. Holdings are the fallback, and only when they are
      //   unambiguous -- exactly one candidate, in an open sleeve.
      // ⚠ ROUND 4. Three more ways to pick the wrong sleeve, all demonstrated:
      //   · an exact name is still ARRAY ORDER when two open sleeves share it (nothing enforces
      //     unique names), so an empty decoy captured the flip;
      //   · an exact name was trusted WITHOUT checking the sleeve holds SAP at all;
      //   · the holdings fallback had no retirement/default exclusion, so a five-share parking
      //     row inside Retirement armed the EUR-272 trigger there -- and because the loop gives
      //     Retirement `sizing:'weighted'`, `portfolio.accumulateTrigger` (which filters on
      //     sizing === 'accumulate') then reported "no accumulate sleeves" while the price sat
      //     10% above the level. The row built to catch the silence was silent again.
      //
      // So every route now demands the SAME evidence: exactly one open, non-default sleeve that
      // BOTH answers to the identity and holds the thing. Ambiguity declares nothing, which the
      // `portfolio.sizing` row then reports.
      const eligible = ports.filter(p => p.type !== 'retirement' && p.isDefault !== true);
      const heldIn = (pid, pred) => holdings.some(h => h.portfolioId === pid && pred(h));
      const uniqueOrNull = (arr) => (arr.length === 1 ? arr[0].id : null);

      // qty is deliberately NOT required: a sleeve that has just been sold down to zero is still
      // the SAP sleeve, and identity must not flicker with a position size.
      const isSapHolding = h => h.ticker === 'SAP' && (h.currency || '') === 'EUR';
      const sapNamed = eligible.filter(p => String(p.name || '') === 'SAP XETRA' && heldIn(p.id, isSapHolding));
      const sapHeld = eligible.filter(p => heldIn(p.id, isSapHolding));
      const sapPid = uniqueOrNull(sapNamed) || uniqueOrNull(sapHeld);

      const isCrypto = h => h.type === 'Crypto';
      const cryNamed = eligible.filter(p => String(p.name || '') === 'Crypto-Medium Term' && heldIn(p.id, isCrypto));
      const cryHeld = eligible.filter(p => heldIn(p.id, isCrypto));
      const cryPid = uniqueOrNull(cryNamed) || uniqueOrNull(cryHeld);
      const cryptoPids = new Set(cryPid ? [cryPid] : []);
      void openIds;

      const set = [];
      ports.forEach(p => {
        if (p.sizing) return;                             // never overwrite a declared value
        const name = String(p.name || '');
        let sizing = null;
        if (p.type === 'retirement' || p.isDefault === true) sizing = 'weighted';
        else if (sapPid && p.id === sapPid) sizing = 'accumulate';
        else if (cryptoPids.has(p.id)) sizing = 'cyclical';
        if (!sizing) return;                              // undeclared is a reportable state
        p.sizing = sizing;
        bumpMigrated(p);
        set.push(`${name || p.id}=${sizing}`);
      });

      let mandateFixed = null;
      const sap = sapPid ? ports.find(p => p.id === sapPid) : null;
      if (sap) {
        if (sap.mandate !== 'accumulate') {
          mandateFixed = `${sap.mandate || 'unset'} -> accumulate`;
          sap.mandate = 'accumulate';
          bumpMigrated(sap);
        }
        if (!sap.trigger) {
          sap.trigger = {
            type: 'price_level', ticker: 'SAP', level: 272, currency: 'EUR',
            direction: 'above', note: 'All-time high -- review the standing instruction',
          };
          bumpMigrated(sap);
        }
      }

      if (!sap) {
        console.warn('[v55] no SAP sleeve could be identified unambiguously — its mandate and'
          + ' price trigger were NOT set. `portfolio.sizing` will report it as undeclared.');
      }
      const undeclared = ports.filter(p => !p.sizing).map(p => p.name || p.id);
      if (set.length || mandateFixed || undeclared.length) {
        console.warn(`[v55] sizing set: ${set.join(', ') || 'none'}`
          + (mandateFixed ? `; SAP mandate ${mandateFixed}` : '')
          + (undeclared.length ? `; UNDECLARED and reported as unknown: ${undeclared.join(', ')}` : ''));
      }
    }
  },
  {
    v: 56,
    name: 'The SAP payroll lots move no cash, and July/August DRIP goes to the holding that paid it',
    // A ONE-OFF REPAIR OF TWO ERRORS FROM ONE SESSION, 2026-09-10. Both were measured on the
    // live book before a line of this was written, and `5bdf59d` closed the doors that let them
    // in. This closes the damage they already did.
    //
    // (a) THE PAYROLL LOTS DEBITED A BALANCE THAT WAS NEVER THERE. The owner entered the July
    //     and August SAP lots through `saveAddLot`, which debits `S.cash` and stamps
    //     `funded: 'cash'`. The SAP plan is a payroll deduction: employer to XETRA, never
    //     through an account this book holds. EUR went to -1,973.74 on two `portfolio_buy`
    //     rows of 986.87. His eleven earlier SAP investment lots carry no stamp and moved no
    //     cash, so the same monthly fact was recorded two ways depending on the door.
    //
    //     Total Wealth read 1,973.74 low as a result -- the 11.007 new shares are worth
    //     1,957.26 at 177.82, so the reported move was -16.48 and he said the figure "did not
    //     move at all". And capital.js counts an investment lot as EXTERNAL capital unless it
    //     says `funded: 'cash'`, so eleven months of contribution counted and two did not.
    //
    // (b) THE DRIPS LANDED ON THE EUR CASH ROW. On a Cash holding `qty` IS the currency amount
    //     and `price` IS the FX rate. So EUR gained 4.637729 of quantity it never received, and
    //     two `S.dividends` rows were written under ticker EUR while SAP was missing both
    //     dividends entirely.
    //
    //     ⚠ THE AVERAGE MOVED THE OTHER WAY FROM THE OBVIOUS GUESS. `blendedAvgCost` adds a
    //     DRIP at ZERO cost, so those two lots DILUTED the EUR rate rather than inflating it:
    //     52.031325 -> 51.745345. The first reading of this defect assumed 414.75 of cost each
    //     and computed a pre-error rate of 51.043, which was wrong. `holdingAfterExit` with
    //     exitCost 0 is the documented inverse and is what runs below.
    //
    // The owner confirmed the figures: July 414.75 at 171.679, August 414.75 at 186.666.
    //
    // ⚠ STILL OPEN, DELIBERATELY NOT TOUCHED. His August INVESTMENT lot carries 187.666 while
    // the August dividend is 186.666, and in all eleven prior months the investment and the
    // DRIP share one month-end price. One of the two is a typo. A share count is not this
    // migration's to guess, so the investment lot is left exactly as entered and the question
    // is his. `blendedAvgCost` ignores a DRIP's price, so the discrepancy costs nothing until
    // it is answered.
    //
    // Idempotent by construction: every step tests for the damage before repairing it, so a
    // reload, a second device or a re-run finds nothing to do.
    run: () => {
      const eur = (S.holdings || []).find(h => h && h.ticker === 'EUR' && h.type === 'Cash');
      const sap = (S.holdings || []).find(h => h && h.ticker === 'SAP');
      const DATES = ['2026-07-31', '2026-08-31'];

      // (a1) the two portfolio_buy rows, and the balance they moved. Tombstoned, because an
      //      untombstoned delete resurrects on the next merge and re-spends the money.
      const bad = (S.cashTxs || []).filter(t => t && t.type === 'portfolio_buy'
        && (t.currency || '') === 'EUR' && /^Buy SAP:/.test(String(t.note || ''))
        && DATES.includes(String(t.date || '').slice(0, 10)));
      bad.forEach(t => {
        const i = S.cashTxs.indexOf(t);
        if (i >= 0) S.cashTxs.splice(i, 1);
        const cur = t.currency || 'EUR';
        S.cash[cur] = (S.cash[cur] || 0) - t.amount;   // amount is negative, so this adds back
        tombstoneId(t.id);
      });

      // (a2) the stamp. An unstamped lot reaches capital.js's plan_lots door as the external
      //      capital it is, which is the shape the eleven older SAP lots already have.
      if (sap) {
        let touched = false;
        (sap.lots || []).forEach(l => {
          if (l && l.funded === 'cash' && DATES.includes(String(l.date || ''))) {
            delete l.funded; touched = true;
          }
        });
        if (touched) bumpMigrated(sap);
      }

      // (b) move the drip lots off the cash row and onto the holding that paid the dividend.
      if (eur && sap) {
        const moved = (eur.lots || []).filter(l => l && l.type === 'drip' && DATES.includes(String(l.date || '')));
        if (moved.length) {
          const remQty = moved.reduce((a, l) => a + (Number(l.qty) || 0), 0);
          eur.lots = (eur.lots || []).filter(l => moved.indexOf(l) === -1);
          const e = holdingAfterExit(eur.qty, eur.avgCost, remQty, 0);   // DRIP: exitCost 0
          eur.qty = e.qty; eur.avgCost = e.avgCost; bumpMigrated(eur);
          moved.slice().sort((a, b) => String(a.date).localeCompare(String(b.date))).forEach(l => {
            sap.avgCost = blendedAvgCost(sap.qty, sap.avgCost, { qty: l.qty, type: 'drip' });
            sap.qty = (Number(sap.qty) || 0) + (Number(l.qty) || 0);
            sap.lots.push({ id: uid(), date: l.date, type: 'drip', qty: l.qty, price: l.price, cost: l.cost });
          });
          bumpMigrated(sap);
        }
      }

      // (b2) the dividend rows name the wrong instrument. Retargeted rather than deleted: the
      //      dividend was real and its amount is right; only the ticker was wrong.
      (S.dividends || []).forEach(d => {
        if (!d || d.ticker !== 'EUR' || !DATES.includes(String(d.date || ''))) return;
        d.ticker = 'SAP';
        d.note = String(d.note || '').replace(/^EUR DRIP/, 'SAP DRIP');
        bumpMigrated(d);
      });

      // (b3) and the ledger prose that named it too. These rows net to zero on the balance, so
      //      nothing here moves money -- but a false note is still a false record.
      //
      //      Scoped by the NOTE PATTERN alone and not by date, because the pattern itself is the
      //      damage test: EUR is a cash row and a cash row pays no dividend, so "Dividend: EUR"
      //      cannot arise legitimately and there is nothing else it could match. That also makes
      //      this step idempotent -- after one run no such note exists.
      (S.cashTxs || []).forEach(t => {
        const n = String((t && t.note) || '');
        if (!/^Dividend: EUR/.test(n) && !/^DRIP reinvest: EUR/.test(n)) return;
        t.note = n.replace(/^Dividend: EUR/, 'Dividend: SAP')
          .replace(/^DRIP reinvest: EUR/, 'DRIP reinvest: SAP')
          .replace(/EUR DRIP/, 'SAP DRIP');
        bumpMigrated(t);
      });
    }
  },
  {
    v: 57,
    name: 'The 2026-09-11 Garanti purchases are rebuilt from one declared table',
    // A ONE-OFF REPAIR OF A DATA-ENTRY ACCIDENT, 2026-09-11. The eleven Garanti buys of that
    // day were entered by driving `openAddLot`/`saveAddLot` from the console in batches. Two of
    // those batches timed out at the CDP layer WITHOUT STOPPING — the async loop kept running in
    // the page while a fresh loop was started against the same modal. Both loops write the same
    // four DOM ids (`lotDate`, `lotType`, `lotQty`, `lotPrice`) and then read them back, so they
    // raced, and the damage measured on the live book was:
    //
    //     XAG      2 lots of 391.1        one is a duplicate
    //     ASELS    2 lots of 54           one is a duplicate
    //     THYAO    2 lots of 36           one is a duplicate
    //     ENJSA    3 lots                 one duplicate of 184, plus ISGYO's 448 @ 23.826371
    //     ISGYO    3 lots                 one duplicate of 448, plus XAG's 391.1 @ 102.168842
    //     GARAN    0 lots                 never written at all
    //
    // The two cross-contaminated lots are the tell: a quantity and a price that belong to a
    // DIFFERENT instrument were read out of the shared modal by the wrong loop. TRY 155,146.35
    // of cash left the balance for purchases that never happened, and TRY 5,414.54 stayed for
    // one that did.
    //
    // ⚠ THE APP HAS NO INVERSE FOR `+ Lot`. There is no `delLot` anywhere in the codebase, so a
    // mistyped or duplicated lot has no door out and the only remedy is a migration. That is a
    // real gap and it is named here rather than quietly worked around; closing it is its own
    // track, because a delete primitive has to answer what happens to the cash row, the average
    // and the month entry, and none of those is decided by this repair.
    //
    // REBUILD, not patch. Every step below discards whatever is dated 2026-09-11 on these
    // eleven holdings and reconstructs exactly one lot per row from the table, priced through
    // `pricePurchase` — the same function `saveAddLot` uses, so the rebuilt rows are
    // indistinguishable from correctly entered ones. That makes it idempotent against itself,
    // against a reload, and against a second device: a re-run removes the lot it wrote and
    // writes the identical lot back. The quantities and gross prices are the owner's own
    // confirmations from the Garanti trade screens, not derived from the damaged state.
    //
    // The averages are unwound with `holdingAfterExit`, the documented inverse of
    // `blendedAvgCost`, against the LIVE qty and avgCost — never recomputed from the lot list,
    // because ASELS and ISGYO both carry basis their lots do not describe.
    run: () => {
      const DATE = '2026-09-11';
      // ticker, quantity, GROSS price as quoted by Garanti. Commission is the model's business.
      const BUYS = [
        ['USD',   957,     49.1620],
        ['EUR',   824,     56.9930],
        ['XAU',     9.64, 6888.4750],
        ['XAG',   391.10,  101.9700],
        ['ENJSA', 184,     116.9000],
        ['ISGYO', 448,      23.7800],
        ['ASELS',  54,     397.0000],
        ['THYAO',  36,     300.7500],
        ['TUPRS',  51,     412.5000],
        ['GARAN',  40,     135.1000],
        ['TCELL',  53,     102.9000],
      ];

      BUYS.forEach(([ticker, qty, grossPrice]) => {
        const h = (S.holdings || []).find(x => x && x.ticker === ticker
          && String((x && x.broker) || '').toLowerCase().includes('garanti'));
        if (!h) return;

        // (a) back every 2026-09-11 investment lot out of qty and avgCost, exactly. DRIP is
        //     excluded because a reinvested distribution is not one of these purchases and
        //     carries no cost to unwind.
        const stale = (h.lots || []).filter(l => l && l.type !== 'drip' && String(l.date || '') === DATE);
        if (stale.length) {
          const sq = stale.reduce((a, l) => a + (Number(l.qty) || 0), 0);
          const sc = stale.reduce((a, l) => a + (Number(l.cost) || 0), 0);
          const e = holdingAfterExit(h.qty, h.avgCost, sq, sc);
          h.qty = e.qty; h.avgCost = e.avgCost;
          h.lots = (h.lots || []).filter(l => stale.indexOf(l) === -1);
        }

        // (b) and every cash row those lots wrote, refunding the balance. Tombstoned, because an
        //     untombstoned delete resurrects on the next merge and re-spends the money.
        (S.cashTxs || []).filter(t => t && t.type === 'portfolio_buy'
          && (t.currency || '') === 'TRY'
          && String(t.date || '').slice(0, 10) === DATE
          && String(t.note || '').indexOf('Buy ' + ticker + ':') === 0
        ).forEach(t => {
          const i = S.cashTxs.indexOf(t);
          if (i >= 0) S.cashTxs.splice(i, 1);
          S.cash.TRY = (S.cash.TRY || 0) - t.amount;   // amount is negative, so this adds back
          tombstoneId(t.id);
        });

        // (c) one lot, priced through the model every other purchase in this book goes through.
        const buy = pricePurchase(h, qty, grossPrice);
        const cost = qty * buy.price;
        h.avgCost = blendedAvgCost(h.qty, h.avgCost, { qty, price: buy.price, cost, type: 'investment' });
        if (!h.lots) h.lots = [];
        h.lots.push({ id: uid(), date: DATE, type: 'investment', qty, price: buy.price, cost, funded: 'cash' });
        h.qty = (Number(h.qty) || 0) + qty;
        bumpMigrated(h);

        S.cash.TRY = (S.cash.TRY || 0) - cost;
        S.cashTxs.unshift(bumpMigrated({
          id: uid(), date: DATE + 'T12:00:00', type: 'portfolio_buy',
          amount: -cost, currency: 'TRY',
          note: `Buy ${h.ticker}: ${f(qty, 4)} @ ${buy.grossPrice} + ${f(buy.feeLocal, 2)} TRY commission (${buy.feeModel})`
        }));
      });
    }
  },
  {
    v: 58,
    name: 'Every conversion already in the book was the IBKR wire, and now it says so',
    // `fxPurpose` arrives with this commit. Two readers -- `cashflow.js:283` (the plan
    // transfer) and `integrity.js:832` (what became dollars) -- used to treat EVERY TRY->USD
    // conversion as the monthly IBKR wire, because the currency pair was the only thing they
    // had to go on.
    //
    // That assumption held only because it had never been tested. All six fx_convert legs in
    // the live book on 2026-09-11 are IBKR wires: 2026-08-04 (TRY 59,262.99 -> USD 1,246.33),
    // 2026-08-28 (TRY 82,991.51 -> USD 1,720.00) and 2026-09-11 (TRY 431,903.76 -> USD
    // 8,884.00). Measured, not recalled -- which is what makes stamping them `ibkr` a record
    // rather than a guess.
    //
    // The Garanti BBVA Kripto account is dollar-denominated too (its own screen reports
    // `Fiat Assets 45.60 USD` and every P&L in USD), so the very next conversion would have
    // been the first one the old readers got wrong.
    //
    // Idempotent: stamps only a leg that has no valid purpose, so a re-run, a reload and a
    // second device all find nothing to do. It moves no money and changes no amount.
    run: () => {
      const VALID = ['ibkr', 'crypto', 'other'];
      (S.cashTxs || []).forEach(t => {
        if (!t || t.type !== 'fx_convert') return;
        if (VALID.indexOf(t.fxPurpose) !== -1) return;
        t.fxPurpose = 'ibkr';
        bumpMigrated(t);
      });
    }
  },
  {
    v: 59,
    name: "August's SAP DRIP was priced a lira low, and the share count was derived from the price",
    // THE QUESTION v56 LEFT OPEN, ANSWERED BY THE OWNER 2026-09-22.
    //
    // v56's docblock flagged this and deliberately did not guess: August's SAP investment lot
    // carries 187.666 while that month's DRIP carries 186.666, and in every other month the two
    // share one month-end price. A share count is not a migration's to invent, so it was left
    // exactly as entered and the question was his. He has ruled: 187.666 is the cost.
    //
    // ⚠ AND THE PRICE IS NOT THE ONLY THING WRONG, WHICH IS WHY THIS IS NOT A ONE-FIELD EDIT.
    // The DRIP quantity was DERIVED from the price, to sixteen significant digits:
    //
    //     414.75 / 186.666 = 2.2218829352961977   <- exactly the stored qty
    //     414.75 / 187.666 = 2.210043374932...    <- what it should be
    //
    // July is the same shape (414.75 / 171.679 = 2.4158458518514205, stored verbatim) and there
    // the DRIP price EQUALS that month's investment price, as it does in May and June. August is
    // the only month where the two disagree. So the typo propagated into the share count and the
    // book carries 0.01183956 shares the plan never credited.
    //
    // ⚠ THE AVERAGE MOVES THE OPPOSITE WAY FROM THE OBVIOUS GUESS, for the same reason it did in
    // v56: `blendedAvgCost` adds a DRIP at ZERO cost. So removing DRIP quantity does not remove
    // any basis -- the €12,447.7244 stands untouched, spread over fewer shares, and the average
    // RISES: 127.02040398593907 -> 127.035751748. Anyone reading "0.012 fewer shares" and
    // expecting the average to fall would write the wrong number with full confidence.
    //
    // The dividend is untouched: €414.75 is the cash the plan paid and that was never in doubt.
    // Only its prose named the wrong price.
    //
    // Idempotent: every step tests for the damage first, so a re-run, a reload or a second
    // device finds nothing to do.
    run: () => {
      const sap = (S.holdings || []).find(h => h && h.ticker === 'SAP');
      if (!sap) return;
      const DATE = '2026-08-31';
      const WRONG = 186.666, RIGHT = 187.666, GROSS = 414.75;

      const lot = (sap.lots || []).find(l => l && l.type === 'drip'
        && String(l.date || '') === DATE && Math.abs((Number(l.price) || 0) - WRONG) < 1e-9);
      if (lot) {
        const newQty = GROSS / RIGHT;
        const prevQty = Number(sap.qty) || 0;
        // Hold the basis, not the average: a DRIP carries no cost, so the euros do not move.
        const totalCost = prevQty * (Number(sap.avgCost) || 0);
        const nextQty = prevQty - ((Number(lot.qty) || 0) - newQty);
        lot.qty = newQty;
        lot.price = RIGHT;
        sap.qty = nextQty;
        if (nextQty > 0) sap.avgCost = totalCost / nextQty;
        bumpMigrated(sap);
      }

      // The dividend row's prose named 186.67. The amount was always right.
      (S.dividends || []).forEach(d => {
        if (!d || d.ticker !== 'SAP' || String(d.date || '') !== DATE) return;
        const n = String(d.note || '');
        if (n.indexOf('186.6') === -1) return;
        d.note = `SAP DRIP \u2014 ${(GROSS / RIGHT).toFixed(6)} shares @ \u20ac${RIGHT}`;
        bumpMigrated(d);
      });
    }
  },
  {
    v: 60,
    name: "August 2026 declared its IBKR funding twice, and that is the rest of a5",
    // THE MONTH WHERE BOTH DOORS FIRED. `integrity.js` has warned about this exact month by
    // name since it was written: "August 2026 is the first such month -- a TRY 59,262.99 ->
    // USD 1,246.33 fx_convert on the 4th and no reconciliation yet. Reconciling it would credit
    // another $2,400 on top, for a month whose real conversion was $1,246.33. Two records, one
    // event." It was reconciled anyway, on 2026-08-28.
    //
    // Measured 2026-10-02:
    //
    //   Monthly IBKR deposit -- 2026-08    +$2,400   deposit, EXTERNAL_IN
    //   Portfolio allocation -- 2026-08    -$2,400   portfolio_buy, INTERNAL
    //   real conversions that month       +$1,246.33 (08-04) and +$1,720.00 (08-28), both ibkr
    //   first cash reconciliation          2026-08-08
    //
    // THE PAIR NETS TO ZERO ON THE BALANCE AND NOT ON THE CAPITAL DOORS, which is the whole
    // defect: only the deposit leg is external, so capital gained $2,400 while USD cash did not
    // move. Removing both legs therefore leaves `S.cash.USD` untouched -- verified by the test.
    //
    // ⚠ AND JANUARY THROUGH JULY MUST STAY. For those months no conversion was ever recorded
    // and the cash reconciliations had not started, so the monthly row is the ONLY record in
    // this book that lira became dollars. `integrity.js` priced that too: removing them "would
    // leave USD cash at -$16,399". August is the ONE month where the funding is recorded twice,
    // because 2026-08-08 is when the reconciliations began capturing salary as external capital.
    //
    // September could not happen: `4a245e7` taught the deposit leg to look for an ibkr
    // conversion in the month before declaring one, and Sep-2026 reconciled booking nothing.
    // This closes the one month that predates that guard.
    //
    //   a5 before  -$2,290.09 / ±$2,060   111%
    //   a5 after      +$110    / ±$2,060     5%
    //
    // Idempotent: it looks for the rows before removing them, so a re-run finds nothing.
    run: () => {
      const MONTH = '2026-08';
      const PAIR = [
        { type: 'deposit', prefix: 'Monthly IBKR deposit \u2014 ' },
        { type: 'portfolio_buy', prefix: 'Portfolio allocation \u2014 ' },
      ];
      PAIR.forEach(({ type, prefix }) => {
        (S.cashTxs || []).filter(t => t && t.type === type && (t.currency || '') === 'USD'
          && String(t.note || '').indexOf(prefix + MONTH) === 0
        ).forEach(t => {
          const i = S.cashTxs.indexOf(t);
          if (i >= 0) S.cashTxs.splice(i, 1);
          // Both legs come out together, so the balance is restored by the pair itself: the
          // deposit's +2,400 and the buy's -2,400 cancel. Reversing each against S.cash keeps
          // the ledger identity true whichever order they are found in.
          S.cash.USD = (S.cash.USD || 0) - t.amount;
          tombstoneId(t.id);
        });
      });
    }
  },
  {
    v: 61,
    name: "January through March 2026 were never statusless by accident",
    // THREE MONTHS THAT READ AS UNFINISHED FOR SIX MONTHS AND WERE NOT.
    //
    // `monthlyRates['2026-01'/'-02'/'-03']` carry entries and NO `status` field. Two readers test
    // the raw field -- `monthly.js:177` and `integrity.js:1703`, both `status !== 'reconciled' &&
    // status !== 'legacy'` -- so all three land in `staleOpen` and `plan.currentMonth` has been a
    // standing WARN naming them. `math.js:191` does NOT: it defaults a missing status to
    // 'legacy', which is why no NUMBER was ever wrong. One field, two conventions.
    //
    // WHY THEY LOOK HALF-EMPTY, MEASURED 2026-10-02 against the owner's own allocation sheet for
    // that period:
    //
    //   the sheet's IBKR rows                      37.5%  = $900/month
    //   recorded entries, 2026-01 / -02 / -03      $944 / $835 / $956     (13 / 11 / 12 rows)
    //   the sheet's Garanti rows                   61%    = $1,464/month
    //
    // So the monthly rows hold the IBKR leg in full -- the spread around $900 is fill variance,
    // not a gap -- and the Garanti leg of those months is NOT missing. It went in as the opening
    // bulk quantities at the app's 2026-04-01 inception. It is measurable today as basis beyond
    // lots on every Garanti holding (ENJSA, ISGYO, ASELS, THYAO, TUPRS, EUR, USD, XAU, XAG, NVB
    // -- TRY 313,262, which also covers 2025), and `5fca41f` is what finally let the
    // opening-balance door account for it instead of one lot closing the door on the lot.
    //
    // The $2,400 pair in the ledger for each of these months is therefore correct and STAYS --
    // v60 already recorded why: for Jan through Jul the monthly row is the only record in this
    // book that lira became dollars.
    //
    // So the truthful label is `reconciled`, and the `note` is the load-bearing half: without it
    // the next pass to read "13 of 44 rows" re-enters the Garanti leg through `saveMo`, which
    // ADDS to the position, and double-books TRY 313,262 of purchases that are already in the
    // opening quantities. August 2026 carries a note for exactly this reason and it has held.
    //
    // Changes no valuation: `math.js` already took the 'legacy' branch for a missing status, and
    // 'reconciled' takes the same one. What changes is the Months KPI (+3), the reconciled streak
    // (6 -> 9), the three badges, and `plan.currentMonth` going WARN -> OK with nothing stale.
    // The Edit button goes away on those three, which is the point rather than a side effect.
    //
    // Idempotent: a row that already carries a status is left alone.
    run: () => {
      const MONTHS = ['2026-01', '2026-02', '2026-03'];
      const NOTE = 'Pre-inception month. The IBKR leg (37.5% of base, ~$900) is recorded here in '
        + 'full. The Garanti leg (61% of base) is NOT missing from the book: it went in as the '
        + 'opening bulk quantities on 2026-04-01 and is carried by the opening-balance capital '
        + 'door as basis beyond lots. Do NOT enter the Garanti rows here — saveMo ADDS to the '
        + 'position and would double-book purchases that already exist. The $2,400 cash pair for '
        + 'this month is the only record that lira became dollars and must stay (see v60).';
      MONTHS.forEach(k => {
        const r = S.monthlyRates && S.monthlyRates[k];
        if (!r || r.status) return;
        r.status = 'reconciled';
        r.note = NOTE;
        // No bumpMigrated: `monthlyRates` is a SCALAR_FIELD, so the merge compares whole-field
        // JSON and `save()` stamps `_scalarTimestamps.monthlyRates` on any content change. A
        // per-row `_updatedAt` is read by nothing here (only 2026-08 carries one, from an
        // earlier edit) and would be dead weight in the field every device compares.
        console.warn(`[v61] ${k} stamped reconciled — pre-inception month, Garanti leg is in the opening quantities`);
      });
    }
  },
  {
    v: 62,
    name: "A visa fee and two school installments were claiming a monthly reserve",
    // THE THREE ROWS THAT MOVED THE GOAL POST.
    //
    // `forwardEssentialMonthlyTRY` answers "what leaves every month from here on", and it is
    // multiplied by 6 for the reserve target and by 12 for the FI number. Until the `oneOff` flag
    // shipped alongside this migration, every confirmed non-def item in a closed month entered it
    // and there was no way to say a payment happens once. On this book exactly three rows are in
    // that state, and two of them WERE the entire trailing median:
    //
    //     2026-08  USA Visa                 TRY 21,300   paid once, ever
    //     2026-09  School Transportation-1  TRY 75,000   installment 1 of 2
    //     2026-10  School Transportation-2  TRY 75,000   installment 2 of 2, still unconfirmed
    //
    // When September closed, that median went 21,300 -> 48,150 (the median of two numbers is their
    // average) and the four card projections rolled their 3-month window at the same time. Measured
    // against the live book:
    //
    //                       before Sep closed     after
    //     essential/month       TRY 178,389     TRY 243,220
    //     reserve target          $21,790.19      $29,709.28
    //     coverage                $22,661.95      $22,661.95   <- unmoved, to the cent
    //     Goals stage            past Stability   SURVIVAL, 76%
    //
    // The owner opened Goals, found himself demoted, and reported that he had closed nothing. He
    // was right: coverage never fell. The bar rose, on a visa fee he will not pay again and a
    // school fee with one installment left.
    //
    // ⚠ WHY NOT EXPENSE DEFINITIONS, WHICH IS WHAT WAS ASKED FOR. A definition with
    // `fixed: true, fixedAmount: 75000, loanTotal: 2` lands in `defPart`, which adds the FIXED
    // AMOUNT every month it is active — TRY 75,000/month instead of the TRY 48,150 median. It
    // would make the target worse, not better, and only stop when the installments ran out. The
    // intent behind that request is "get these out of the recurring basis", and the flag is what
    // does that.
    //
    // NOT SOLVED, and worth saying plainly: a two-installment school fee is not TRY 0 a month
    // either. The honest figure is the annual total amortised, and this app has no amortisation
    // for expenses. This picks the smaller error.
    //
    // Idempotent: it tests the flag before setting it, and matches on month + exact name + exact
    // amount so a later row that happens to share a name is not caught.
    run: () => {
      const TARGETS = [
        { mo: '2026-08', name: 'USA Visa', amount: 21300 },
        { mo: '2026-09', name: 'School Transportation-1', amount: 75000 },
        { mo: '2026-10', name: 'School Transportation-2', amount: 75000 },
      ];
      const cf = S.cashflow;
      if (!cf || typeof cf !== 'object') return;
      TARGETS.forEach(({ mo, name, amount }) => {
        const m = cf[mo];
        if (!m || !Array.isArray(m.items)) return;
        m.items.forEach(it => {
          if (!it || it.oneOff === true) return;
          // Trimmed on both sides: the live `USA Visa ` carries a trailing space.
          if (String(it.name || '').trim() !== name) return;
          if (Math.abs((Number(it.amount) || 0) - amount) > 0.01) return;
          it.oneOff = true;
          console.warn(`[v62] ${mo} ${name} marked one-off — out of the forward essential basis`);
        });
      });
    }
  },
  {
    v: 63,
    name: "The school fee is annual, billed twice, and now says so",
    // `oneOff` (v62, one day old) could say "never again" and nothing else. The owner's answer to
    // the open question settles the shape: the school fee "her yil eylul ve ekim ayinda gelir,
    // 2 taksit olur, ve yilliktir" — TRY 150,000 a year, two TRY 75,000 installments, every
    // September and October, for as long as there is a school.
    //
    // `recurEvery` replaces `oneOff` rather than joining it: two fields answering "how often" is
    // the N-copies failure this file keeps paying for. 0 is what `oneOff: true` meant.
    //
    // TWO DEFINITIONS, NOT ONE. A single annual def cannot express two billing months, and the
    // owner had already named them -1 and -2. Each carries `recurEvery: 12` with its own anchor,
    // so each amortises to 6,250/month — TRY 12,500 together, the honest monthly figure — and
    // each bills its whole 75,000 in its own month.
    //
    // This is what was asked for two rounds ago ("2 yi de kendin girip yapabilirsin") and refused
    // then, correctly: without a period field a def lands in `defPart` at its FULL fixedAmount
    // every active month, TRY 75,000 instead of the 48,150 median, and would have made the target
    // worse. The field is what makes definitions the right answer.
    //
    //     reserve target   $29,709.28 (before oneOff)  ->  $23,827.77 (v62)  ->  $25,355 (here)
    //
    // The existing rows are LINKED rather than left loose: without `defId` the generator would add
    // a second School Transportation row beside the one already there.
    //
    // Idempotent: it looks for its own defs by name before creating them, and tests each field
    // before writing it.
    run: () => {
      // ── a) the legacy flag becomes the period, wherever it sits
      // Two walkers, not one shared `carry`: a definition is a MERGE_ARRAYS row and must be
      // bumped so the merge carries the conversion (an unbumped edit loses to its cloud twin);
      // an item lives inside `cashflow`, a SCALAR_FIELD copied whole, and needs no bump. One
      // function for both was what the row-outrank sweep could not tell apart, and it is why
      // this migration failed the deploy gate on 2026-10-03 (45157f3 never deployed).
      (S.expenseDefs || []).forEach(d => {
        if (!d || d.oneOff !== true) return;
        if (d.recurEvery == null) d.recurEvery = 0;
        delete d.oneOff;
        bumpMigrated(d);
      });
      Object.keys(S.cashflow || {}).forEach(k => {
        const m = S.cashflow[k];
        if (!m || !Array.isArray(m.items)) return;
        m.items.forEach(it => {
          if (!it || it.oneOff !== true) return;
          if (it.recurEvery == null) it.recurEvery = 0;
          delete it.oneOff;
        });
      });

      // ── b) the two school definitions, annual, anchored a month apart
      if (!Array.isArray(S.expenseDefs)) S.expenseDefs = [];
      const SCHOOL = [
        { name: 'School Transportation-1', day: 1, anchor: '2026-09' },
        { name: 'School Transportation-2', day: 1, anchor: '2026-10' },
      ];
      const byName = (n) => (S.expenseDefs || []).find(d => d && String(d.name || '').trim() === n);
      SCHOOL.forEach(({ name, day, anchor }) => {
        let d = byName(name);
        if (!d) {
          d = { id: uid(), name, type: 'Other', day, fixed: true, fixedAmount: 75000, active: true,
            loanTotal: 0, loanRemain: 0, loanStart: anchor, interestRate: 0, recurEvery: 12,
            note: 'Annual school fee, billed in two installments' };
          S.expenseDefs.push(stampItem(d));
          console.warn(`[v63] created ${name} — annual, anchored ${anchor}, amortises to 6,250/mo`);
        }
        // ── c) link the rows that already exist, or the generator doubles them
        Object.keys(S.cashflow || {}).forEach(k => {
          const m = S.cashflow[k];
          if (!m || !Array.isArray(m.items)) return;
          m.items.forEach(it => {
            if (!it || it.defId) return;
            if (String(it.name || '').trim() !== name) return;
            it.defId = d.id;
            delete it.recurEvery;           // the definition carries it now
            console.warn(`[v63] ${k} ${name} linked to its definition`);
          });
        });
      });
    }
  },
  {
    v: 64,
    name: "A manual row dated inside the bank reading does not move the balance",
    // saveCTx moved S.cash.TRY for every manual deposit and withdrawal whatever its date, while
    // saveOTI had known since 2026-08-10 that a row dated on or before the reconciliation
    // reading is already inside the figure the owner typed. Entered 2026-10-13: a TRY 40,720
    // Living Expense withdrawal dated 16 Sep, eleven days after the 2 Oct reading. The ledger
    // was right, the balance was TRY 40,720 low, and the 1 Nov reconciliation would have
    // absorbed it as an unexplained +40,720.
    //
    // GUARDED, not a target: a manual TRY row with no mirror flag, dated on or before the
    // CURRENT anchor day and written after the anchor time, is the double count. Older anchors
    // are not consulted: a row entered between two readings was absorbed by the later one, and
    // flipping it now would double-correct. The flip is the guard, so a replay finds `_audit`
    // set and moves nothing. Measured on the 2026-10-17 backup: one row qualifies.
    run: () => {
      const rec = S.cashRecon && S.cashRecon.TRY;
      const atMs = rec && rec.at ? Date.parse(rec.at) : NaN;
      if (!isFinite(atMs)) return;
      const a = new Date(atMs);
      const anchorDay = `${a.getFullYear()}-${String(a.getMonth() + 1).padStart(2, '0')}-${String(a.getDate()).padStart(2, '0')}`;
      (S.cashTxs || []).forEach(tx => {
        if (!tx || (tx.type !== 'deposit' && tx.type !== 'withdraw')) return;
        if (tx._audit === true || tx._incomeId || tx._otiId) return;
        if ((tx.currency || 'TRY') !== 'TRY') return;
        if (/^Cash reconciliation/.test(String(tx.note || ''))) return;
        if (!(String(tx.date || '').slice(0, 10) <= anchorDay)) return;
        if (!((Number(tx._updatedAt) || 0) > atMs)) return;
        tx._audit = true;
        bumpMigrated(tx);
        S.cash.TRY = (S.cash.TRY || 0) - (Number(tx.amount) || 0);
        console.warn(`[v64] ${String(tx.date).slice(0, 10)} ${tx.type} ${tx.amount} was inside the ${anchorDay} reading: marked _audit, balance restored by ${-(Number(tx.amount) || 0)}`);
      });
    }
  }
];

/**
 * Local copy of utils.isoDate. state.js is imported BY utils in places and must not depend on
 * it; a migration that cannot run because of an import cycle is worse than eight duplicated
 * lines. Kept deliberately identical — if one changes, change both.
 */
function _isoDateForMigration(raw) {
  if (raw == null) return null;
  if (typeof raw === 'number') {
    return isFinite(raw) && raw > 0 ? new Date(raw).toISOString().slice(0, 10) : null;
  }
  const str = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  if (!/\b\d{4}\b/.test(str)) return null;
  const t = Date.parse(str);
  return isNaN(t) ? null : new Date(t).toISOString().slice(0, 10);
}

/**
 * The highest schema version this build understands — DERIVED, never restated.
 *
 * It does two jobs: the ceiling `importData` checks a file against, and the `_schemaVersion` a
 * brand-new state starts at. Job two used to be the dangerous one — a payload carrying no version
 * did not overwrite the default, so `runMigrations` read it as `fromV` and skipped everything
 * beneath, and raising the number would have widened that from 29 versions to all of them. That
 * hole is closed at the readers now: the three LOAD readers (IndexedDB, localStorage, cloud)
 * refuse to adopt a payload without a numeric `_schemaVersion`, and the two RESTORE doors —
 * `importData` and `restoreBackup` — refuse the payload itself through `schemaLegibility` below.
 * (DATA-001 / DATA-005 / DATA-014)
 *
 * ⚠ THIS DOCBLOCK SAID "all three of them" AND THERE WERE FIVE. `restoreBackup` is a reader like
 * any other and it adopted whatever it was handed; the sentence read as coverage for four months.
 * A doctrine in a docblock propagates further than a bug (PROC-021) — count the doors, do not
 * recall them.
 *
 * NOTE, deliberate: a fresh state now starts at the newest version, so migrations no longer run
 * on a brand-new device. Some of them seeded this household's own corrections — a declared
 * opening month, four Turkish credit-card names — into every new state, which was never right.
 * The cost is that `settings.basisMonth` is unset on a new or wiped device until it is entered.
 */
export const CURRENT_SCHEMA = MIGRATIONS.reduce((max, m) => {
  const v = Number(m && m.v);
  return isFinite(v) && v > max ? v : max;
}, 0);

S._schemaVersion = CURRENT_SCHEMA;

/**
 * CAN THIS BUILD READ THE DATE ON THE TIN? — one rule, both restore doors (DATA-014).
 *
 * `runMigrations` consumes `S._schemaVersion` and nothing else. A payload that does not carry a
 * numeric one therefore does not lower the number on install — `Object.assign` cannot overwrite a
 * key that is absent — so the chain reads the LIVE version, finds nothing pending, and installs
 * old data into a new build having run **zero** migrations. That is the opposite failure to every
 * other door here, which exist to stop migrations REPLAYING.
 *
 * ⚠ AND THE BLOB CLASS IS REAL, not hypothetical. Four full-state exports on disk — 2026-03-29,
 * 04-01, 04-04, 04-07 — carry no `_schemaVersion` at all (the 03-29 one still calls the cash
 * balance `cashBalance`). This app wrote them. The IndexedDB snapshot store `restoreBackup` reads
 * has ONE writer, `idbSave({ ...S })` in `importData`, capped at ten entries and written once per
 * import — so a blob from that era survives in it until ten later imports push it out, and it is
 * offered in the restore table with a date and a row count like any other.
 *
 * ⚠ WHY THE TRUSTED DOOR CHECKS ANYTHING AT ALL, given CONTRACT-005. Trust is about AUTHORSHIP,
 * not legibility. `restoreBackup` trusts the blob not to be hostile, which is why it installs by
 * denylist rather than allowlist; it cannot thereby read a version the blob does not state.
 * Declining the payload is not an allowlist and does not narrow what a legible blob installs.
 *
 * ⚠ AND THE ANSWER IS NOT "TREAT IT AS v0 AND RUN THE WHOLE CHAIN". Measured in RUN 7: replaying
 * v40 resurrects two positions he has since closed, because its targets are hard-coded to the day
 * they were written. A correction is dated and the book is not (theme #30). Running fifty-two
 * dated corrections over a March book produces a state that existed on no day. Refusing is the
 * only answer that does not invent one.
 *
 * `why` is 'missing' for a payload with no readable version and 'newer' for one from a build
 * ahead of this one; the caller words its own message, because a FILE and a SNAPSHOT are not the
 * same noun to the person reading the toast.
 *
 * ⚠ `Number.isFinite`, not `typeof === 'number'`: `NaN` passes the typeof test, fails
 * `NaN > CURRENT_SCHEMA`, installs, and then `S._schemaVersion || 0` reads it as **0** — the
 * whole chain from the other side. `JSON.parse` cannot produce one, so this is unreachable
 * through either door today; it costs one word to keep it that way.
 */
export function schemaLegibility(payload) {
  const v = payload && typeof payload === 'object' ? payload._schemaVersion : undefined;
  if (!Number.isFinite(v)) return { ok: false, why: 'missing', v: null };
  if (v > CURRENT_SCHEMA) return { ok: false, why: 'newer', v };
  return { ok: true, why: 'ok', v };
}

/**
 * Run pending migrations to bring schema up to date
 */
export function runMigrations() {
  const fromV = S._schemaVersion || 0;
  let migrated = 0;
  let lastSuccessV = fromV;

  // CE-16 Fix: Stop chain on failure, only advance to highest successful version
  const pending = MIGRATIONS.filter(m => m.v > fromV).sort((a, b) => a.v - b.v);

  for (const m of pending) {
    try {
      m.run();
      migrated++;
      lastSuccessV = m.v;
      console.warn('[ZAGX] Migration v' + m.v + ': ' + m.name + ' ✓');
    } catch (e) {
      console.error('[ZAGX] Migration v' + m.v + ' FAILED:', e);
      toast('Migration v' + m.v + ' failed: ' + e.message + '. Data may be incomplete — export a backup.', 'a-r');
      break; // Stop chain — don't skip past failed migration
    }
  }

  S._schemaVersion = lastSuccessV > fromV ? lastSuccessV : fromV;
  // warn, not log. The production bundle gates console.log, so every line a migration
  // wrote about moving the owner's money was invisible exactly where it mattered: after a
  // deploy, on the machine holding the real data. A migration that edits quantities and
  // cost basis has to be able to prove it ran. (audit 2026-08-03)
  console.warn('[ZAGX] schema v' + S._schemaVersion + (migrated > 0 ? ' — ' + migrated + ' migration(s) applied this load' : ' — up to date'));
}

// ============ IMPORT VALIDATION ============

/**
 * Deep schema validation: whitelisted keys per array item type
 *
 * THIS LIST IS A DELETION LIST. Any key a writer produces that is absent here is silently
 * dropped on backup import — the row survives looking healthy while a field that carried
 * meaning is gone. The 2026-08-02 audit found nine such fields in live data, three of them
 * destructive:
 *
 *   - rfTxs.currency  — ALL five reserve-fund rows. Without it, "15.09" stops being 15.09
 *     grams of gold and becomes 15.09 of nothing. The reserve fund is multi-currency by
 *     design (TRY/USD/EUR/GBP/XAU/XAG) and the ledger carried the only record of which.
 *   - trades.lots     — the eight open BIST positions each hold a two-lot averaging history.
 *     Dropping it leaves `entry` with no derivation and no audit trail.
 *   - dividends.kind  — 'match' marks the SAP employer contribution, which is deliberately
 *     excluded from passive income and yield (goals.js:167, cash.js:426, portfolio.js:259).
 *     Strip it and free employer shares start reading as investment income.
 *
 * `tests/core/import-keys.test.js` pins every key a writer emits. When you add a field to a
 * record anywhere in the app, add it here in the same commit or that test fails.
 */
export const _IMPORT_KEYS = {
  holdings: ['id', 'ticker', 'name', 'type', 'subType', 'portfolioName', 'broker', 'buyWindow', 'allocationPct', 'allocation', 'currency', 'qty', 'avgCost', 'portfolioId', 'lots', 'since', 'basisDate', '_updatedAt'],
  trades: ['id', 'ticker', 'type', 'side', 'currency', 'entry', 'qty', 'leverage', 'sl', 'tp1', 'tp1Pct', 'tp2', 'tp2Pct', 'note', 'horizon', 'annualRate', 'duration', 'status', 'openDate', 'closeDate', 'exits', 'lots', 'td', 'broker', '_updatedAt', '_originalQty', 'journal',
    // ⚠ ADDED IN THE SAME COMMIT AS `core/thesis.js` — trap #1. This list is a DELETION
    // list: every array it names is stripped to these keys on import, so a field that is
    // not here is silently erased by the owner's next export-import round trip. It has
    // cost live data twice; `oneTimeIncome[].type` was the second and it made a paid
    // ₺93.669,31 refund read as unbooked. `_sanitizeImportArray` copies an allowed key
    // wholesale, so the nested object survives intact once it is named here.
    'thesis'],
  // `_liabId` / `_paymentId` are what `deletePayment` reverses by. Strip them and the door falls
  // back to a note match, or refuses — and an unlinked payment cannot be undone at all. (v51)
  cashTxs: ['id', 'date', 'type', 'amount', 'currency', 'reason', 'note', 'tradeId', '_incomeId', '_otiId', '_divId', '_liabId', '_paymentId', '_audit', '_reconciledAt', '_updatedAt', 'cat', 'txClass', 'fxId', 'fxRate', 'fxTo', 'fxFrom', 'fxPurpose'],
  // `kind` was missing here while `closeDailyTd` / `confirmMaturity` write it, so an import
  // deleted it from every deposit row. Survivable only because `isInterestRealized` has two
  // other legs (`reason` 'Daily TD closed' / 'Matured') -- a list whose omission is covered by a
  // redundancy somewhere else is still an omission (trap #1, HORIZ-004).
  realized: ['id', 'date', 'ticker', 'qty', 'entry', 'exit', 'pnl', 'pnlUSD', 'currency', 'reason', 'leverage', 'tradeId', 'kind', 'feeLocal', 'feeModel', '_updatedAt'],
  dividends: ['id', 'ticker', 'date', 'gross', 'grossUSD', 'currency', 'whtPct', 'whtAmt', 'net', 'netUSD', 'note', 'drip', 'kind', 'lotId', 'lotHoldingId', '_updatedAt'],
  // `oneOff` shipped in defa1c7 and was NOT added here, which is trap #1 in the note above
  // happening again to the person who wrote the note. `cfGenItems` passes it down to generated
  // items and `forwardEssentialMonthlyTRY` reads it on the definition, so an export-import round
  // trip would have silently un-flagged every definition that carried it. Latent only because no
  // UI sets it on a definition yet. (2026-10-03)
  expenseDefs: ['id', 'name', 'type', 'day', 'fixed', 'fixedAmount', 'active', 'loanTotal', 'loanRemain', 'loanStart', 'interestRate', 'note', 'paidBy', 'oneOff', 'recurEvery', '_updatedAt'],
  incomeDefs: ['id', 'name', 'type', 'amount', 'currency', 'note', '_updatedAt'],
  // `funded` / `_cashTxId` are what `rfDeleteTx` reverses by. Strip them and a cash-funded
  // reserve deposit becomes indistinguishable from an external one, which is the exact state
  // MOVE-002 existed in: the inverse could not know what to undo. (v52)
  rfTxs: ['id', 'date', 'type', 'amount', 'currency', 'note', 'funded', '_cashTxId', '_updatedAt'],
  liabilities: ['id', 'name', 'currency', 'originalAmount', 'remainingAmount', 'createdAt', 'note', 'status', 'payments', '_updatedAt'],
  goals: ['id', 'name', 'type', 'targetAmount', 'currency', 'linkedSource', 'createdAt', 'completedAt', 'status', '_updatedAt'],
  achievements: ['id', 'type', 'name', 'threshold', 'unlockedAt', '_updatedAt'],
  // `mandate` decides whether the AI may recommend selling a sleeve at all (alpha-score.js
  // mandateOfPortfolio → the MANDATE POLICY rule in ai-copilot.js). It was written by
  // savePortfolio and missing here, and the moment `portfolios` became restorable that made
  // every import strip it: SAP XETRA is isDefault:false, so it fell back to 'active' and the
  // "never recommend trimming" rule stopped applying to the DCA plan. (audit 2026-08-13)
  // `sizing` and `trigger` JOINED THIS LIST IN THE SAME COMMIT THAT INTRODUCED THEM (2026-09-02).
  // This list is a DELETION list -- an array is stripped to these keys on import -- and omitting a
  // new field here has cost live data twice already (`trades.thesis`, `oneTimeIncome.type`).
  portfolios: ['id', 'name', 'type', 'horizon', 'tags', 'isDefault', 'status', 'base', 'mandate', 'sizing', 'trigger', 'createdAt', 'closedAt', 'note', '_updatedAt'],
  signals: ['id', 'sourceId', 'sourceName', 'sourceTitle', 'sourceUrl', 'sourceDate', 'ticker', 'assetName', 'assetClass', 'action', 'trend', 'supportLevel', 'supportCcy', 'supportLevelTRY', 'targetLevel', 'targetCcy', 'targetLevelTRY', 'watchTriggers', 'notes', 'evidencePhrases', 'status', 'createdAt', 'expiredAt', 'actedAt', '_extId', '_outcome', '_updatedAt'],
  signalSources: ['id', 'name', 'type', 'analyst', 'channel', 'url', 'trustScore', 'totalSignals', 'hitCount', 'missCount', 'assetClasses', 'lastFetchedAt', '_updatedAt'],
  signalAlerts: ['id', 'signalId', 'type', 'threshold', 'triggered', 'triggeredAt', 'dismissedAt', 'notifyBrowser', 'notifySound', '_updatedAt'],
  bonusPlans: ['id', 'label', 'fiscalYear', 'startDate', 'endDate', 'targetEUR', 'currency', 'allocFramework', '_createdAt', '_updatedAt'],
  bonusQuarters: ['id', 'planId', 'quarter', 'status', 'grossEUR', 'fxRate', 'grossTRY', 'netTRY', 'netBrutRatio', 'earnedDate', 'paidDate', 'allocations', 'note', '_createdAt', '_updatedAt'],
  aiRules: ['id', 'pattern', 'category', 'source', 'confidence', 'hitCount', 'createdAt', '_updatedAt'],
  notifications: ['id', 'type', 'title', 'body', 'ticker', 'action', 'ts', 'read', 'dismissed', '_updatedAt'],
  // The only MERGE_ARRAYS member that had no row shape, so `importData`'s CE-11 sanitiser
  // skipped it and raw file objects went straight into a list that feeds external-capital
  // classification and tax-refund booking.
  //
  // ⚠ THIS LIST WAS WRITTEN FROM GUESSWORK AND COST A LIVE FIELD THE SAME DAY. It shipped as
  // `['id','date','label','amount','currency','cat','note','txId','_updatedAt']` — three keys
  // that do not exist (`label`, `cat`, `txId`) and, fatally, WITHOUT `type`. `saveOTI` writes
  // `{id, date, type, amount, currency, note}` plus `taxYear`/`taxRef` when the tax bridge
  // creates the row, and `taxRefundBooking` adopts a refund by `e.type === 'Tax Refund'`.
  // So the first import after that commit stripped `type` from both of the owner's rows and
  // the integrity check went from "IN CASH" to "not in cash" on money that had not moved.
  // Measured blast radius: this array, this field, two rows. Nothing else.
  //
  // The lesson is not "be careful with the list". `import.keyLoss` in integrity.js now grades
  // every one of these allowlists against the rows actually in the state, so the next guess
  // announces itself instead of deleting something. (audit 2026-08-13/14)
  oneTimeIncome: ['id', 'date', 'type', 'amount', 'currency', 'note', 'taxYear', 'taxRef', '_updatedAt']
};

/**
 * Top-level keys a restore is allowed to install — DERIVED from the lists that already say what
 * this app stores, never restated by hand.
 *
 * The hand-written version omitted FIFTEEN keys that `_IMPORT_KEYS` sanitises, `MERGE_ARRAYS`
 * merges or `SCALAR_FIELDS` replaces: portfolios, oneTimeIncome, bonusPlans, bonusQuarters,
 * signals, signalSources, signalAlerts, aiRules, notifications, aiInsights, aiCopilot,
 * activePortfolioId, tickerMap, rebalancing, benchmarkPrices. A restore therefore came back
 * missing the portfolio grouping, every one-time income row and every bonus quarter — and
 * reported success. Adding a sixteenth entry to a list nobody can keep in sync is not a fix.
 *
 * `_IMPORT_KEYS` says "these arrays have a known row shape". `MERGE_ARRAYS` says "these merge by
 * id". `SCALAR_FIELDS` says "these objects are replaced whole". A key in any of the three is part
 * of the state a restore must carry. EXTRA holds the few that are in none of them.
 *
 * (DATA-002, audit 2026-08-13)
 */
const _RESTORE_EXTRA = ['snapshots', 'history', 'prices',
  '_schemaVersion', '_deletedIds', '_deletionTimestamps'];
// `_originalQty` and `_lastModified` used to be in here, inherited verbatim from the
// hand-written list this replaced. Both are dead: `_originalQty` is only ever a field ON A TRADE
// (already in `_IMPORT_KEYS.trades`), never a top-level key, and `save()` overwrites
// `_lastModified` one line after the import installs it.

/**
 * Keys a restore must NOT install, and why. Without this the union has no opposing force: every
 * assertion about RESTORE_KEYS says "contains", so a key that must stay out can be added and
 * nothing objects.
 */
export const NEVER_RESTORE = Object.freeze([
  // Written by load() when it gives up; installing a file's copy would either lie about this
  // device or clear a mark that is currently true.
  '_provisional',
  // Re-derived on the next backup. Restoring it dates the nag from the backup's day.
  'lastBackup',
  // Per-field merge clocks. A file's clocks describe the device that wrote it, and installing
  // them hands that device's merge wins to this one.
  '_scalarTimestamps',
  // Says the ledger was trimmed HERE, which governs whether the reconciler may auto-fix a gap.
  '_truncated',
  // Cloud row version for optimistic locking; belongs to this session, not to a file.
  '_cloudVersion',
]);

/**
 * `activePortfolioId` and `benchmarkPrices` are in SCALAR_FIELDS but must not travel through a
 * restore, for reasons that are not "they are transient":
 *
 * - `activePortfolioId` is a VIEW FILTER. `renderP` hides every holding outside it and scopes
 *   the page totals, so a backup taken while filtered shows a fraction of the book to the one
 *   person on earth who is at that moment checking whether the restore worked. It can also point
 *   at a portfolio the file does not contain, and the portfolio bar hides itself when only one
 *   survives — leaving no control to clear it.
 * - `benchmarkPrices` only ever grows (`backfillBenchmarkPrices` writes a month only when it is
 *   missing, and only from a manual button). An older copy is therefore a strict subset, and
 *   `save()` would stamp the shrunken version as the newest so the merge propagates the loss to
 *   the cloud and the other device. That exact loss is already recorded in this file's merge
 *   docblock; this is the same incident through a different door.
 */
const _RESTORE_EXCLUDE = new Set([...NEVER_RESTORE, 'activePortfolioId', 'benchmarkPrices']);

/**
 * ⚠ THE TWO RESTORE DOORS DIFFER BY TRUST, NOT BY CARELESSNESS — CONTRACT-005.
 *
 * `importData` reads a file the owner picked off disk. It is UNTRUSTED, so it installs an
 * ALLOWLIST (`RESTORE_KEYS`) and a hostile file cannot set an arbitrary key on `S`.
 * `restoreBackup` reads a blob this app wrote into its own IndexedDB. It is TRUSTED, and its job
 * is to be COMPLETE — so it installs everything and must only decline the keys that describe THIS
 * DEVICE rather than the book.
 *
 * It was declining nothing. `Object.assign(S, data)` installed all seven excluded keys, and
 * measured on the two backups actually on his disk (2026-08-16), four of them are present:
 *
 *   activePortfolioId  BOTH blobs carry 'moy4wrqmxycbu' = SAP XETRA, which holds 1 of 44
 *                      holdings, while the live book has NO filter. Restoring either one showed
 *                      the owner a 44th of his book at the exact moment he was checking whether
 *                      the restore had worked.
 *   _scalarTimestamps  16 and 18 keys — the merge clocks of the device that wrote the file,
 *                      handing that device's merge wins to this one.
 *   _truncated         "the ledger was trimmed HERE", which governs whether the reconciler may
 *                      auto-fix a gap. A stale copy is a false statement about this device.
 *   lastBackup         dates the backup nag from the backup's day.
 *
 * ⚠ **AND THE OBVIOUS FIX IS WRONG.** Switching this door to `RESTORE_KEYS` would also stop
 * restoring TEN keys that allowlist has never heard of, because it is derived from `_IMPORT_KEYS`,
 * `MERGE_ARRAYS` and `SCALAR_FIELDS` and none of those enumerate top-level scalars. Among them:
 * **`fxEURUSD`**, read by `math.js`, `utils.js` and `dividends.js` to convert EUR money; `fxGBPUSD`;
 * and the migration guards `_v5_migrated` and `_salaryAuditMigrated` — and a re-run of a
 * delta-style migration is what moved TRY 63,485 of assets that were never bought (RUN 3).
 * A rollback that quietly declines to roll those back is worse than one that carries a stale view
 * filter. So: keep the completeness, subtract the exclusions. A denylist, over the list that is
 * already a denylist.
 *
 * Returns a SHALLOW COPY. The caller's blob is not mutated, because `preserveSecrets` has already
 * written into it and `idbRestore` may hand back a cached object.
 */
export function stripNeverRestored(payload) {
  if (!payload || typeof payload !== 'object') return payload;
  const out = {};
  Object.keys(payload).forEach(k => { if (!_RESTORE_EXCLUDE.has(k)) out[k] = payload[k]; });
  return out;
}

/** What `stripNeverRestored` declines, for the tests and for anything that wants to say so. */
export function neverRestoredKeys() { return [..._RESTORE_EXCLUDE]; }

/**
 * Top-level keys the UNTRUSTED door deliberately does not install — and the reason for each.
 *
 * ⚠ EXCLUSION BY OMISSION IS NOT A DECISION. Measured 2026-08-16 against
 * `backup/zagx-2026-08-14.json`: **ten of its 56 top-level keys were neither in `RESTORE_KEYS`
 * nor in `_RESTORE_EXCLUDE`.** They fell out of an `importData` because no list had heard of
 * them, which is indistinguishable — from the code, from a test, from a reader — from a decision
 * to leave them out. Two of the ten (`fxEURUSD`, `fxGBPUSD`) turned out to be real data and went
 * into `SCALAR_FIELDS`; these eight are the ones that genuinely must not travel through a file
 * the owner picked off disk, and now they say so.
 *
 * ⚠ THIS IS NOT `NEVER_RESTORE`, AND THE DIFFERENCE IS THE DOOR. `NEVER_RESTORE` also governs
 * `stripNeverRestored`, which the TRUSTED `restoreBackup` uses — and that door must carry
 * `_v5_migrated` and `_salaryAuditMigrated`, because a rollback that declines to roll back a
 * migration guard is worse than one that does (CONTRACT-005, and the TRY 63,485 of RUN 3). A key
 * here is declined by the ALLOWLIST only.
 *
 * Nothing reads this list at runtime. Its whole job is to let
 * `every-top-level-key-is-declared.test.js` tell "deliberately out" from "forgotten" — the same
 * instrument as `DECLARED` in `internal-types.test.js`. (2026-08-16)
 */
export const IMPORT_DECLINE = Object.freeze([
  // ── STILL WRITTEN BY THIS BUILD ────────────────────────────────────────────────────────────
  // Session and device bookkeeping. A file's copy describes the device that wrote it.
  '_lastMerge',      // mergeStates: `result._lastMerge = Date.now()`
  '_lastModified',   // save(): the global conflict clock
  '_fxTs',           // prices.js: FX freshness, three sites
  // "When this device last pulled the signal feed" (signals.js). Installing a file's copy would
  // make this device believe it has already pulled, and the 3h auto-pull would skip.
  '_lastSignalPull',
  // ── LEGACY: PRESENT IN STORED STATE, NO LONGER WRITTEN ─────────────────────────────────────
  // ⚠ These four are in his live book and in no code path. They still arrive inside a file, so
  // declining them is not theoretical — but nothing in this build creates them, and a future
  // reader should not go looking for the writer.
  '_saveCount',      // only ever destructured OUT before persisting (persistence.js, main.js)
  '_updatedAt',      // a ROW field everywhere; at top level it is residue of an older build
  '_v5_migrated',    // state.js:1375 says the flag "never could" prevent a double-run; deleted
  '_salaryAuditMigrated', // cashflow.js:545 records that this approach was abandoned
  // ⚠ THE TWO MIGRATION GUARDS MUST STAY DECLINED HERE WHILE STAYING RESTORABLE THROUGH THE
  // TRUSTED DOOR: a rollback that declines to roll a guard back is worse than one that does
  // (CONTRACT-005, and the TRY 63,485 of RUN 3). That is why this is not `NEVER_RESTORE`.
]);

export const RESTORE_KEYS = Object.freeze([...new Set([
  ...Object.keys(_IMPORT_KEYS),
  ...MERGE_ARRAYS,
  ...SCALAR_FIELDS,
  ..._RESTORE_EXTRA,
])].filter(k => !_RESTORE_EXCLUDE.has(k)));

/**
 * Sanitize import array: filter by allowed keys
 */
export function _sanitizeImportArray(arr, allowedKeys) {
  if (!Array.isArray(arr)) return [];
  return arr
    .filter(item => item && typeof item === 'object' && item.id && typeof item.id === 'string')
    .map(item => {
      const clean = {};
      allowedKeys.forEach(k => {
        if (item[k] !== undefined) clean[k] = item[k];
      });
      return clean;
    });
}
